import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, uploadFile, getAuthToken } from "@/lib/queryClient";
import { stripImageMetadata } from "@/lib/strip-image-metadata";
import { isHeicFile, convertHeicToJpeg } from "@/lib/convert-heic";
import { useStore } from "@/lib/store";
import { useToast } from "@/hooks/use-toast";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Field, FieldLabel } from "./ui/field";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  Rocket,
  Upload,
  X,
  Image as ImageIcon,
  Video,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Search,
  Plus,
  Minus,
  Target,
  Crosshair,
  Bookmark,
  Sparkles,
  Brain,
  Globe,
  Maximize2,
  Minimize2,
  FileText,
  Users,
  ChevronDown,
  ChevronUp,
  Check,
  HelpCircle,
  Settings2,
  BookOpen,
  ArrowRight,
  Copy,
  Code,
} from "lucide-react";
import InstantFormCreator from "./instant-form-creator";
import FastTrackFlow from "./fast-track-flow";
import PixelInstallGuideModal from "./pixel-install-guide-modal";
import AdSetBrowser, { type SelectedAdSetData, type AdRow } from "./adset-browser";
import { PaywallModal } from "./paywall-modal";
import { CampaignLaunchLog } from "./ui/campaign-launch-log";
import { addCreativeToPipeline } from "./creative-pipeline";
import { AiGeneratingOverlay } from "@/components/ui/ai-generating-overlay";
import GeoTargetingWidget, {
  type GeoTargetingValue,
  type SelectedGeoLocation,
} from "./geo-targeting-widget";
import type { TargetingVariant, TargetingVariantCount } from "@shared/schema";
import { META_AD_LOCALES, normaliseAdLocaleKey } from "@shared/meta/ad-locales";
import { withActPrefix, bareAdAccountId } from "@shared/meta/ad-account-id";

interface AgencyClient {
  id: string;
  name: string;
  /** Wlasciciel klienta — backend zwraca go w liscie klientow agencji. */
  userId?: string | null;
  adAccounts: {
    id: string;
    name: string;
    platform?: string | null;
    facebookPageId?: string;
    facebookPageName?: string;
    instagramActorId?: string;
    instagramActorName?: string;
    pixelId?: string;
    pixelName?: string;
  }[];
  offerSheetId: string | null;
  facebookPageId: string | null;
  facebookPageName: string | null;
  instagramActorId: string | null;
  instagramActorName: string | null;
  pixelId: string | null;
  pixelName: string | null;
}

// ============================================================
// Types
// ============================================================

interface Creative {
  id: string;
  file?: File;
  preview: string;
  type: "image" | "video";
  name: string;
  uploading: boolean;
  uploadedUrl?: string;
  graphicPrompt?: string;
}

interface LaunchResult {
  success: boolean;
  campaignId?: string;
  adSetId?: string;
  dupAdSetId?: string;
  dupAdSetUrl?: string;
  results?: { step: string; id?: string; error?: string }[];
  message: string;
}

interface MetaInterest {
  id: string;
  name: string;
  audienceSize?: number;
  path?: string[];
}
interface CustomAudience {
  id: string;
  name: string;
  subtype: string;
  approximateCount?: number;
}
interface Pixel {
  id: string;
  name: string;
}
interface CustomConversion {
  id: string;
  name: string;
  pixel?: { id: string };
  eventName?: string;
}

interface CampaignPreset {
  id: string;
  label: string;
  campaignNameTemplate: string;
  adSetNameTemplate: string;
  objective: string;
  budgetType: "ABO" | "CBO";
  optimizationGoal: string;
  conversionLocation?: string;
  callToAction?: string;
  conversionEvent?: string;
  description: string;
}

// ============================================================
// Helpers
// ============================================================

function todayFormatted(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

// ============================================================
// Campaign Presets
// ============================================================

const CAMPAIGN_PRESETS: CampaignPreset[] = [
  {
    id: "vsl-funnel",
    label: "VSL Funnel",
    campaignNameTemplate: "{brand} | TOF | VSL FUNNEL | {date}",
    adSetNameTemplate: "{brand} | TOF | VSL FUNNEL | {targeting}",
    objective: "OUTCOME_LEADS",
    budgetType: "ABO",
    optimizationGoal: "OFFSITE_CONVERSIONS",
    conversionLocation: "WEBSITE",
    description:
      "Lejek z VSL (Video Sales Letter) — kampania konwersyjna z optymalizacją na zapisy/zakupy na stronie. Wymaga Pixela.",
  },
  {
    id: "autowebinar",
    label: "Autowebinar",
    campaignNameTemplate: "{brand} | TOF | AUTOWEBINAR | {date}",
    adSetNameTemplate: "{brand} | TOF | AUTOWEBINAR | {targeting}",
    objective: "OUTCOME_LEADS",
    budgetType: "ABO",
    optimizationGoal: "OFFSITE_CONVERSIONS",
    conversionLocation: "WEBSITE",
    description:
      "Autowebinar funnel — kampania konwersyjna na zapisy do webinaru (na stronie). Wymaga Pixela.",
  },
  {
    id: "lead-gen",
    label: "Lead Generation",
    campaignNameTemplate: "{brand} | Lead Gen | {date}",
    adSetNameTemplate: "{brand} | Lead Gen | {targeting}",
    objective: "OUTCOME_LEADS",
    budgetType: "ABO",
    optimizationGoal: "LEAD_GENERATION",
    conversionLocation: "ON_AD",
    description:
      "Lead Generation — formularz Instant Form bezpośrednio w reklamie (bez przekierowania). Najszybsze leady.",
  },
  {
    id: "ecommerce",
    label: "Ecommerce",
    campaignNameTemplate: "{brand} | Ecom | Sales | {date}",
    adSetNameTemplate: "{brand} | Ecom | Sales | {targeting}",
    objective: "OUTCOME_SALES",
    budgetType: "ABO",
    optimizationGoal: "OFFSITE_CONVERSIONS",
    conversionLocation: "WEBSITE",
    callToAction: "SHOP_NOW",
    conversionEvent: "PURCHASE",
    description:
      "Ecommerce / sklep — optymalizacja na zakup (Purchase) na stronie. Wymaga Pixela + eventu Purchase. Bez Pixela użyj schematu Traffic.",
  },
  {
    id: "traffic",
    label: "Traffic",
    campaignNameTemplate: "{brand} | Traffic | {date}",
    adSetNameTemplate: "{brand} | Traffic | {targeting}",
    objective: "OUTCOME_TRAFFIC",
    budgetType: "CBO",
    optimizationGoal: "LANDING_PAGE_VIEWS",
    description:
      "Traffic — maksymalizacja kliknięć na stronę bez optymalizacji konwersji. Tańsze CPC, brak wymaganego Pixela.",
  },
];

// ============================================================
// Constants
// ============================================================

const OBJECTIVES: { value: string; label: string; description: string; recommended?: boolean }[] = [
  {
    value: "OUTCOME_LEADS",
    label: "Leady (Lead Generation)",
    description: "Zbieranie kontaktów — zapisy, formularze, leady. Najczęściej używane.",
    recommended: true,
  },
  {
    value: "OUTCOME_SALES",
    label: "Sprzedaż (Conversions)",
    description: "Konwersje sprzedażowe na stronie (zakupy w e-commerce). Wymaga Pixela.",
  },
  {
    value: "OUTCOME_TRAFFIC",
    label: "Ruch na stronie (Traffic)",
    description: "Maksymalizacja kliknięć na stronę — bez optymalizacji konwersji.",
  },
  {
    value: "OUTCOME_AWARENESS",
    label: "Zasięg (Awareness)",
    description: "Maksymalny zasięg — pokaz reklamy największej liczbie ludzi (branding).",
  },
  {
    value: "OUTCOME_ENGAGEMENT",
    label: "Zaangażowanie (Engagement)",
    description: "Lajki, komentarze, udostępnienia — budowanie społeczności.",
  },
];

const SPECIAL_AD_CATEGORIES = [
  {
    value: "FINANCIAL_PRODUCTS_SERVICES",
    label: "Produkty finansowe i usługi",
    description: "Karty kredytowe, ubezpieczenia, pożyczki, konta bankowe",
  },
  {
    value: "EMPLOYMENT",
    label: "Zatrudnienie",
    description: "Oferty pracy, staże, programy certyfikacji",
  },
  {
    value: "HOUSING",
    label: "Nieruchomości",
    description: "Ogłoszenia nieruchomości, kredyty hipoteczne",
  },
  {
    value: "ISSUES_ELECTIONS_POLITICS",
    label: "Sprawy społeczne, wybory lub polityka",
    description: "Reklamy dotyczące kwestii społecznych, wyborów lub polityków",
  },
] as const;

const SPECIAL_AD_CATEGORY_COUNTRIES = [
  { value: "PL", label: "🇵🇱 Polska" },
  { value: "US", label: "🇺🇸 USA" },
  { value: "GB", label: "🇬🇧 Wielka Brytania" },
  { value: "DE", label: "🇩🇪 Niemcy" },
  { value: "FR", label: "🇫🇷 Francja" },
  { value: "ES", label: "🇪🇸 Hiszpania" },
  { value: "IT", label: "🇮🇹 Włochy" },
  { value: "NL", label: "🇳🇱 Holandia" },
  { value: "CA", label: "🇨🇦 Kanada" },
  { value: "AU", label: "🇦🇺 Australia" },
] as const;

const TARGETING_RESTRICTED_CATEGORIES = new Set([
  "FINANCIAL_PRODUCTS_SERVICES",
  "EMPLOYMENT",
  "HOUSING",
]);

const OPTIMIZATION_GOALS: Record<string, { value: string; label: string }[]> = {
  OUTCOME_LEADS: [
    { value: "LEAD_GENERATION", label: "Lead Generation" },
    { value: "OFFSITE_CONVERSIONS", label: "Konwersje na stronie" },
    { value: "LANDING_PAGE_VIEWS", label: "Wyświetlenia strony" },
  ],
  OUTCOME_SALES: [
    { value: "OFFSITE_CONVERSIONS", label: "Konwersje" },
    { value: "LANDING_PAGE_VIEWS", label: "Wyświetlenia strony" },
  ],
  OUTCOME_TRAFFIC: [
    { value: "LINK_CLICKS", label: "Kliknięcia linku" },
    { value: "LANDING_PAGE_VIEWS", label: "Wyświetlenia strony" },
  ],
  OUTCOME_AWARENESS: [
    { value: "REACH", label: "Zasięg" },
    { value: "IMPRESSIONS", label: "Wyświetlenia" },
  ],
  OUTCOME_ENGAGEMENT: [
    { value: "IMPRESSIONS", label: "Wyświetlenia" },
    { value: "REACH", label: "Zasięg" },
  ],
};

// Conversion location options per objective — maps to Meta API destination_type + optimization_goal
type ConversionLocationConfig = {
  value: string;
  label: string;
  destinationType: string;
  optimizationGoal: string;
  needsPixel: boolean;
};
const CONVERSION_LOCATIONS: Record<string, ConversionLocationConfig[]> = {
  OUTCOME_LEADS: [
    {
      value: "ON_AD",
      label: "Formularze błyskawiczne",
      destinationType: "ON_AD",
      optimizationGoal: "LEAD_GENERATION",
      needsPixel: false,
    },
    {
      value: "WEBSITE",
      label: "Strona internetowa",
      destinationType: "WEBSITE",
      optimizationGoal: "OFFSITE_CONVERSIONS",
      needsPixel: true,
    },
    {
      value: "WEBSITE_AND_ON_AD",
      label: "Strona i formularze",
      destinationType: "WEBSITE_AND_ON_AD",
      optimizationGoal: "OFFSITE_CONVERSIONS",
      needsPixel: true,
    },
    {
      value: "MESSENGER",
      label: "Messenger",
      destinationType: "MESSENGER",
      optimizationGoal: "LEAD_GENERATION",
      needsPixel: false,
    },
    {
      value: "INSTAGRAM_DIRECT",
      label: "Instagram",
      destinationType: "INSTAGRAM_DIRECT",
      optimizationGoal: "LEAD_GENERATION",
      needsPixel: false,
    },
    {
      value: "WHATSAPP",
      label: "WhatsApp",
      destinationType: "WHATSAPP",
      optimizationGoal: "LEAD_GENERATION",
      needsPixel: false,
    },
    {
      value: "PHONE_CALL",
      label: "Rozmowy telefoniczne",
      destinationType: "PHONE_CALL",
      optimizationGoal: "QUALITY_CALL",
      needsPixel: false,
    },
  ],
  OUTCOME_SALES: [
    {
      value: "WEBSITE",
      label: "Strona internetowa",
      destinationType: "WEBSITE",
      optimizationGoal: "OFFSITE_CONVERSIONS",
      needsPixel: true,
    },
    {
      value: "MESSENGER",
      label: "Messenger",
      destinationType: "MESSENGER",
      optimizationGoal: "OFFSITE_CONVERSIONS",
      needsPixel: true,
    },
    {
      value: "WHATSAPP",
      label: "WhatsApp",
      destinationType: "WHATSAPP",
      optimizationGoal: "OFFSITE_CONVERSIONS",
      needsPixel: true,
    },
  ],
};

const STANDARD_EVENTS = [
  { value: "PURCHASE", label: "Purchase" },
  { value: "LEAD", label: "Lead" },
  { value: "COMPLETE_REGISTRATION", label: "CompleteRegistration" },
  { value: "ADD_TO_CART", label: "AddToCart" },
  { value: "INITIATED_CHECKOUT", label: "InitiateCheckout" },
  { value: "SUBSCRIBE", label: "Subscribe" },
  { value: "CONTACT", label: "Contact" },
  { value: "VIEW_CONTENT", label: "ViewContent" },
  { value: "ADD_PAYMENT_INFO", label: "AddPaymentInfo" },
  { value: "SCHEDULE", label: "Schedule" },
  { value: "SUBMIT_APPLICATION", label: "SubmitApplication" },
  { value: "START_TRIAL", label: "StartTrial" },
  { value: "OTHER", label: "Inne (Custom Conversion)" },
];

const CTA_OPTIONS = [
  { value: "LEARN_MORE", label: "Dowiedz się więcej" },
  { value: "SIGN_UP", label: "Zarejestruj się" },
  { value: "SHOP_NOW", label: "Kup teraz" },
  { value: "BOOK_TRAVEL", label: "Zarezerwuj" },
  { value: "CONTACT_US", label: "Skontaktuj się" },
  { value: "DOWNLOAD", label: "Pobierz" },
  { value: "GET_OFFER", label: "Pobierz ofertę" },
  { value: "GET_QUOTE", label: "Zapytaj o wycenę" },
  { value: "SUBSCRIBE", label: "Subskrybuj" },
  { value: "APPLY_NOW", label: "Aplikuj teraz" },
  { value: "WATCH_MORE", label: "Oglądaj więcej" },
  { value: "SEND_MESSAGE", label: "Wyślij wiadomość" },
];

const PUBLISHER_PLATFORMS = [
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "audience_network", label: "Audience Network" },
  { value: "messenger", label: "Messenger" },
];

const FACEBOOK_POSITIONS = [
  { value: "feed", label: "Feed" },
  { value: "right_hand_column", label: "Prawa kolumna" },
  { value: "instant_article", label: "Instant Articles" },
  { value: "marketplace", label: "Marketplace" },
  { value: "video_feeds", label: "Video Feeds" },
  { value: "story", label: "Stories" },
  { value: "search", label: "Wyniki wyszukiwania" },
  { value: "instream_video", label: "In-Stream Video" },
  { value: "facebook_reels", label: "Reels" },
];

const INSTAGRAM_POSITIONS = [
  { value: "stream", label: "Feed" },
  { value: "story", label: "Stories" },
  { value: "explore", label: "Explore" },
  { value: "reels", label: "Reels" },
  { value: "ig_search", label: "Wyszukiwanie" },
];

// Shared input styles
const INPUT_CLS =
  "h-9 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30";
const SELECT_TRIGGER_CLS = "h-9 text-sm bg-[#030407] border-white/[0.1] text-white";
const SELECT_CONTENT_CLS = "bg-[#0a0c10] border-white/[0.1]";
const SELECT_ITEM_CLS = "text-white hover:bg-white/[0.08] text-sm";
const SECTION_CLS = "bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 space-y-4";
const LABEL_CLS = "text-xs text-white/60 mb-1";

// Language targeting uses Meta locale **keys** (e.g. "pl_PL"). The numeric IDs
// Meta requires under the hood are resolved server-side against the live
// `/search?type=adlocale` catalogue — never hardcoded. See
// shared/meta/ad-locales.ts for the SSOT.
const LOCALE_OPTIONS = META_AD_LOCALES;

// ── Geo targeting helpers ─────────────────────────────────────────────────────

function buildIncludedGeoLocations(locations: SelectedGeoLocation[]) {
  const countries = locations.filter((l) => l.type === "country").map((l) => l.countryCode);
  const regions = locations.filter((l) => l.type === "region").map((l) => ({ key: l.key }));
  const cities = locations
    .filter((l) => l.type === "city")
    .map((l) => ({ key: l.key, radius: 25, distance_unit: "kilometer" as const }));
  const empty = countries.length === 0 && regions.length === 0 && cities.length === 0;
  return {
    countries: empty ? ["PL"] : countries.length > 0 ? countries : undefined,
    regions: regions.length > 0 ? regions : undefined,
    cities: cities.length > 0 ? cities : undefined,
  };
}

function buildExcludedGeoLocations(locations: SelectedGeoLocation[]) {
  if (locations.length === 0) return undefined;
  const countries = locations.filter((l) => l.type === "country").map((l) => l.countryCode);
  const regions = locations.filter((l) => l.type === "region").map((l) => ({ key: l.key }));
  const cities = locations.filter((l) => l.type === "city").map((l) => ({ key: l.key }));
  return {
    countries: countries.length > 0 ? countries : undefined,
    regions: regions.length > 0 ? regions : undefined,
    cities: cities.length > 0 ? cities : undefined,
  };
}

// ============================================================
// Component
// ============================================================

/** Konto reklamowe w pickerze — pola, ktore backend zwraca w /api/meta/status i liscie klienta. */
type MetaAdAccountOption = {
  id: string;
  name?: string;
  currency?: string;
  accountStatus?: number;
  platform?: string;
};

/** Strona FB z /api/campaign-creator/pages (wraz z podpietym kontem IG). */
type MetaPageOption = {
  id: string;
  /** Meta zawsze zwraca nazwe strony (fields=id,name) — czytana bez fallbacku. */
  name: string;
  instagramBusinessAccount?: { id: string; name?: string; username?: string };
};

/** Konto IG z /api/campaign-creator/instagram-accounts. */
type MetaIgAccountOption = { id: string; name?: string; username?: string };

/** Targetowanie budowane przed wyslaniem /launch — ksztalt czytany przez createMetaAdSet. */
type CampaignTargeting = {
  geoLocations: unknown;
  excludedGeoLocations?: unknown;
  ageMin: number;
  ageMax: number;
  genders?: number[];
  locales?: string[];
  interests?: Array<{ id: string; name: string }>;
  customAudiences?: Array<{ id: string }>;
  excludedCustomAudiences?: Array<{ id: string }>;
  publisherPlatforms?: string[];
  facebookPositions?: string[];
  instagramPositions?: string[];
};

/** promoted_object kampanii — strona (lead form) albo piksel z eventem konwersji. */
type CampaignPromotedObject = {
  pageId?: string;
  pixelId?: string;
  customEventType?: string;
};

/**
 * VP-326: ksztalty odpowiedzi backendu dla tego kreatora. Zrodlem sa handlery
 * w `apps/api/modules/campaign-creator/routes.ts` oraz `modules/meta/meta-integration.ts`
 * — nie domysl. Trzymane lokalnie, bo poza tym plikiem nikt ich nie uzywa.
 */
type MetaStatusResponse = {
  connected: boolean;
  wrongApp?: boolean;
  selectedAdAccountId?: string;
  adAccounts?: MetaAdAccountOption[];
  pages?: MetaPageOption[];
};

type PartnerPageOption = {
  id: string;
  name: string;
  picture?: string;
  creatorUsername?: string;
  brandPageName?: string;
};

type VerifiedPageOption = { id: string; name: string; picture?: string };

type LeadgenFormOption = { id: string; name: string; status: string; leadCount?: number };

/** `/generate-ad-copy` — teksty po przycieciu do limitow Meta. */
type AdCopyResponse = {
  primaryTexts?: string[];
  headlines?: string[];
  hasLandingPageContext?: boolean;
  hasBestAdsContext?: boolean;
  hasVideoTranscript?: boolean;
};

/**
 * `/suggest-targeting` zwraca ALBO liste wariantow, ALBO — dla zgodnosci wstecz —
 * pojedynczy wariant rozlozony na plaskie pola. Front obsluguje oba, wiec typ tez.
 */
type TargetingSuggestionResponse = {
  variants?: TargetingVariant[];
  countries?: string[];
  ageMin?: number;
  ageMax?: number;
  genders?: string;
  locales?: unknown[];
  interests?: { id: string; name: string; audienceSize?: number }[];
  reasoning?: string;
};

/**
 * Zestaw reklam zwracany przez `/api/campaign-creator/best-adset` i trzymany
 * w stanie kreatora — jeden ksztalt, zeby nie opisywac go dwa razy (VP-326).
 * `brandedContentSponsorIgUserId` czyta tylko handler odpowiedzi, stan go nie trzyma.
 */
type BestAdSet = {
  adSetId: string;
  adSetName: string;
  campaignId: string;
  spend: string;
  ctr: string;
  clicks: string;
  leads: number;
  costPerLead: number;
  optimizationGoal: string;
  destinationType: string;
  dailyBudget?: number;
  lifetimeBudget?: number;
  instagramActorId?: string;
  sourcePageId?: string;
  brandedContentSponsorPageId?: string;
  promotedObject?: Record<string, unknown> | null;
  targeting: Record<string, unknown>;
  ads: {
    adId: string;
    adName: string;
    primaryText: string;
    headline: string;
    description: string;
    callToAction: string;
    imageUrl: string | null;
    thumbnailUrl: string | null;
    videoId: string | null;
    linkUrl?: string;
  }[];
  brandedContentSponsorIgUserId?: string;
};

export default function CampaignBuilder() {
  const { t } = useTranslation("campaignBuilder");
  const { toast } = useToast();
  const { user } = useAuth();
  const agencyBrandName = user?.agencyName as string | undefined;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const launchLogRef = useRef<HTMLDivElement>(null);

  // ==================== Agency client (global store) ====================
  const {
    selectedClientId: globalClientId,
    clients: globalAgencyClients,
    setSelectedClient: setGlobalSelectedClient,
  } = useAgencyClients();

  // ==================== Fast Track mode ====================
  // First-visit default: drop fresh users straight into FastTrackFlow so they don't
  // stare at the full-form wall. Persisted via localStorage so subsequent visits
  // respect the user's choice (clicking "Szybki tor" button toggles it explicitly).
  const [fastTrackMode, setFastTrackMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return !localStorage.getItem("campaignBuilderVisited");
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("campaignBuilderVisited", "1");
    }
  }, []);
  // After Fast Track finalize, we hydrate the builder from the server-side draft.
  const [pendingDraftId, setPendingDraftId] = useState<string | null>(null);
  const [pixelGuideOpen, setPixelGuideOpen] = useState(false);
  const [pixelInstallAcknowledged, setPixelInstallAcknowledged] = useState(false);
  const [pixelSnippetsOpen, setPixelSnippetsOpen] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<"base" | "event" | null>(null);

  // ==================== Form State ====================
  const selectedClientId = globalClientId ?? "";
  // Auto-save draft to localStorage so a refresh doesn't lose user's progress
  const draft = useMemo(() => {
    try {
      const j = localStorage.getItem("campaign-builder.draft");
      return j
        ? (JSON.parse(j) as {
            campaignName?: string;
            adSetName?: string;
            linkUrl?: string;
            description?: string;
            primaryTexts?: string[];
            headlines?: string[];
            selectedPreset?: string;
          })
        : {};
    } catch {
      return {} as Record<string, never>;
    }
  }, []);
  const [selectedPreset, setSelectedPreset] = useState<string>(draft.selectedPreset || "");
  const [confirmLaunchOpen, setConfirmLaunchOpen] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState<boolean>(() => {
    try {
      return localStorage.getItem("campaign-builder.dismissedHIW") !== "1";
    } catch {
      return true;
    }
  });
  const dismissHowItWorks = useCallback(() => {
    setShowHowItWorks(false);
    try {
      localStorage.setItem("campaign-builder.dismissedHIW", "1");
    } catch {}
  }, []);
  const [brandName, setBrandName] = useState<string>(() => {
    try {
      return localStorage.getItem("campaign-builder.brandName") || "Viral.Partners";
    } catch {
      return "Viral.Partners";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("campaign-builder.brandName", brandName);
    } catch {}
  }, [brandName]);
  const [adAccountId, setAdAccountId] = useState("");
  const [adAccountSearch, setAdAccountSearch] = useState("");
  const [adAccountDropdownOpen, setAdAccountDropdownOpen] = useState(false);
  const [campaignName, setCampaignName] = useState<string>(draft.campaignName || "");
  const [objective, setObjective] = useState("OUTCOME_LEADS");
  const [budgetType, setBudgetType] = useState<"ABO" | "CBO">("ABO");
  const [dailyBudget, setDailyBudget] = useState("50");
  // Budget mode (advanced): 'daily' (standard) | 'lifetime' (total budget, runs until spent/endDate).
  const [budgetMode, setBudgetMode] = useState<"daily" | "lifetime">("daily");
  const [lifetimeBudget, setLifetimeBudget] = useState("1000");
  const [budgetEndDate, setBudgetEndDate] = useState("");
  const [specialAdCategories, setSpecialAdCategories] = useState<string[]>([]);
  const [specialAdCategoryCountry, setSpecialAdCategoryCountry] = useState<string[]>(["PL"]);
  const [specialAdCategoriesOpen, setSpecialAdCategoriesOpen] = useState(false);
  const [adSetName, setAdSetName] = useState<string>(draft.adSetName || "");
  const [optimizationGoal, setOptimizationGoal] = useState("LEAD_GENERATION");
  const [conversionLocation, setConversionLocation] = useState("WEBSITE");
  const [pageId, setPageId] = useState("");
  const [instagramActorId, setInstagramActorId] = useState("");
  const [pageSearch, setPageSearch] = useState("");
  const [pageDropdownOpen, setPageDropdownOpen] = useState(false);
  const [igSearch, setIgSearch] = useState("");
  const [igDropdownOpen, setIgDropdownOpen] = useState(false);
  const [partnershipEnabled, setPartnershipEnabled] = useState(false);
  const [partnerPageId, setPartnerPageId] = useState("");
  const [partnerPageIdInput, setPartnerPageIdInput] = useState("");
  const [partnerIgUserId, setPartnerIgUserId] = useState("");
  const [partnerIgUserIdInput, setPartnerIgUserIdInput] = useState("");
  const [linkUrl, setLinkUrl] = useState<string>(draft.linkUrl || "");
  const [description, setDescription] = useState<string>(draft.description || "");
  const [callToAction, setCallToAction] = useState("LEARN_MORE");

  // UTM parameters
  const [utmEnabled, setUtmEnabled] = useState(true);
  const [utmSource, setUtmSource] = useState("facebook");
  const [utmMedium, setUtmMedium] = useState("paid");
  const [utmCampaign, setUtmCampaign] = useState("{{campaign.id}}");
  const [utmTerm, setUtmTerm] = useState("{{adset.id}}");
  const [utmContent, setUtmContent] = useState("{{ad.id}}");

  // Ad-level settings
  const [multiAdvertiserAds, setMultiAdvertiserAds] = useState(false);
  const [advantagePlusCreative, setAdvantagePlusCreative] = useState(false);
  const [selectedLeadgenFormId, setSelectedLeadgenFormId] = useState("");
  const [instantFormCreatorOpen, setInstantFormCreatorOpen] = useState(false);

  // Split ads across multiple ad sets
  const [splitAdsEnabled, setSplitAdsEnabled] = useState(false);
  const [adsPerAdSet, setAdsPerAdSet] = useState(3);
  const [customAdsPerAdSetInput, setCustomAdsPerAdSetInput] = useState("");

  // A/B Test duplication
  const [abTestEnabled, setAbTestEnabled] = useState(false);
  const [abTestBestAdSet, setAbTestBestAdSet] = useState<BestAdSet | null>(null);
  const [abTestFetching, setAbTestFetching] = useState(false);
  const [abTestDatePreset, setAbTestDatePreset] = useState("last_7d");
  const [dupCustomize, setDupCustomize] = useState(false);
  const [dupCloneBestAds, setDupCloneBestAds] = useState(false);
  const [dupConvLocation, setDupConvLocation] = useState("");
  const [dupLinkUrl, setDupLinkUrl] = useState("");
  const [dupLeadgenFormId, setDupLeadgenFormId] = useState("");
  const [dupOptimizationGoal, setDupOptimizationGoal] = useState("");
  const [dupUseFormTargeting, setDupUseFormTargeting] = useState(false);

  // Dup targeting — dedicated targeting overrides for duplicate ad set
  const [dupGeoTargeting, setDupGeoTargeting] = useState<GeoTargetingValue>({
    included: [
      {
        key: "PL",
        name: "Poland",
        type: "country" as const,
        countryCode: "PL",
        countryName: "Poland",
        mode: "include",
      },
    ],
    excluded: [],
  });
  const [dupAgeMin, setDupAgeMin] = useState("18");
  const [dupAgeMax, setDupAgeMax] = useState("65");
  const [dupGenders, setDupGenders] = useState("0");
  const [dupSelectedInterests, setDupSelectedInterests] = useState<MetaInterest[]>([]);
  const [dupInterestSearch, setDupInterestSearch] = useState("");
  const [dupInterestResults, setDupInterestResults] = useState<MetaInterest[]>([]);
  const [dupSearchingInterests, setDupSearchingInterests] = useState(false);
  const [dupIncludedAudiences, setDupIncludedAudiences] = useState<CustomAudience[]>([]);
  const [dupExcludedAudiences, setDupExcludedAudiences] = useState<CustomAudience[]>([]);
  const [dupAudienceIncludeSearch, setDupAudienceIncludeSearch] = useState("");
  const [dupAudienceExcludeSearch, setDupAudienceExcludeSearch] = useState("");

  // A/B Test — selection mode: 'auto' (best ad set) vs 'manual' (browse tree)
  const [abTestMode, setAbTestMode] = useState<"auto" | "manual">("auto");
  const [manualBrowserOpen, setManualBrowserOpen] = useState(false);
  const [manualSelectedAdSet, setManualSelectedAdSet] = useState<SelectedAdSetData | null>(null);

  // A/B Test — best text + swap video mode
  const [dupBestTextSwapVideo, setDupBestTextSwapVideo] = useState(false);
  // A/B Test — use current form texts/link with user's creatives in duplicate-only mode
  const [dupUseCurrentFormAds, setDupUseCurrentFormAds] = useState(false);

  // A/B Test — campaign placement: 'new' (new campaign) vs 'source' (original campaign)
  const [dupCampaignMode, setDupCampaignMode] = useState<"new" | "source">("new");

  // Advanced settings
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [activateImmediately, setActivateImmediately] = useState(false);

  // Add to existing ad set
  const [addToAdSetEnabled, setAddToAdSetEnabled] = useState(false);
  const [addToAdSetBrowserOpen, setAddToAdSetBrowserOpen] = useState(false);
  const [selectedAddToAdSet, setSelectedAddToAdSet] = useState<SelectedAdSetData | null>(null);
  const [addToAdSetDatePreset, setAddToAdSetDatePreset] = useState("last_7d");
  const [addToAdSetUseSourceTexts, setAddToAdSetUseSourceTexts] = useState(true);

  // Multi texts (5 slots each)
  const [primaryTexts, setPrimaryTexts] = useState<string[]>(
    draft.primaryTexts && draft.primaryTexts.length === 5
      ? draft.primaryTexts
      : ["", "", "", "", ""]
  );
  const [headlines, setHeadlines] = useState<string[]>(
    draft.headlines && draft.headlines.length === 5 ? draft.headlines : ["", "", "", "", ""]
  );

  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  // Auto-save draft to localStorage (debounced 400ms)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const hasContent = !!(
          selectedPreset ||
          campaignName ||
          adSetName ||
          linkUrl ||
          description ||
          primaryTexts.some(Boolean) ||
          headlines.some(Boolean)
        );
        localStorage.setItem(
          "campaign-builder.draft",
          JSON.stringify({
            selectedPreset,
            campaignName,
            adSetName,
            linkUrl,
            description,
            primaryTexts,
            headlines,
          })
        );
        if (hasContent) setDraftSavedAt(Date.now());
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [selectedPreset, campaignName, adSetName, linkUrl, description, primaryTexts, headlines]);

  // Hydrate builder state from a server-side draft (e.g. after Fast Track finalize).
  // Only top-level fields with concrete UI mappings are pulled in — anything not
  // covered here stays at its current value (or the localStorage-restored default).
  useEffect(() => {
    if (!pendingDraftId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<{ draft?: { payload?: unknown } }>(
          "GET",
          `/api/campaign-creator/draft/${pendingDraftId}`
        );
        if (cancelled) return;
        const payload = (res?.draft?.payload ?? {}) as {
          campaignName?: string;
          landingPageUrl?: string;
          offer?: { title?: string };
          goals?: { businessModel?: string };
          structure?: {
            budgetType?: "ABO" | "CBO";
            adsPerAdSet?: number;
            adSets?: { name: string }[];
          };
          copy?: {
            primaryTexts?: string[];
            headlines?: string[];
            descriptions?: string[];
            cta?: string;
          };
          creatives?: { url: string; type: "image" | "video"; name?: string }[];
          metaConfig?: { adAccountId?: string; pageId?: string };
        };

        if (payload.campaignName) setCampaignName(payload.campaignName);
        else if (payload.offer?.title) setCampaignName(payload.offer.title);
        if (payload.landingPageUrl) setLinkUrl(payload.landingPageUrl);
        // VP-175: Onboarding Wizard wstępnie przypina konto reklamowe + stronę do draftu.
        if (payload.metaConfig?.adAccountId) setAdAccountId(payload.metaConfig.adAccountId);
        if (payload.metaConfig?.pageId) setPageId(payload.metaConfig.pageId);

        // Auto-pick preset based on business model
        if (payload.goals?.businessModel === "ecommerce_direct") {
          const eco = CAMPAIGN_PRESETS.find((p) => p.id === "ecommerce");
          if (eco) {
            setSelectedPreset(eco.id);
            setObjective(eco.objective);
            setBudgetType(eco.budgetType);
            setOptimizationGoal(eco.optimizationGoal);
            if (eco.conversionLocation) setConversionLocation(eco.conversionLocation);
            if (eco.callToAction) setCallToAction(eco.callToAction);
          }
        }

        if (payload.structure?.budgetType) setBudgetType(payload.structure.budgetType);
        if (payload.structure?.adsPerAdSet) {
          setAdsPerAdSet(payload.structure.adsPerAdSet);
          setSplitAdsEnabled(payload.structure.adsPerAdSet > 1);
        }

        // Copy → 5-slot arrays (pad with empty strings)
        const padTo5 = (arr: string[] | undefined): string[] => {
          const base = (arr ?? []).slice(0, 5);
          while (base.length < 5) base.push("");
          return base;
        };
        if (payload.copy?.primaryTexts?.length) setPrimaryTexts(padTo5(payload.copy.primaryTexts));
        if (payload.copy?.headlines?.length) setHeadlines(padTo5(payload.copy.headlines));
        if (payload.copy?.descriptions?.[0]) setDescription(payload.copy.descriptions[0]);
        if (payload.copy?.cta) setCallToAction(payload.copy.cta);

        // Creatives → builder shape (already-uploaded URLs from R2/scrape)
        if (payload.creatives?.length) {
          const incoming: Creative[] = payload.creatives.map((c, i) => ({
            id: `fasttrack-${i}-${Date.now()}`,
            preview: c.url,
            type: c.type,
            name: c.name || `creative-${i + 1}`,
            uploading: false,
            uploadedUrl: c.url,
          }));
          setCreatives((prev) => [...prev, ...incoming]);
        }

        toast({
          title: "Draft załadowany",
          description: "Fast Track wypełnił kreator — sprawdź i odpal.",
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Load failed";
        toast({
          title: "Nie udało się załadować draftu",
          description: msg,
          variant: "destructive",
        });
      } finally {
        if (!cancelled) setPendingDraftId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDraftId]);

  // VP-175: hand-off z Onboarding Wizarda (tryb URL) — ?draftId hydratuje serwerowy draft
  // (powyższa hydracja mapuje też konto/stronę z metaConfig). Raz na mount.
  useEffect(() => {
    const draftId = new URLSearchParams(window.location.search).get("draftId");
    if (draftId) setPendingDraftId(draftId);
  }, []);

  // AI copy generation
  type AiCopyMode =
    | "from_lp"
    | "from_best_ads"
    | "from_both"
    | "from_video"
    | "from_video_lp"
    | "from_video_best_ads"
    | "from_graphics"
    | "from_offer_sheet";
  const [aiCopyMode, setAiCopyMode] = useState<AiCopyMode | null>(null);
  const [aiCopyLoading, setAiCopyLoading] = useState(false);
  const [aiCopyMenuOpen, setAiCopyMenuOpen] = useState(false);
  const [expandedPT, setExpandedPT] = useState<number | null>(null);
  const [showManualLpInput, setShowManualLpInput] = useState(false);
  const [manualLpText, setManualLpText] = useState("");
  const [generateCount, setGenerateCount] = useState(1);

  const generateAiCopy = async (mode: AiCopyMode) => {
    setAiCopyMode(mode);
    setAiCopyLoading(true);
    setAiCopyMenuOpen(false);
    try {
      // If mode involves video, upload video files first
      const videoFilenames: string[] = [];
      if (mode === "from_video" || mode === "from_video_lp" || mode === "from_video_best_ads") {
        const videoCreatives = creatives.filter((c) => c.type === "video");
        if (videoCreatives.length === 0) {
          throw new Error("Dodaj przynajmniej jedno wideo");
        }
        for (const vc of videoCreatives.slice(0, 3)) {
          if (vc.uploadedUrl) {
            // Already uploaded — extract filename
            videoFilenames.push(vc.uploadedUrl.split("/").pop() || "");
          } else if (vc.file) {
            // Upload it now
            const uploadResp = await uploadForAnalysis(vc.file);
            videoFilenames.push(uploadResp.filename);
            // Save uploaded URL back
            setCreatives((prev) =>
              prev.map((c) => (c.id === vc.id ? { ...c, uploadedUrl: uploadResp.url } : c))
            );
          }
        }
      }

      // If mode is from_graphics, upload image files to R2 (presigned URLs accessible by LLM Vision API)
      const uploadedImageUrls: string[] = [];
      if (mode === "from_graphics") {
        const imageCreatives = creatives.filter((c) => !c.graphicPrompt && c.type === "image");
        for (const ic of imageCreatives.slice(0, 5)) {
          if (ic.uploadedUrl) {
            uploadedImageUrls.push(ic.uploadedUrl);
          } else if (ic.file) {
            const { url, r2Key } = await uploadCreativeToR2(ic);
            setCreatives((prev) =>
              prev.map((c) => (c.id === ic.id ? { ...c, uploadedUrl: url } : c))
            );
            uploadedImageUrls.push(url);
          }
        }
      }

      const resolvedOfferSheetId =
        selectedOfferSheetId !== "none" ? selectedOfferSheetId : undefined;

      const resp = await apiRequest<AdCopyResponse>(
        "POST",
        "/api/campaign-creator/generate-ad-copy",
        {
          linkUrl: linkUrl || undefined,
          adAccountId: adAccountId || undefined,
          mode,
          count: generateCount,
          videoFilenames: videoFilenames.length > 0 ? videoFilenames : undefined,
          manualLpText: manualLpText.trim() || undefined,
          graphicDescriptions:
            mode === "from_graphics"
              ? creatives.filter((c) => c.graphicPrompt).map((c) => c.graphicPrompt!)
              : undefined,
          graphicImageUrls:
            mode === "from_graphics" && uploadedImageUrls.length > 0
              ? uploadedImageUrls
              : undefined,
          offerSheetId: mode === "from_offer_sheet" ? resolvedOfferSheetId : undefined,
        }
      );
      const generatedPrimaryTexts = resp.primaryTexts ?? [];
      if (generatedPrimaryTexts.length) {
        setPrimaryTexts((prev) => {
          const updated = [...prev];
          generatedPrimaryTexts.slice(0, 5).forEach((t, i) => {
            updated[i] = t;
          });
          return updated;
        });
      }
      const generatedHeadlines = resp.headlines ?? [];
      if (generatedHeadlines.length) {
        setHeadlines((prev) => {
          const updated = [...prev];
          generatedHeadlines.slice(0, 5).forEach((t, i) => {
            updated[i] = t;
          });
          return updated;
        });
      }
      toast({
        title: "Wygenerowano!",
        description: `Primary texts: ${resp.primaryTexts?.length || 0}, Headlines: ${resp.headlines?.length || 0}`,
      });
      setShowManualLpInput(false);
      setManualLpText("");
    } catch (err) {
      const msg = (err as Error).message || "";
      if (
        msg.includes("Cloudflare") ||
        msg.includes("pobrać treści") ||
        msg.includes("Skopiuj tekst")
      ) {
        setShowManualLpInput(true);
        toast({
          title: t("toasts.fetchPageError"),
          description: t("toasts.fetchPageErrorDesc"),
          variant: "destructive",
        });
      } else {
        // Server returns a localized message for AI/provider-limit errors (429/503);
        // strip the leading "NNN: " HTTP-status prefix so only the human message is shown.
        const description = msg.replace(/^\d{3}:\s*/, "") || t("toasts.generateErrorDesc");
        toast({ title: t("toasts.generateError"), description, variant: "destructive" });
      }
    } finally {
      setAiCopyLoading(false);
      setAiCopyMode(null);
    }
  };

  // AI targeting suggestion
  const [aiTargetingLoading, setAiTargetingLoading] = useState(false);
  const [aiTargetingReasoning, setAiTargetingReasoning] = useState("");
  const [showTargetingReasoning, setShowTargetingReasoning] = useState(false);
  const [variantCount, setVariantCount] = useState<TargetingVariantCount>(1);
  const [targetingVariants, setTargetingVariants] = useState<TargetingVariant[] | null>(null);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState<number>(0);
  const [launchAllVariants, setLaunchAllVariants] = useState(false);

  // Apply a single TargetingVariant to the form fields
  const applyVariantToFields = useCallback((variant: TargetingVariant) => {
    if (variant.countries?.length) {
      const newIncluded: SelectedGeoLocation[] = variant.countries.map((code) => ({
        key: code,
        name: code,
        type: "country" as const,
        countryCode: code,
        countryName: code,
        mode: "include" as const,
      }));
      setGeoTargeting({ included: newIncluded, excluded: [] });
    }
    setAgeMin(String(variant.ageMin || 18));
    setAgeMax(String(variant.ageMax || 65));
    setGenders(variant.genders || "0");
    setSelectedLocales(
      (variant.locales || [])
        .map((l) => normaliseAdLocaleKey(l))
        .filter((k): k is string => k !== null)
    );
    setSelectedInterests(
      (variant.interests || []).map((i) => ({
        id: i.id,
        name: i.name,
        audienceSize: i.audienceSize,
      }))
    );
    setAiTargetingReasoning(variant.reasoning || "");
    setShowTargetingReasoning(true);
  }, []);

  const suggestTargetingWithAI = async () => {
    setAiTargetingLoading(true);
    setAiTargetingReasoning("");
    setTargetingVariants(null);
    setSelectedVariantIdx(0);
    try {
      const resolvedOfferSheetId =
        selectedOfferSheetId !== "none" ? selectedOfferSheetId : undefined;
      const resp = await apiRequest<TargetingSuggestionResponse>(
        "POST",
        "/api/campaign-creator/suggest-targeting",
        {
          objective,
          preset: selectedPreset,
          brandName,
          offerSheetId: resolvedOfferSheetId,
          clientId: selectedClientId || undefined,
          landingPageUrl: linkUrl || undefined,
          variantCount,
        }
      );

      // Multi-variant response: list of N variants, auto-apply the first
      if (Array.isArray(resp?.variants) && resp.variants.length > 0) {
        const variants = resp.variants as TargetingVariant[];
        setTargetingVariants(variants);
        setSelectedVariantIdx(0);
        applyVariantToFields(variants[0]);
        toast({
          title: t("toasts.aiTargetingSuccess", { count: variants.length }),
          description: t("toasts.aiTargetingDesc", { label: variants[0].label }),
        });
        return;
      }

      // Single-variant (legacy) response
      if (resp.countries?.length) {
        const newIncluded: SelectedGeoLocation[] = resp.countries.map((code: string) => ({
          key: code,
          name: code,
          type: "country" as const,
          countryCode: code,
          countryName: code,
          mode: "include" as const,
        }));
        setGeoTargeting({ included: newIncluded, excluded: [] });
      }
      if (resp.ageMin) setAgeMin(String(resp.ageMin));
      if (resp.ageMax) setAgeMax(String(resp.ageMax));
      if (resp.genders) setGenders(resp.genders);
      if (resp.locales?.length) {
        setSelectedLocales(
          (resp.locales as unknown[])
            .map((l) => normaliseAdLocaleKey(l))
            .filter((k): k is string => k !== null)
        );
      }
      if (resp.interests?.length) {
        setSelectedInterests(
          resp.interests.map((i: { id: string; name: string; audienceSize?: number }) => ({
            id: i.id,
            name: i.name,
            audienceSize: i.audienceSize,
          }))
        );
      }
      if (resp.reasoning) {
        setAiTargetingReasoning(resp.reasoning);
        setShowTargetingReasoning(true);
      }

      const interestCount = resp.interests?.length || 0;
      toast({
        title: t("toasts.aiTargetingFallback"),
        description: t("toasts.aiTargetingFallbackDesc", {
          countries: resp.countries?.join(", "),
          age: `${resp.ageMin}-${resp.ageMax}`,
          interests: interestCount,
        }),
      });
    } catch (err) {
      // Prefer the server's localized AI/provider-limit message (429/503), stripping the "NNN: " prefix.
      const description =
        ((err as Error)?.message || "").replace(/^\d{3}:\s*/, "") ||
        t("toasts.aiTargetingErrorDesc");
      toast({ title: t("toasts.aiTargetingError"), description, variant: "destructive" });
    } finally {
      setAiTargetingLoading(false);
    }
  };

  // Pixel & conversion
  const [selectedPixelId, setSelectedPixelId] = useState("");
  const [selectedConversionEvent, setSelectedConversionEvent] = useState("LEAD");
  const [selectedCustomConversionId, setSelectedCustomConversionId] = useState("");

  // Targeting
  const [geoTargeting, setGeoTargeting] = useState<GeoTargetingValue>({
    included: [
      {
        key: "PL",
        name: "Poland",
        type: "country" as const,
        countryCode: "PL",
        countryName: "Poland",
        mode: "include",
      },
    ],
    excluded: [],
  });
  const [selectedLocales, setSelectedLocales] = useState<string[]>([]);
  const [localeDropdownOpen, setLocaleDropdownOpen] = useState(false);
  const [localeSearch, setLocaleSearch] = useState("");
  const localeDropdownRef = useRef<HTMLDivElement>(null);
  const [ageMin, setAgeMin] = useState("18");
  const [ageMax, setAgeMax] = useState("65");
  const [genders, setGenders] = useState("0");

  // Interests
  const [interestSearch, setInterestSearch] = useState("");
  const [interestResults, setInterestResults] = useState<MetaInterest[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<MetaInterest[]>([]);
  const [searchingInterests, setSearchingInterests] = useState(false);

  // Custom audiences
  const [includedAudiences, setIncludedAudiences] = useState<CustomAudience[]>([]);
  const [excludedAudiences, setExcludedAudiences] = useState<CustomAudience[]>([]);
  const [audienceIncludeSearch, setAudienceIncludeSearch] = useState("");
  const [audienceExcludeSearch, setAudienceExcludeSearch] = useState("");

  // Placements
  const [manualPlacements, setManualPlacements] = useState(false);
  const [publisherPlatforms, setPublisherPlatforms] = useState<string[]>(["facebook", "instagram"]);
  const [facebookPositions, setFacebookPositions] = useState<string[]>([]);
  const [instagramPositions, setInstagramPositions] = useState<string[]>([]);

  // Creatives
  const [creatives, setCreatives] = useState<Creative[]>([]);

  // Library picker dialog
  const [, setRouterLocation] = useLocation();
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const { data: libraryData, isFetching: libraryLoading } = useQuery<{
    items: Array<{
      id: string;
      signedUrl: string | null;
      filename: string;
      mediaType: string | null;
      format: string;
      prompt: string | null;
      createdAt: string | null;
    }>;
  }>({
    queryKey: ["campaign-builder-library", libraryDialogOpen],
    queryFn: () => apiRequest("GET", "/api/graphics?contentType=ad"),
    enabled: libraryDialogOpen,
  });
  const libraryItems = libraryData?.items ?? [];

  const handlePickFromLibrary = useCallback(
    (item: {
      id: string;
      signedUrl: string | null;
      filename: string;
      mediaType: string | null;
      prompt: string | null;
    }) => {
      if (!item.signedUrl) {
        toast({
          title: "Brak URL",
          description: "Nie udało się pobrać linku do pliku.",
          variant: "destructive",
        });
        return;
      }
      const isVideo =
        (item.mediaType ?? "").startsWith("video") || /\.(mp4|mov|webm)$/i.test(item.filename);
      const newCreative: Creative = {
        id: `lib-${item.id}-${Date.now()}`,
        preview: item.signedUrl,
        type: isVideo ? "video" : "image",
        name: item.filename,
        uploading: false,
        uploadedUrl: item.signedUrl,
        graphicPrompt: item.prompt ?? undefined,
      };
      setCreatives((prev) => [...prev, newCreative]);
    },
    [toast]
  );

  // Close locale dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (localeDropdownRef.current && !localeDropdownRef.current.contains(e.target as Node))
        setLocaleDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Pick up pending creatives from ad-graphics-builder
  const { pendingCampaignCreatives, clearPendingCampaignCreatives } = useStore();
  useEffect(() => {
    if (pendingCampaignCreatives.length === 0) return;
    const incoming: Creative[] = pendingCampaignCreatives.map((pc) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      preview: pc.url,
      type: pc.type ?? "image",
      name: pc.name,
      uploading: false,
      uploadedUrl: pc.url,
      graphicPrompt: pc.prompt,
    }));
    setCreatives((prev) => [...prev, ...incoming]);
    clearPendingCampaignCreatives();
    const videoCount = incoming.filter((c) => c.type === "video").length;
    const imageCount = incoming.length - videoCount;
    const parts = [
      imageCount > 0 ? `${imageCount} grafik` : "",
      videoCount > 0 ? `${videoCount} wideo` : "",
    ]
      .filter(Boolean)
      .join(" i ");
    toast({
      title: t("toasts.pipelineAdded", { count: parts }),
      description: t("toasts.pipelineAddedDesc"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` zmienia tozsamosc przy zmianie jezyka; efekt ma sie odpalac tylko na nowe kreatywy z pipeline'u
  }, [pendingCampaignCreatives, clearPendingCampaignCreatives, toast]);

  // Launch result
  const [launchResult, setLaunchResult] = useState<LaunchResult | null>(null);
  const [launchProgress, setLaunchProgress] = useState<
    { step: string; status: string; message: string; id?: string }[]
  >([]);
  const [isLaunching, setIsLaunching] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);

  // ==================== Queries ====================
  const { data: metaStatus } = useQuery<MetaStatusResponse>({
    queryKey: ["meta-status-campaign"],
    queryFn: () => apiRequest("GET", "/api/integrations/meta/status"),
    staleTime: 60_000,
  });

  const {
    data: pagesData,
    isLoading: pagesLoading,
    isError: pagesError,
  } = useQuery<{ pages: MetaPageOption[] }>({
    queryKey: ["campaign-creator-pages"],
    queryFn: () => apiRequest("GET", "/api/campaign-creator/pages"),
    enabled: !!metaStatus?.connected,
    staleTime: 300_000,
    retry: 2,
  });

  const { data: pixelsData, isLoading: pixelsLoading } = useQuery<{ pixels: Pixel[] }>({
    queryKey: ["campaign-creator-pixels", adAccountId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/pixels?adAccountId=${encodeURIComponent(adAccountId)}`
      ),
    enabled: !!adAccountId && (objective === "OUTCOME_LEADS" || objective === "OUTCOME_SALES"),
    staleTime: 300_000,
  });

  const { data: customConversionsData } = useQuery<{ conversions: CustomConversion[] }>({
    queryKey: ["campaign-creator-conversions", adAccountId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/custom-conversions?adAccountId=${encodeURIComponent(adAccountId)}`
      ),
    enabled: !!adAccountId && selectedConversionEvent === "OTHER",
    staleTime: 300_000,
  });

  const {
    data: adAccountFunding,
    refetch: refetchAdAccountFunding,
    isFetching: isFetchingFunding,
  } = useQuery<{
    /**
     * VP-233: `status` rozróżnia „Meta potwierdza brak płatności" (missing) od
     * „Meta w ogóle nie oddała danych rozliczeniowych" (unknown). Czerwony alert
     * leci wyłącznie przy `missing` — wcześniej oba przypadki dawały
     * `hasFunding: false` i konto z aktywną kartą dostawało ostrzeżenie.
     * Opcjonalne, bo odpowiedź z cache sprzed deployu go nie ma.
     */
    status?: "has" | "missing" | "unknown";
    hasFunding: boolean;
    displayString: string | null;
    accountStatus: number | null;
    disableReason: number | null;
  }>({
    queryKey: ["campaign-creator-ad-account-funding", adAccountId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/ad-account-funding?adAccountId=${encodeURIComponent(adAccountId)}`
      ),
    enabled: !!adAccountId,
    staleTime: 300_000,
    retry: false,
  });

  const { data: pixelEventStats } = useQuery<{ count: number }>({
    queryKey: ["campaign-creator-pixel-event-stats", selectedPixelId, selectedConversionEvent],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/pixel-event-stats?pixelId=${encodeURIComponent(selectedPixelId)}&event=${encodeURIComponent(selectedConversionEvent)}`
      ),
    enabled: !!selectedPixelId && !!selectedConversionEvent && selectedConversionEvent !== "OTHER",
    staleTime: 300_000,
    retry: false,
  });

  const { data: customAudiencesData } = useQuery<{ audiences: CustomAudience[] }>({
    queryKey: ["campaign-creator-audiences", adAccountId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/custom-audiences?adAccountId=${encodeURIComponent(adAccountId)}`
      ),
    enabled: !!adAccountId,
    staleTime: 300_000,
  });

  // useMemo, bo `x || []` tworzy nowa tablice przy kazdym renderze i przeliczal
  // wszystkie hooki, ktore maja ja w zaleznosciach. Wartosc bez zmian.
  const ownerAdAccounts: MetaAdAccountOption[] = useMemo(
    () => metaStatus?.adAccounts || [],
    [metaStatus]
  );
  const pages: MetaPageOption[] = pagesData?.pages || [];

  // Sync brandName to agency name when user data loads (only if still at default)
  useEffect(() => {
    if (agencyBrandName && brandName === "Viral.Partners") {
      setBrandName(agencyBrandName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyBrandName]);

  // Agency clients — use global hook (shared with sidebar selector)
  const agencyClients: AgencyClient[] = useMemo(
    () => (globalAgencyClients as unknown as AgencyClient[]) || [],
    [globalAgencyClients]
  );
  const selectedClient = agencyClients.find((c) => c.id === selectedClientId);
  const isAgencyUser =
    user?.role === "employee" || user?.role === "agency_owner" || user?.role === "super_admin";

  // Offer sheets for non-agency users
  const { data: offerSheetsData } = useQuery<
    Array<{ id: string; title: string; isActive: boolean }>
  >({
    queryKey: ["/api/offer-sheets"],
    queryFn: () => apiRequest("GET", "/api/offer-sheets"),
    staleTime: 300_000,
  });
  const offerSheetsList = offerSheetsData || [];
  const [selectedOfferSheetId, setSelectedOfferSheetId] = useState<string>("none");
  const hasOfferSheets = offerSheetsList.length > 0;
  const activeOfferSheet = offerSheetsList.find((s) => s.isActive);

  const isMetaAdAccount = useCallback((platform?: string | null) => {
    if (!platform) return true;
    const normalizedPlatform = platform.toLowerCase();
    return normalizedPlatform !== "google" && normalizedPlatform !== "google_ads";
  }, []);

  const getClientMetaAccounts = useCallback(
    (client?: AgencyClient) => {
      if (!client?.adAccounts?.length) return [];
      return client.adAccounts.filter((account) => isMetaAdAccount(account.platform));
    },
    [isMetaAdAccount]
  );

  // Merge owner's ad accounts with selected client's ad accounts (deduplicated)
  const allAdAccounts = useMemo(() => {
    const clientAccounts = selectedClient ? getClientMetaAccounts(selectedClient) : [];
    const ownerIds = new Set(ownerAdAccounts.map((a) => a.id));
    const merged = [...ownerAdAccounts];
    for (const ca of clientAccounts) {
      if (!ownerIds.has(ca.id)) {
        merged.push({ id: ca.id, name: ca.name, accountStatus: 1, currency: "PLN" });
      }
    }
    return merged;
  }, [ownerAdAccounts, selectedClient, getClientMetaAccounts]);

  const selectedAdAccountCurrency = useMemo(() => {
    const account = allAdAccounts.find((a) => a.id === adAccountId);
    return account?.currency || "PLN";
  }, [allAdAccounts, adAccountId]);

  const {
    data: igAccountsData,
    isLoading: igLoading,
    isError: igError,
  } = useQuery<{ accounts: MetaIgAccountOption[] }>({
    queryKey: ["campaign-creator-ig-accounts"],
    queryFn: () => apiRequest("GET", "/api/campaign-creator/ig-accounts"),
    enabled: !!metaStatus?.connected,
    staleTime: 300_000,
    retry: 2,
  });
  const igAccounts: MetaIgAccountOption[] = igAccountsData?.accounts || [];

  // Partnership pages — fetch approved partners when toggle is on + page selected
  const { data: partnershipData, isLoading: partnersLoading } = useQuery<{
    partners: PartnerPageOption[];
  }>({
    queryKey: ["campaign-creator-partnership-pages", pageId || "all"],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/partnership-pages${pageId ? `?pageId=${encodeURIComponent(pageId)}` : ""}`
      ),
    enabled: !!metaStatus?.connected && partnershipEnabled,
    staleTime: 300_000,
    retry: 1,
  });
  const partnerPages: {
    id: string;
    name: string;
    picture?: string;
    creatorUsername?: string;
    brandPageName?: string;
  }[] = partnershipData?.partners || [];

  // Partnership page verification — verify manually entered page ID
  const debouncedPartnerId = partnerPageIdInput.trim();
  const isManualId =
    debouncedPartnerId.length >= 5 && !partnerPages.some((p) => p.id === debouncedPartnerId);
  const { data: verifiedPartnerPage, isLoading: partnerVerifying } = useQuery<{
    page: VerifiedPageOption | null;
  }>({
    queryKey: ["campaign-creator-verify-page", debouncedPartnerId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/verify-page?pageId=${encodeURIComponent(debouncedPartnerId)}`
      ),
    enabled: !!metaStatus?.connected && partnershipEnabled && isManualId,
    staleTime: 300_000,
    retry: 0,
  });
  const verifiedPage: { id: string; name: string; picture?: string } | null =
    verifiedPartnerPage?.page || null;

  const pixels: Pixel[] = useMemo(() => pixelsData?.pixels || [], [pixelsData]);
  const customConversions: CustomConversion[] = customConversionsData?.conversions || [];
  const allCustomAudiences: CustomAudience[] = useMemo(
    () => customAudiencesData?.audiences || [],
    [customAudiencesData]
  );

  // Leadgen forms — only when conversion location is ON_AD (instant forms) and a page is selected
  const isInstantForm =
    conversionLocation === "ON_AD" || conversionLocation === "WEBSITE_AND_ON_AD";
  // Pure instant form (ON_AD): the form lives inside the ad, there is NO landing page —
  // so the destination URL must NOT be required (a leadgen form is required instead).
  const isOnAdOnly = conversionLocation === "ON_AD";
  const dupNeedsInstantForm =
    dupCustomize && (dupConvLocation === "ON_AD" || dupConvLocation === "WEBSITE_AND_ON_AD");
  const { data: leadgenFormsData } = useQuery<{ forms: LeadgenFormOption[] }>({
    queryKey: ["campaign-creator-leadgen-forms", pageId],
    queryFn: () =>
      apiRequest("GET", `/api/campaign-creator/leadgen-forms?pageId=${encodeURIComponent(pageId)}`),
    enabled: !!pageId && (isInstantForm || dupNeedsInstantForm),
    staleTime: 300_000,
  });
  const leadgenForms: { id: string; name: string; status: string; leadCount?: number }[] =
    leadgenFormsData?.forms || [];

  // Meta requires an external link on lead-gen creatives even though the form opens in-app.
  // We take it from the form's privacy policy — show it up front so nobody discovers it only
  // in Ads Manager afterwards, and let the user override it (VP-306).
  const { data: creativeLinkData, isError: creativeLinkFailed } = useQuery<{
    url: string | null;
    source: string | null;
  }>({
    queryKey: ["campaign-creator-leadgen-creative-link", pageId, selectedLeadgenFormId],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/campaign-creator/leadgen-forms/${encodeURIComponent(selectedLeadgenFormId)}/creative-link?pageId=${encodeURIComponent(pageId)}`
      ),
    enabled: !!pageId && !!selectedLeadgenFormId && isOnAdOnly,
    staleTime: 300_000,
  });
  const resolvedCreativeLink: string | null = creativeLinkData?.url ?? null;
  /** Until the answer comes back we claim nothing — an empty response and "not asked yet"
   *  must not look the same, or the form flashes a false "no privacy policy" warning. */
  const creativeLinkKnown = creativeLinkData !== undefined;
  /** Form genuinely has no privacy URL → Meta rejects the ad unless the user supplies a link.
   *  A failed check does NOT block the launch: the backend still reports it with a clear error. */
  const needsManualLeadgenLink =
    isOnAdOnly && !!selectedLeadgenFormId && creativeLinkKnown && !resolvedCreativeLink;

  // Handle client selection — auto-set ad account & brand name
  const handleClientSelect = useCallback(
    (clientId: string) => {
      const client = agencyClients.find((c) => c.id === clientId);
      if (client)
        setGlobalSelectedClient({
          id: client.id,
          userId: client.userId ?? "",
          name: client.name,
        });
      if (!client) return;
      // Auto-select Meta ad account if exactly one
      const metaAccounts = getClientMetaAccounts(client);
      if (metaAccounts.length === 1) {
        const selectedAccount = metaAccounts[0];
        setAdAccountId(selectedAccount.id);

        // Use per-ad-account Meta defaults, falling back to client-level
        const adAccountEntry = (client.adAccounts || []).find((a) => a.id === selectedAccount.id);
        const pageId = adAccountEntry?.facebookPageId || client.facebookPageId;
        const igId = adAccountEntry?.instagramActorId || client.instagramActorId;
        const pxId = adAccountEntry?.pixelId || client.pixelId;
        if (pageId) setPageId(pageId);
        if (igId) setInstagramActorId(igId);
        if (pxId) setSelectedPixelId(pxId);
      } else {
        // Multiple accounts — use client-level fallback defaults
        if (client.facebookPageId) setPageId(client.facebookPageId);
        if (client.instagramActorId) setInstagramActorId(client.instagramActorId);
        if (client.pixelId) setSelectedPixelId(client.pixelId);
      }
      // Set brand name to client name — skip for agency users (they use their own agency name)
      if (!agencyBrandName) setBrandName(client.name);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `agencyBrandName` czytane tylko jako straz "czy nadpisac nazwe marki"; w zaleznosciach kazaloby przebudowac callback przy kazdej zmianie danych agencji
    [agencyClients, getClientMetaAccounts, setGlobalSelectedClient]
  );

  // Auto-apply side-effects when global client changes.
  // Wait for agencyClients data to load — otherwise handleClientSelect bails on
  // empty .find() and never re-runs (effect deps wouldn't trigger again).
  const lastHandledClientIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedClientId) {
      lastHandledClientIdRef.current = null;
      return;
    }
    if (lastHandledClientIdRef.current === selectedClientId) return;
    const client = agencyClients.find((c) => c.id === selectedClientId);
    if (!client) return; // wait until clients query resolves
    lastHandledClientIdRef.current = selectedClientId;
    handleClientSelect(selectedClientId);
  }, [selectedClientId, agencyClients, handleClientSelect]);

  // Filtered ad accounts by search
  const filteredAdAccounts = useMemo(() => {
    if (!adAccountSearch.trim()) return allAdAccounts;
    const q = adAccountSearch.toLowerCase();
    return allAdAccounts.filter(
      (a) => a.name?.toLowerCase().includes(q) || a.id?.toLowerCase().includes(q)
    );
  }, [allAdAccounts, adAccountSearch]);

  // ==================== Defaults ====================
  // Ponizsze trzy efekty ustawiaja wartosc domyslna TYLKO gdy pole jest jeszcze puste.
  // Dopisanie brakujacej zaleznosci (adAccountId / pageId / selectedPixelId) kazaloby im
  // reagowac na kazda zmiane wyboru uzytkownika — straz `!x` i tak by je wygasila, ale
  // intencja jest jednorazowa, wiec zostawiamy zaleznosci na danych zrodlowych.
  useEffect(() => {
    if (metaStatus?.selectedAdAccountId && !adAccountId) {
      setAdAccountId(metaStatus.selectedAdAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaStatus]);

  useEffect(() => {
    if (pages.length > 0 && !pageId) setPageId(pages[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  useEffect(() => {
    if (pixels.length > 0 && !selectedPixelId) setSelectedPixelId(pixels[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixels]);

  // Reset "I have a pending Pixel" acknowledgement once a Pixel actually shows up
  // on the account (user resolved the install — no need to keep the alert hidden).
  useEffect(() => {
    if (pixelInstallAcknowledged && pixels.length > 0) {
      setPixelInstallAcknowledged(false);
    }
  }, [pixels, pixelInstallAcknowledged]);

  // ==================== Interest Search ====================
  const searchInterestsDebounced = useCallback(async (query: string) => {
    if (query.length < 2) {
      setInterestResults([]);
      return;
    }
    setSearchingInterests(true);
    try {
      const data = await apiRequest<{ interests?: MetaInterest[] }>(
        "GET",
        `/api/campaign-creator/search-interests?q=${encodeURIComponent(query)}`
      );
      setInterestResults(data.interests || []);
    } catch {
      setInterestResults([]);
    }
    setSearchingInterests(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchInterestsDebounced(interestSearch), 400);
    return () => clearTimeout(timer);
  }, [interestSearch, searchInterestsDebounced]);

  const addInterest = (interest: MetaInterest) => {
    if (!selectedInterests.find((i) => i.id === interest.id)) {
      setSelectedInterests((prev) => [...prev, interest]);
    }
    setInterestSearch("");
    setInterestResults([]);
  };

  const removeInterest = (id: string) => {
    setSelectedInterests((prev) => prev.filter((i) => i.id !== id));
  };

  // ==================== Audience helpers ====================
  const toggleAudience = (audience: CustomAudience, list: "include" | "exclude") => {
    const setter = list === "include" ? setIncludedAudiences : setExcludedAudiences;
    setter((prev) => {
      if (prev.find((a) => a.id === audience.id)) return prev.filter((a) => a.id !== audience.id);
      return [...prev, audience];
    });
  };

  const availableAudiencesForInclude = useMemo(() => {
    let list = allCustomAudiences.filter((a) => !includedAudiences.find((i) => i.id === a.id));
    if (audienceIncludeSearch.trim()) {
      const q = audienceIncludeSearch.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    return list;
  }, [allCustomAudiences, includedAudiences, audienceIncludeSearch]);

  const availableAudiencesForExclude = useMemo(() => {
    let list = allCustomAudiences.filter((a) => !excludedAudiences.find((e) => e.id === a.id));
    if (audienceExcludeSearch.trim()) {
      const q = audienceExcludeSearch.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    return list;
  }, [allCustomAudiences, excludedAudiences, audienceExcludeSearch]);

  // ==================== Dup targeting helpers ====================
  const dupSearchInterestsDebounced = useCallback(async (query: string) => {
    if (query.length < 2) {
      setDupInterestResults([]);
      return;
    }
    setDupSearchingInterests(true);
    try {
      const data = await apiRequest<{ interests?: MetaInterest[] }>(
        "GET",
        `/api/campaign-creator/search-interests?q=${encodeURIComponent(query)}`
      );
      setDupInterestResults(data.interests || []);
    } catch {
      setDupInterestResults([]);
    }
    setDupSearchingInterests(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => dupSearchInterestsDebounced(dupInterestSearch), 400);
    return () => clearTimeout(timer);
  }, [dupInterestSearch, dupSearchInterestsDebounced]);

  const addDupInterest = (interest: MetaInterest) => {
    if (!dupSelectedInterests.find((i) => i.id === interest.id)) {
      setDupSelectedInterests((prev) => [...prev, interest]);
    }
    setDupInterestSearch("");
    setDupInterestResults([]);
  };

  const removeDupInterest = (id: string) => {
    setDupSelectedInterests((prev) => prev.filter((i) => i.id !== id));
  };

  const toggleDupAudience = (audience: CustomAudience, list: "include" | "exclude") => {
    const setter = list === "include" ? setDupIncludedAudiences : setDupExcludedAudiences;
    setter((prev) => {
      if (prev.find((a) => a.id === audience.id)) return prev.filter((a) => a.id !== audience.id);
      return [...prev, audience];
    });
  };

  const dupAvailableAudiencesForInclude = useMemo(() => {
    const includedIds = new Set(dupIncludedAudiences.map((i) => i.id));
    let list = allCustomAudiences.filter((a) => !includedIds.has(a.id));
    if (dupAudienceIncludeSearch.trim()) {
      const q = dupAudienceIncludeSearch.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    return list;
  }, [allCustomAudiences, dupIncludedAudiences, dupAudienceIncludeSearch]);

  const dupAvailableAudiencesForExclude = useMemo(() => {
    const excludedIds = new Set(dupExcludedAudiences.map((e) => e.id));
    let list = allCustomAudiences.filter((a) => !excludedIds.has(a.id));
    if (dupAudienceExcludeSearch.trim()) {
      const q = dupAudienceExcludeSearch.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    return list;
  }, [allCustomAudiences, dupExcludedAudiences, dupAudienceExcludeSearch]);

  // Initialize dup targeting from Section 3 values when toggled on
  const initDupTargetingFromForm = useCallback(() => {
    setDupGeoTargeting(geoTargeting);
    setDupAgeMin(ageMin);
    setDupAgeMax(ageMax);
    setDupGenders(genders);
    setDupSelectedInterests(selectedInterests);
    setDupIncludedAudiences(includedAudiences);
    setDupExcludedAudiences(excludedAudiences);
  }, [
    geoTargeting,
    ageMin,
    ageMax,
    genders,
    selectedInterests,
    includedAudiences,
    excludedAudiences,
  ]);

  // ==================== Preset apply ====================
  const getTargetingLabel = useCallback((): string => {
    if (selectedInterests.length > 0) return selectedInterests.map((i) => i.name).join(", ");
    if (includedAudiences.length > 0) {
      const first = includedAudiences[0];
      if (first.subtype === "LOOKALIKE") return `LLA`;
      return first.name;
    }
    return "BROAD";
  }, [selectedInterests, includedAudiences]);

  const applyPreset = useCallback(
    (presetId: string) => {
      const preset = CAMPAIGN_PRESETS.find((p) => p.id === presetId);
      if (!preset) return;
      setSelectedPreset(presetId);
      setObjective(preset.objective);
      setBudgetType(preset.budgetType);
      setOptimizationGoal(preset.optimizationGoal);
      if (preset.conversionLocation) setConversionLocation(preset.conversionLocation);
      if (preset.callToAction) setCallToAction(preset.callToAction);
      if (preset.conversionEvent) setSelectedConversionEvent(preset.conversionEvent);
      const date = todayFormatted();
      setCampaignName(
        preset.campaignNameTemplate.replace("{brand}", brandName).replace("{date}", date)
      );
      setAdSetName(
        preset.adSetNameTemplate
          .replace("{brand}", brandName)
          .replace("{targeting}", getTargetingLabel())
      );
    },
    [brandName, getTargetingLabel]
  );

  // Auto-update adset name when targeting changes (if preset active)
  useEffect(() => {
    if (!selectedPreset) return;
    const preset = CAMPAIGN_PRESETS.find((p) => p.id === selectedPreset);
    if (!preset) return;
    setAdSetName(
      preset.adSetNameTemplate
        .replace("{brand}", brandName)
        .replace("{targeting}", getTargetingLabel())
    );
  }, [selectedInterests, includedAudiences, brandName, selectedPreset, getTargetingLabel]);
  // Auto-scroll to launch log when launching starts
  useEffect(() => {
    if (isLaunching && launchLogRef.current) {
      launchLogRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isLaunching]);

  // ==================== Placement helpers ====================
  const toggleArrayItem = (arr: string[], val: string) =>
    arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

  // ==================== Multi-text helpers ====================
  const updatePrimaryText = (idx: number, val: string) => {
    setPrimaryTexts((prev) => {
      const n = [...prev];
      n[idx] = val;
      return n;
    });
  };
  const updateHeadline = (idx: number, val: string) => {
    setHeadlines((prev) => {
      const n = [...prev];
      n[idx] = val;
      return n;
    });
  };

  // ==================== A/B Test — fetch best ad set ====================
  const fetchBestAdSet = async () => {
    if (!adAccountId) {
      toast({
        title: t("toasts.noAdAccount"),
        description: t("toasts.noAdAccountDesc"),
        variant: "destructive",
      });
      return;
    }
    setAbTestFetching(true);
    try {
      // Determine metric based on campaign objective
      const metric = objective === "OUTCOME_SALES" ? "purchases" : "leads";
      const data = await apiRequest<{ bestAdSet: BestAdSet | null }>(
        "GET",
        `/api/campaign-creator/best-adset?adAccountId=${encodeURIComponent(adAccountId)}&datePreset=${abTestDatePreset}&metric=${metric}`
      );
      if (!data.bestAdSet) {
        toast({
          title: t("toasts.noData"),
          description: t("toasts.noDataDesc"),
          variant: "destructive",
        });
        setAbTestBestAdSet(null);
        return;
      }
      setAbTestBestAdSet(data.bestAdSet);
      // Auto-fill page, IG, partnership from best adset
      if (data.bestAdSet.sourcePageId) setPageId(data.bestAdSet.sourcePageId);
      if (data.bestAdSet.instagramActorId) setInstagramActorId(data.bestAdSet.instagramActorId);
      if (data.bestAdSet.brandedContentSponsorPageId) {
        setPartnershipEnabled(true);
        setPartnerPageId(data.bestAdSet.brandedContentSponsorPageId);
        setPartnerPageIdInput(data.bestAdSet.brandedContentSponsorPageId);
      }
      if (data.bestAdSet.brandedContentSponsorIgUserId) {
        setPartnershipEnabled(true);
        setPartnerIgUserId(data.bestAdSet.brandedContentSponsorIgUserId);
        setPartnerIgUserIdInput(data.bestAdSet.brandedContentSponsorIgUserId);
      }
      toast({
        title: t("toasts.bestAdSetFound"),
        description: t("toasts.bestAdSetFoundDesc", {
          name: data.bestAdSet.adSetName,
          cpl: data.bestAdSet.costPerLead?.toFixed(2) || "—",
          ctr: data.bestAdSet.ctr,
        }),
      });
    } catch (err) {
      toast({
        title: t("toasts.fetchAdSetError"),
        description: t("toasts.fetchAdSetErrorDesc"),
        variant: "destructive",
      });
    } finally {
      setAbTestFetching(false);
    }
  };

  // ==================== File handling ====================
  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const newCreatives: Creative[] = [];
      for (let file of Array.from(files)) {
        // VP-206: HEIC z iPhone'a → JPEG jeszcze w przeglądarce, bo dalej plik
        // leci presigned PUT-em prosto do S3 i serwer nie ma gdzie go skonwertować.
        if (isHeicFile(file)) {
          try {
            file = await convertHeicToJpeg(file);
          } catch {
            toast({
              title: t("toasts.heicConvertError"),
              description: t("toasts.heicConvertErrorDesc", { name: file.name }),
              variant: "destructive",
            });
            continue;
          }
        }
        const isVideo = file.type.startsWith("video/");
        const isImage = file.type.startsWith("image/");
        if (!isVideo && !isImage) continue;
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const preview = isImage ? URL.createObjectURL(file) : "";
        newCreatives.push({
          id,
          file,
          preview,
          type: isVideo ? "video" : "image",
          name: file.name,
          uploading: false,
        });
      }
      setCreatives((prev) => [...prev, ...newCreatives]);
    },
    [toast, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeCreative = (id: string) => {
    setCreatives((prev) => {
      const removed = prev.find((c) => c.id === id);
      if (removed?.preview && removed.file) URL.revokeObjectURL(removed.preview);
      return prev.filter((c) => c.id !== id);
    });
  };

  // Upload to Cloudflare R2 — tries presigned URL (direct browser→R2), falls back to server proxy.
  // Both paths retry with backoff: bez tego JEDEN przejściowy błąd (blip sieci, 5xx z R2,
  // reset połączenia przy ALB) ubijał plik bezpowrotnie. Większe pliki = dłuższe okno
  // transferu = większa szansa na taki blip, dlatego w batchu padał akurat ten najcięższy
  // (np. finalny render) a mniejsze przechodziły. Nazwa pliku nie ma znaczenia — klucz to UUID.
  const UPLOAD_MAX_ATTEMPTS = 3;
  const uploadAttemptDelayMs = (attempt: number): number => 1000 * 2 ** (attempt - 1); // 1s, 2s, 4s
  const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

  const putToPresignedWithRetry = async (
    file: File
  ): Promise<{ url: string; r2Key: string } | null> => {
    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      // Świeży presign na każdą próbę — uniknięcie wygasłego/zużytego URL-a.
      const { uploadUrl, getUrl, key } = await apiRequest<{
        uploadUrl: string;
        getUrl: string;
        key: string;
      }>("POST", "/api/campaign-creator/r2-presign", {
        filename: file.name,
        mimeType: file.type || "video/mp4",
      });
      try {
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "video/mp4" },
        });
        if (putRes.ok) return { url: getUrl, r2Key: key };
        // 4xx (np. 403 SignatureMismatch) nie naprawi się retry'em — od razu na proxy.
        if (putRes.status < 500) {
          // eslint-disable-next-line no-console -- diagnostyka fallbacku uploadu, widoczna w konsoli klienta
          console.warn("[R2] Direct PUT 4xx, falling back to server proxy", {
            status: putRes.status,
          });
          return null;
        }
        // eslint-disable-next-line no-console -- j.w.
        console.warn(`[R2] Direct PUT ${putRes.status} (próba ${attempt}/${UPLOAD_MAX_ATTEMPTS})`);
      } catch (err) {
        // eslint-disable-next-line no-console -- j.w.
        console.warn(`[R2] Direct PUT threw (próba ${attempt}/${UPLOAD_MAX_ATTEMPTS})`, err);
      }
      if (attempt < UPLOAD_MAX_ATTEMPTS) await sleep(uploadAttemptDelayMs(attempt));
    }
    return null; // wszystkie próby direct PUT padły → proxy fallback
  };

  const proxyUploadWithRetry = async (file: File): Promise<{ url: string; r2Key: string }> => {
    let lastErr: unknown;
    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        return (await uploadFile("/api/campaign-creator/r2-upload", formData)) as {
          url: string;
          r2Key: string;
        };
      } catch (err) {
        lastErr = err;
        // eslint-disable-next-line no-console -- diagnostyka retry uploadu, widoczna w konsoli klienta
        console.warn(`[R2] Proxy upload threw (próba ${attempt}/${UPLOAD_MAX_ATTEMPTS})`, err);
        if (attempt < UPLOAD_MAX_ATTEMPTS) await sleep(uploadAttemptDelayMs(attempt));
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  };

  const uploadCreativeToR2 = async (
    creative: Creative
  ): Promise<{ url: string; r2Key: string }> => {
    if (!creative.file) throw new Error(t("errors.noFile"));
    // Strip metadanych XMP RAZ, zanim wybierzemy ścieżkę. Design-exporty PNG (np.
    // reklama-feed-*.png) niosą XML XMP, który WAF blokuje na proxy. CORS na buckecie
    // często wymusza fallback na proxy, więc czyścimy plik dla OBU ścieżek na wejściu.
    const file = await stripImageMetadata(creative.file);
    const direct = await putToPresignedWithRetry(file);
    if (direct) return direct;
    return proxyUploadWithRetry(file);
  };

  // Server upload for AI analysis (generate-ad-copy) — uses small chunks, video stays on server for transcription
  const ANALYSIS_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB chunks — below Replit nginx limit
  const uploadForAnalysis = async (file: File): Promise<{ url: string; filename: string }> => {
    if (file.size <= ANALYSIS_CHUNK_SIZE) {
      const formData = new FormData();
      formData.append("file", file);
      return uploadFile("/api/campaign-creator/upload-creative", formData) as Promise<{
        url: string;
        filename: string;
      }>;
    }
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(file.size / ANALYSIS_CHUNK_SIZE);
    let result: { url: string; filename: string } | undefined;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * ANALYSIS_CHUNK_SIZE;
      const end = Math.min(start + ANALYSIS_CHUNK_SIZE, file.size);
      const formData = new FormData();
      formData.append("chunk", file.slice(start, end), file.name);
      formData.append("uploadId", uploadId);
      formData.append("chunkIndex", String(i));
      formData.append("totalChunks", String(totalChunks));
      formData.append("filename", file.name);
      formData.append("mimeType", file.type);
      result = (await uploadFile("/api/campaign-creator/upload-creative-chunk", formData)) as {
        url: string;
        filename: string;
      };
    }
    // totalChunks >= 1 dla kazdego pliku, wiec petla wyzej zawsze ustawia `result`.
    return result!;
  };

  // ==================== Special Ad Category targeting restrictions ====================
  const hasTargetingRestrictions = specialAdCategories.some((c) =>
    TARGETING_RESTRICTED_CATEGORIES.has(c)
  );

  // ==================== Needs pixel config? ====================
  // Get current conversion location config
  const convLocationConfig = (CONVERSION_LOCATIONS[objective] || []).find(
    (cl) => cl.value === conversionLocation
  );
  const needsPixelConfig =
    convLocationConfig?.needsPixel ??
    ((objective === "OUTCOME_LEADS" || objective === "OUTCOME_SALES") &&
      (optimizationGoal === "OFFSITE_CONVERSIONS" || optimizationGoal === "LEAD_GENERATION"));

  // ==================== Launch mutation ====================
  const launchMutation = useMutation({
    mutationFn: async () => {
      setLaunchResult(null);
      setLaunchProgress([]);
      setIsLaunching(true);

      // Phase 1: Upload files directly to R2 (skip in pure clone-only mode, but NOT for swap-new-campaign)
      const skipR2Upload = isCloneOnly && !isSwapNewCampaign;
      const creativesToUpload = skipR2Upload ? [] : creatives;
      const uploadedCreatives: { type: string; url: string; name: string; r2Key?: string }[] = [];

      // Parallel batch upload — 5 files concurrently (browser limit ~6 connections per domain)
      const R2_UPLOAD_CONCURRENCY = 5;

      // Pre-fill already-uploaded creatives
      const pendingCreatives: { index: number; creative: Creative }[] = [];
      for (let i = 0; i < creativesToUpload.length; i++) {
        if (creativesToUpload[i].uploadedUrl) {
          uploadedCreatives[i] = {
            type: creativesToUpload[i].type,
            url: creativesToUpload[i].uploadedUrl!,
            name: creativesToUpload[i].name,
          };
          setLaunchProgress((prev) => [
            ...prev,
            {
              step: `upload-${i + 1}`,
              status: "done",
              message: t("launchSteps.fileReady", { name: creativesToUpload[i].name }),
            },
          ]);
        } else {
          pendingCreatives.push({ index: i, creative: creativesToUpload[i] });
        }
      }

      // Upload pending creatives in parallel batches
      for (
        let batchStart = 0;
        batchStart < pendingCreatives.length;
        batchStart += R2_UPLOAD_CONCURRENCY
      ) {
        const batch = pendingCreatives.slice(batchStart, batchStart + R2_UPLOAD_CONCURRENCY);

        // Mark batch as pending
        for (const { index, creative } of batch) {
          setLaunchProgress((prev) => [
            ...prev,
            {
              step: `upload-${index + 1}`,
              status: "pending",
              message: t("launchSteps.uploadingFile", {
                index: index + 1,
                total: creativesToUpload.length,
                name: creative.name,
              }),
            },
          ]);
          setCreatives((prev) =>
            prev.map((c) => (c.id === creative.id ? { ...c, uploading: true } : c))
          );
        }

        const batchResults = await Promise.all(
          batch.map(async ({ index, creative }) => {
            try {
              const { url, r2Key } = await uploadCreativeToR2(creative);
              setCreatives((prev) =>
                prev.map((c) =>
                  c.id === creative.id ? { ...c, uploading: false, uploadedUrl: url } : c
                )
              );
              setLaunchProgress((prev) =>
                prev.map((p) =>
                  p.step === `upload-${index + 1}`
                    ? {
                        ...p,
                        status: "done",
                        message: t("launchSteps.uploadedFile", { name: creative.name }),
                      }
                    : p
                )
              );
              return { index, result: { type: creative.type, url, name: creative.name, r2Key } };
            } catch (err) {
              setCreatives((prev) =>
                prev.map((c) => (c.id === creative.id ? { ...c, uploading: false } : c))
              );
              setLaunchProgress((prev) =>
                prev.map((p) =>
                  p.step === `upload-${index + 1}`
                    ? {
                        ...p,
                        status: "error",
                        message: t("errors.uploadError", { name: creative.name }),
                      }
                    : p
                )
              );
              throw new Error(
                t("errors.uploadErrorDetail", {
                  name: creative.name,
                  message: (err as Error).message,
                })
              );
            }
          })
        );

        for (const { index, result } of batchResults) {
          uploadedCreatives[index] = result;
        }
      }

      // Compact — remove any undefined gaps (shouldn't happen, but safe)
      const finalUploadedCreatives = uploadedCreatives.filter(Boolean);

      // Build targeting
      const targetingObj: CampaignTargeting = {
        geoLocations: buildIncludedGeoLocations(geoTargeting.included),
        // Special Ad Categories: location exclusion not supported by Meta
        ...(hasTargetingRestrictions
          ? {}
          : { excludedGeoLocations: buildExcludedGeoLocations(geoTargeting.excluded) }),
        ageMin: hasTargetingRestrictions ? 18 : Number(ageMin) || 18,
        ageMax: hasTargetingRestrictions ? 65 : Number(ageMax) || 65,
      };
      // Special Ad Categories (FINANCIAL_PRODUCTS_SERVICES, EMPLOYMENT, HOUSING) restrict gender, interest & exclusion targeting
      if (!hasTargetingRestrictions && genders !== "0") targetingObj.genders = [Number(genders)];
      if (selectedLocales.length > 0) targetingObj.locales = selectedLocales;
      if (!hasTargetingRestrictions && selectedInterests.length > 0) {
        targetingObj.interests = selectedInterests.map((i) => ({ id: i.id, name: i.name }));
      }
      if (includedAudiences.length > 0) {
        targetingObj.customAudiences = includedAudiences.map((a) => ({ id: a.id }));
      }
      // Special Ad Categories: audience exclusion & detailed targeting exclusion not permitted
      if (!hasTargetingRestrictions && excludedAudiences.length > 0) {
        targetingObj.excludedCustomAudiences = excludedAudiences.map((a) => ({ id: a.id }));
      }
      if (manualPlacements && publisherPlatforms.length > 0) {
        targetingObj.publisherPlatforms = publisherPlatforms;
        if (publisherPlatforms.includes("facebook") && facebookPositions.length > 0) {
          targetingObj.facebookPositions = facebookPositions;
        }
        if (publisherPlatforms.includes("instagram") && instagramPositions.length > 0) {
          targetingObj.instagramPositions = instagramPositions;
        }
      }

      // Use conversion location config if available
      const launchOptGoal = convLocationConfig?.optimizationGoal || optimizationGoal;
      const destinationType =
        convLocationConfig?.destinationType ||
        (launchOptGoal === "LEAD_GENERATION" ? "ON_AD" : "WEBSITE");

      // Build promoted object for conversion optimization
      let promotedObject: CampaignPromotedObject | undefined = undefined;
      if (launchOptGoal === "LEAD_GENERATION" || launchOptGoal === "QUALITY_CALL") {
        promotedObject = { pageId: pageId };
      } else if (needsPixelConfig && selectedPixelId) {
        promotedObject = { pixelId: selectedPixelId };
        if (selectedConversionEvent === "OTHER" && selectedCustomConversionId) {
          const cc = customConversions.find((c) => c.id === selectedCustomConversionId);
          if (cc?.eventName) promotedObject.customEventType = cc.eventName;
        } else if (selectedConversionEvent) {
          promotedObject.customEventType = selectedConversionEvent;
        }
      }

      const filteredPrimaryTexts = primaryTexts.filter(Boolean);
      const filteredHeadlines = headlines.filter(Boolean);

      const utmTags = utmEnabled
        ? `utm_source=${utmSource}&utm_medium=${utmMedium}&utm_campaign=${utmCampaign}&utm_term=${utmTerm}&utm_content=${utmContent}`
        : undefined;

      // Build targetingVariants payload for A/B targeting test (one adset per variant, same creatives).
      // Each variant inherits placement/audience settings from targetingObj and overrides only the AI-suggested fields.
      const shouldLaunchVariants =
        launchAllVariants && targetingVariants && targetingVariants.length > 1;
      const targetingVariantsPayload = shouldLaunchVariants
        ? targetingVariants.map((variant) => {
            const variantTargeting: Record<string, unknown> = { ...targetingObj };
            variantTargeting.geoLocations = buildIncludedGeoLocations(
              variant.countries.map<SelectedGeoLocation>((code) => ({
                key: code,
                name: code,
                type: "country",
                countryCode: code,
                countryName: code,
                mode: "include",
              }))
            );
            variantTargeting.ageMin = hasTargetingRestrictions ? 18 : variant.ageMin;
            variantTargeting.ageMax = hasTargetingRestrictions ? 65 : variant.ageMax;
            if (!hasTargetingRestrictions && variant.genders !== "0") {
              variantTargeting.genders = [Number(variant.genders)];
            } else {
              delete variantTargeting.genders;
            }
            if (variant.locales.length > 0) variantTargeting.locales = variant.locales;
            else delete variantTargeting.locales;
            if (!hasTargetingRestrictions && variant.interests.length > 0) {
              variantTargeting.interests = variant.interests.map((i) => ({
                id: i.id,
                name: i.name,
              }));
            } else {
              delete variantTargeting.interests;
            }
            return { label: variant.label, targeting: variantTargeting };
          })
        : undefined;

      const payload: Record<string, unknown> = {
        adAccountId,
        campaignName,
        objective,
        budgetType,
        dailyBudget: Number(dailyBudget),
        budgetMode,
        lifetimeBudget: budgetMode === "lifetime" ? Number(lifetimeBudget) : undefined,
        endTime:
          budgetMode === "lifetime" && budgetEndDate
            ? new Date(budgetEndDate).toISOString()
            : undefined,
        specialAdCategories: specialAdCategories.length > 0 ? specialAdCategories : undefined,
        specialAdCategoryCountry:
          specialAdCategories.length > 0 && specialAdCategoryCountry.length > 0
            ? specialAdCategoryCountry
            : undefined,
        adSetName: adSetName || `${campaignName} - Ad Set`,
        targeting: targetingObj,
        optimizationGoal: launchOptGoal,
        billingEvent: "IMPRESSIONS",
        promotedObject,
        destinationType,
        pageId,
        instagramActorId: instagramActorId || undefined,
        linkUrl,
        primaryTexts: filteredPrimaryTexts,
        headlines: filteredHeadlines,
        description,
        callToAction,
        urlTags: utmTags,
        leadgenFormId: isInstantForm && selectedLeadgenFormId ? selectedLeadgenFormId : undefined,
        brandedContentSponsorPageId:
          partnershipEnabled && partnerPageId ? partnerPageId : undefined,
        brandedContentSponsorIgUserId:
          partnershipEnabled && partnerIgUserId ? partnerIgUserId : undefined,
        creatives: finalUploadedCreatives,
        cloneOnly: isCloneOnly || isSwapNewCampaign || undefined,
        dupOnly: isDupOnly || undefined,
        splitAds:
          !shouldLaunchVariants && splitAdsEnabled && creatives.length > 1
            ? { adsPerAdSet }
            : undefined,
        targetingVariants: targetingVariantsPayload,
        activateImmediately: activateImmediately || undefined,
        addToExistingAdSet: isAddToAdSet
          ? {
              adSetId: selectedAddToAdSet!.adSetId,
              adSetName: selectedAddToAdSet!.adSetName,
              useSourceTexts: addToAdSetUseSourceTexts,
              sourceAdIds: addToAdSetUseSourceTexts
                ? selectedAddToAdSet!.ads.map((a) => a.adId)
                : undefined,
            }
          : undefined,
      };

      // A/B Test: duplicate ad set with best/manual ad set's targeting in the same campaign
      if (abTestEnabled && effectiveAdSet?.targeting) {
        const dupConfig =
          dupCustomize && dupConvLocation
            ? (CONVERSION_LOCATIONS[objective] || []).find((cl) => cl.value === dupConvLocation)
            : undefined;

        // Build dup-specific targeting in snake_case format for rawTargeting path
        const dupTargetingObj: Record<string, unknown> = {
          geo_locations: buildIncludedGeoLocations(dupGeoTargeting.included),
          ...(hasTargetingRestrictions
            ? {}
            : (() => {
                const excl = buildExcludedGeoLocations(dupGeoTargeting.excluded);
                return excl ? { excluded_geo_locations: excl } : {};
              })()),
          age_min: hasTargetingRestrictions ? 18 : Number(dupAgeMin) || 18,
          age_max: hasTargetingRestrictions ? 65 : Number(dupAgeMax) || 65,
        };
        if (!hasTargetingRestrictions && dupGenders !== "0")
          dupTargetingObj.genders = [Number(dupGenders)];
        if (!hasTargetingRestrictions && dupSelectedInterests.length > 0) {
          dupTargetingObj.flexible_spec = [
            { interests: dupSelectedInterests.map((i) => ({ id: i.id, name: i.name })) },
          ];
        }
        if (dupIncludedAudiences.length > 0) {
          dupTargetingObj.custom_audiences = dupIncludedAudiences.map((a) => ({ id: a.id }));
        }
        if (!hasTargetingRestrictions && dupExcludedAudiences.length > 0) {
          dupTargetingObj.excluded_custom_audiences = dupExcludedAudiences.map((a) => ({
            id: a.id,
          }));
        }

        // Determine source ad IDs based on mode — put the ad with text first
        const adWithText = effectiveAdSet.ads.find((a) => a.primaryText);
        const sourceAdIds = adWithText
          ? [
              adWithText.adId,
              ...effectiveAdSet.ads.filter((a) => a.adId !== adWithText.adId).map((a) => a.adId),
            ]
          : effectiveAdSet.ads.map((a) => a.adId);

        // Best text + swap video mode: find the ad with the best primary text
        // and use only that text, replacing creatives with user-uploaded ones
        const bestTextSwapEnabled = dupBestTextSwapVideo && effectiveAdSet.ads.length > 0;

        payload.duplicateAdSet = {
          rawTargeting: dupUseFormTargeting ? dupTargetingObj : effectiveAdSet.targeting,
          name: `${adSetName || campaignName} - ${effectiveAdSet.adSetName}`,
          // Campaign placement: 'source' = put in source campaign, 'new' = put in newly created campaign
          campaignMode: dupCampaignMode,
          // Always pass sourceCampaignId (independent of mode) so backend can inherit
          // special_ad_categories (FINANCIAL_PRODUCTS_SERVICES, etc.) from the source campaign
          // when creating a new campaign — Meta rejects mismatched categories with subcode 2490408.
          ...(effectiveAdSet.campaignId && { sourceCampaignId: effectiveAdSet.campaignId }),
          ...(dupConfig && {
            optimizationGoal: dupConfig.optimizationGoal,
            destinationType: dupConfig.destinationType,
          }),
          ...(dupCustomize && dupLinkUrl && { linkUrl: dupLinkUrl }),
          ...(dupCustomize && dupOptimizationGoal && { optimizationGoal: dupOptimizationGoal }),
          ...(dupCustomize &&
            dupNeedsInstantForm &&
            dupLeadgenFormId && { leadgenFormId: dupLeadgenFormId }),
          // Always pass source ad set's settings so backend doesn't fall back to new campaign form values
          sourceOptimizationGoal: effectiveAdSet.optimizationGoal || "",
          sourceDestinationType: effectiveAdSet.destinationType || "",
          sourceDailyBudget: effectiveAdSet.dailyBudget,
          sourceInstagramActorId: effectiveAdSet.instagramActorId || "",
          sourcePromotedObject: effectiveAdSet.promotedObject || undefined,
          // Clone best ads mode: copy original ads + swap form only
          ...(dupCloneBestAds &&
            !bestTextSwapEnabled && {
              cloneBestAds: true,
              sourceAdIds,
              newLeadgenFormId: dupLeadgenFormId || undefined,
            }),
          // Best text + swap video mode
          ...(bestTextSwapEnabled && {
            bestTextSwapVideo: true,
            // Send source ad IDs so server can re-fetch the full creative 1:1 from Meta API
            sourceAdIds,
            // Fallback text data (used if Meta re-fetch fails) — pick first ad with text
            ...(() => {
              const best = effectiveAdSet.ads.find((a) => a.primaryText) || effectiveAdSet.ads[0];
              return {
                bestPrimaryText: best?.primaryText || "",
                bestHeadline: best?.headline || "",
                bestDescription: best?.description || "",
                bestCallToAction: best?.callToAction || "LEARN_MORE",
                bestLinkUrl: best?.linkUrl || "",
              };
            })(),
            newLeadgenFormId: dupLeadgenFormId || undefined,
          }),
          ...(dupUseCurrentFormAds && {
            useCurrentFormAds: true,
          }),
        };
      }

      // SSE stream reader helper
      const readSSELaunch = async (
        launchPayload: Record<string, unknown>,
        stepPrefix: string
      ): Promise<LaunchResult> => {
        const token = getAuthToken();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const response = await fetch("/api/campaign-creator/launch", {
          method: "POST",
          headers,
          body: JSON.stringify(launchPayload),
        });

        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("text/event-stream")) {
          const text = await response.text();
          if (!response.ok) throw new Error(text);
          return JSON.parse(text) as LaunchResult;
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let finalResult: LaunchResult | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (const part of parts) {
            const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
            if (!dataLine) continue;

            const data = JSON.parse(dataLine.slice(6));
            if (data.type === "done") {
              finalResult = data as LaunchResult;
            } else if (data.type === "step") {
              setLaunchProgress((prev) => {
                const step = stepPrefix ? `${stepPrefix}-${data.step}` : data.step;
                const message = stepPrefix ? `[${stepPrefix}] ${data.message}` : data.message;
                const idx = prev.findIndex((p) => p.step === step);
                const entry = { step, status: data.status, message, id: data.id };
                if (idx >= 0) return prev.map((p, i) => (i === idx ? entry : p));
                return [...prev, entry];
              });
            }
          }
        }

        if (!finalResult) throw new Error(t("errors.streamEnd"));
        return finalResult;
      };

      // Phase 2: Launch campaign (includes duplicate ad set if A/B enabled)
      const mainResult = await readSSELaunch(payload, "");

      return mainResult;
    },
    onSuccess: (data: LaunchResult) => {
      setIsLaunching(false);
      setLaunchResult(data);

      // Clear localStorage draft after successful launch
      if (data.success) {
        try {
          localStorage.removeItem("campaign-builder.draft");
        } catch {}
        setDraftSavedAt(null);
      }

      // Auto-add creatives to pipeline "w-trakcie-testu" column
      if (data.success) {
        for (const creative of creatives) {
          const label = creative.type === "video" ? "video" : "grafika";
          addCreativeToPipeline(
            {
              id: `campaign-${Date.now()}-${creative.id}`,
              title: creative.name || campaignName || "Kreacja z kampanii",
              description: `Kampania: ${campaignName}`,
              labels: [label],
              imageUrl: creative.preview,
            },
            "w-trakcie-testu",
            user?.id
          );
        }
      }
      const adsManagerUrl = data.campaignId
        ? `https://www.facebook.com/adsmanager/manage/campaigns?act=${bareAdAccountId(adAccountId)}&campaign_ids=${data.campaignId}`
        : undefined;
      const launchedNoFunding = data.success && adAccountFunding?.status === "missing";
      // Tylko realnie częściowy stan: kampania faktycznie powstała (campaignId obecne),
      // ale część reklam padła. Preflight-fail i top-level catch dają success:false BEZ
      // campaignId — wtedy nic nie powstało, więc nie sugerujemy sprzątania w Ads Managerze.
      const partiallyCreated = !data.success && !!data.campaignId;
      toast({
        title: data.success ? t("toasts.campaignCreated") : t("toasts.partialSuccess"),
        description: (
          <>
            {partiallyCreated && (
              <div className="mb-1 font-semibold">{t("toasts.partialPaused")}</div>
            )}
            {data.message}
            {launchedNoFunding && (
              <div className="mt-1 text-amber-300">{t("toasts.launchedNoFunding")}</div>
            )}
            {adsManagerUrl && (
              <>
                {" "}
                <a
                  href={adsManagerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold text-blue-400 hover:text-blue-300"
                >
                  {t("toasts.openAdsManager")} →
                </a>
              </>
            )}
            {data.dupAdSetUrl && (
              <>
                {" "}
                <a
                  href={data.dupAdSetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold text-purple-400 hover:text-purple-300"
                >
                  {t("toasts.openDupAdSet")} →
                </a>
              </>
            )}
          </>
        ),
        variant: data.success ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      setIsLaunching(false);
      if (error.message.includes("SUBSCRIPTION_REQUIRED") || error.message.includes("403")) {
        setPaywallOpen(true);
        return;
      }
      toast({
        title: t("toasts.launchError"),
        description: t("toasts.launchErrorDesc"),
        variant: "destructive",
      });
    },
  });

  const firstPrimaryTextFilled = !!primaryTexts[0];
  const firstHeadlineFilled = !!headlines[0];

  // Effective ad set: auto (best) or manual selection
  const effectiveAdSet =
    abTestMode === "manual" && manualSelectedAdSet
      ? {
          adSetId: manualSelectedAdSet.adSetId,
          adSetName: manualSelectedAdSet.adSetName,
          campaignId: manualSelectedAdSet.campaignId,
          targeting: manualSelectedAdSet.targeting,
          optimizationGoal: manualSelectedAdSet.optimizationGoal,
          destinationType: manualSelectedAdSet.destinationType,
          ads: manualSelectedAdSet.ads,
          spend: manualSelectedAdSet.spend,
          ctr: manualSelectedAdSet.ctr,
          clicks: manualSelectedAdSet.clicks,
          leads: manualSelectedAdSet.leads,
          costPerLead: manualSelectedAdSet.costPerLead,
          dailyBudget: manualSelectedAdSet.dailyBudget as number | undefined,
          lifetimeBudget: manualSelectedAdSet.lifetimeBudget as number | undefined,
          instagramActorId: (manualSelectedAdSet.instagramActorId || undefined) as
            | string
            | undefined,
          sourcePageId: manualSelectedAdSet.sourcePageId,
          brandedContentSponsorPageId: manualSelectedAdSet.brandedContentSponsorPageId,
          promotedObject: manualSelectedAdSet.promotedObject as
            | Record<string, unknown>
            | null
            | undefined,
        }
      : abTestBestAdSet;

  const isCloneOnly = dupCloneBestAds && abTestEnabled && !!effectiveAdSet;
  // dupOnly = skip campaign creation entirely, just add duplicate ad set to source campaign
  const isDupOnly =
    (dupBestTextSwapVideo || dupUseCurrentFormAds) &&
    abTestEnabled &&
    !!effectiveAdSet &&
    dupCampaignMode === "source" &&
    !!effectiveAdSet.campaignId;
  // swapNewCampaign = create new campaign but skip main broad ad set, only duplicate ad set with source targeting
  const isSwapNewCampaign =
    (dupBestTextSwapVideo || dupUseCurrentFormAds) &&
    abTestEnabled &&
    !!effectiveAdSet &&
    dupCampaignMode === "new";
  const isAddToAdSet = addToAdSetEnabled && !!selectedAddToAdSet;
  const dupOnlyNeedsManualTexts = (isDupOnly || isSwapNewCampaign) && dupUseCurrentFormAds;
  const canLaunch = isAddToAdSet
    ? !!(
        adAccountId &&
        pageId &&
        creatives.length > 0 &&
        selectedAddToAdSet &&
        (addToAdSetUseSourceTexts || (firstPrimaryTextFilled && firstHeadlineFilled && linkUrl))
      ) && !isLaunching
    : isCloneOnly || isDupOnly || isSwapNewCampaign
      ? !!(
          campaignName &&
          adAccountId &&
          pageId &&
          effectiveAdSet?.targeting &&
          (isDupOnly || isSwapNewCampaign ? creatives.length > 0 : true) &&
          (!dupOnlyNeedsManualTexts || (firstPrimaryTextFilled && firstHeadlineFilled && !!linkUrl))
        ) && !isLaunching
      : !!(
          campaignName &&
          (isOnAdOnly
            ? !!selectedLeadgenFormId && (!needsManualLeadgenLink || !!linkUrl)
            : !!linkUrl) &&
          firstPrimaryTextFilled &&
          firstHeadlineFilled &&
          creatives.length > 0 &&
          (!needsPixelConfig || !!selectedPixelId) &&
          (selectedConversionEvent !== "OTHER" || !!selectedCustomConversionId) &&
          (budgetMode !== "lifetime" || (Number(lifetimeBudget) > 0 && !!budgetEndDate))
        ) && !isLaunching;

  // Map missing-field labels → anchor IDs for scroll-to-field
  const FIELD_ANCHORS: Record<string, string> = {
    "nazwa kampanii": "field-campaign-name",
    "konto reklamowe": "field-ad-account",
    "URL strony docelowej": "field-link-url",
    "tekst główny reklamy": "field-primary-text",
    "nagłówek reklamy": "field-headline",
    "kreatywy (zdjęcie/wideo)": "field-creatives",
    "Meta Pixel": "field-pixel",
    "zdarzenie konwersji": "field-conversion-event",
    "Facebook Page": "field-fb-page",
    "audiencja źródłowa": "field-targeting",
    "docelowy ad set": "field-target-adset",
  };
  const scrollToField = useCallback((label: string) => {
    const anchorId = FIELD_ANCHORS[label];
    if (!anchorId) return;
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief highlight pulse
      el.classList.add("ring-2", "ring-red-500/60", "rounded-lg", "transition-all");
      setTimeout(() => el.classList.remove("ring-2", "ring-red-500/60", "rounded-lg"), 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIELD_ANCHORS to stala mapa etykieta->id, tworzona na nowo przy renderze; jej zawartosc sie nie zmienia
  }, []);

  // Build human-readable list of missing fields for the disabled Launch button
  const missingFields: string[] = useMemo(() => {
    if (canLaunch || isLaunching) return [];
    const out: string[] = [];
    if (isAddToAdSet) {
      if (!adAccountId) out.push("konto reklamowe");
      if (!pageId) out.push("Facebook Page");
      if (!creatives.length) out.push("kreatywy (zdjęcie/wideo)");
      if (!selectedAddToAdSet) out.push("docelowy ad set");
      if (!addToAdSetUseSourceTexts) {
        if (!firstPrimaryTextFilled) out.push("tekst główny reklamy");
        if (!firstHeadlineFilled) out.push("nagłówek reklamy");
        if (!linkUrl) out.push("URL strony docelowej");
      }
    } else {
      if (!campaignName) out.push("nazwa kampanii");
      if (!adAccountId) out.push("konto reklamowe");
      if (!isCloneOnly && !isDupOnly && !isSwapNewCampaign) {
        if (isOnAdOnly) {
          if (!selectedLeadgenFormId) out.push("formularz błyskawiczny");
          else if (needsManualLeadgenLink && !linkUrl) out.push("URL strony docelowej");
        } else if (!linkUrl) out.push("URL strony docelowej");
        if (!firstPrimaryTextFilled) out.push("tekst główny reklamy");
        if (!firstHeadlineFilled) out.push("nagłówek reklamy");
        if (!creatives.length) out.push("kreatywy (zdjęcie/wideo)");
        if (needsPixelConfig && !selectedPixelId) out.push("Meta Pixel");
        if (selectedConversionEvent === "OTHER" && !selectedCustomConversionId)
          out.push("zdarzenie konwersji");
        if (budgetMode === "lifetime") {
          if (!(Number(lifetimeBudget) > 0)) out.push("budżet całkowity");
          if (!budgetEndDate) out.push("data zakończenia (budżet całkowity)");
        }
      } else {
        if (!pageId) out.push("Facebook Page");
        if (!effectiveAdSet?.targeting) out.push("audiencja źródłowa");
        if ((isDupOnly || isSwapNewCampaign) && !creatives.length)
          out.push("kreatywy (zdjęcie/wideo)");
        if (dupOnlyNeedsManualTexts) {
          if (!firstPrimaryTextFilled) out.push("tekst główny reklamy");
          if (!firstHeadlineFilled) out.push("nagłówek reklamy");
          if (!linkUrl) out.push("URL strony docelowej");
        }
      }
    }
    return out;
  }, [
    canLaunch,
    isLaunching,
    isAddToAdSet,
    adAccountId,
    pageId,
    creatives.length,
    selectedAddToAdSet,
    addToAdSetUseSourceTexts,
    firstPrimaryTextFilled,
    firstHeadlineFilled,
    linkUrl,
    campaignName,
    isCloneOnly,
    isDupOnly,
    isSwapNewCampaign,
    needsPixelConfig,
    selectedPixelId,
    selectedConversionEvent,
    selectedCustomConversionId,
    effectiveAdSet?.targeting,
    dupOnlyNeedsManualTexts,
    isOnAdOnly,
    selectedLeadgenFormId,
    needsManualLeadgenLink,
    budgetMode,
    lifetimeBudget,
    budgetEndDate,
  ]);

  // Budget sanity check — flags suspiciously low/high daily budget
  const budgetWarning = useMemo(() => {
    const n = Number(dailyBudget);
    if (!dailyBudget || isNaN(n) || n <= 0) return null;
    if (n < 10)
      return {
        kind: "error" as const,
        msg: `Bardzo niski budżet (${n} ${selectedAdAccountCurrency}/dzień). Meta może odrzucić kampanię — zalecane minimum to 20 ${selectedAdAccountCurrency}/dzień.`,
      };
    if (n < 25)
      return {
        kind: "warn" as const,
        msg: `Niski budżet — przy ${n} ${selectedAdAccountCurrency}/dzień szansa na konwersje jest mała. Optymalnie 50–100 ${selectedAdAccountCurrency}/dzień.`,
      };
    if (n > 1000)
      return {
        kind: "warn" as const,
        msg: `Bardzo wysoki budżet (${n} ${selectedAdAccountCurrency}/dzień ≈ ${(n * 30).toLocaleString("pl-PL")} ${selectedAdAccountCurrency}/mies.). Upewnij się że to celowe.`,
      };
    return null;
  }, [dailyBudget, selectedAdAccountCurrency]);

  // Per-section completion status — drives green checkmarks on section headers
  const sectionDone = useMemo(
    () => ({
      s1: !!adAccountId && !!campaignName && !!objective,
      s2: !!optimizationGoal && (!needsPixelConfig || !!selectedPixelId),
      s3: true, // audience has defaults — always "ok"
      s4: true, // placement has defaults
      s5: !!linkUrl && firstPrimaryTextFilled && firstHeadlineFilled && !!pageId,
      s6: creatives.length > 0,
      s7: true, // tracking defaults
    }),
    [
      adAccountId,
      campaignName,
      objective,
      optimizationGoal,
      needsPixelConfig,
      selectedPixelId,
      linkUrl,
      firstPrimaryTextFilled,
      firstHeadlineFilled,
      pageId,
      creatives.length,
    ]
  );

  // Progress: how many required fields are filled vs total (standard non-clone path)
  const progressFilled = useMemo(() => {
    if (isAddToAdSet) {
      const checks = [!!adAccountId, !!pageId, creatives.length > 0, !!selectedAddToAdSet];
      return { done: checks.filter(Boolean).length, total: checks.length };
    }
    if (isCloneOnly || isDupOnly || isSwapNewCampaign) {
      const checks = [!!campaignName, !!adAccountId, !!pageId, !!effectiveAdSet?.targeting];
      return { done: checks.filter(Boolean).length, total: checks.length };
    }
    const checks = [
      !!campaignName,
      !!adAccountId,
      !!linkUrl,
      firstPrimaryTextFilled,
      firstHeadlineFilled,
      creatives.length > 0,
    ];
    return { done: checks.filter(Boolean).length, total: checks.length };
  }, [
    isAddToAdSet,
    isCloneOnly,
    isDupOnly,
    isSwapNewCampaign,
    adAccountId,
    pageId,
    creatives.length,
    selectedAddToAdSet,
    campaignName,
    linkUrl,
    firstPrimaryTextFilled,
    firstHeadlineFilled,
    effectiveAdSet?.targeting,
  ]);

  // Keyboard shortcuts:
  //   ⌘+Enter / Ctrl+Enter — opens the confirm modal (when ready)
  //   Shift+? (i.e. "?")  — toggles "Jak to działa" infographic
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore if the focus is inside an input/textarea/contenteditable
      const target = e.target as HTMLElement | null;
      const inEditable =
        !!target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        if (canLaunch && !isLaunching && !confirmLaunchOpen) {
          e.preventDefault();
          setConfirmLaunchOpen(true);
        }
        return;
      }
      // ⌘+S / Ctrl+S — force save draft (no real save needed since auto-save runs, but flash the indicator)
      if (e.key === "s" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setDraftSavedAt(Date.now());
        return;
      }
      if (!inEditable && e.key === "?") {
        e.preventDefault();
        setShowHowItWorks((v) => !v);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canLaunch, isLaunching, confirmLaunchOpen]);

  const handleNewCampaign = () => {
    setLaunchResult(null);
    setLaunchProgress([]);
    setIsLaunching(false);
    setCreatives([]);
    setCampaignName("");
    setAdSetName("");
    setPrimaryTexts(["", "", "", "", ""]);
    setHeadlines(["", "", "", "", ""]);
    setLinkUrl("");
    setDescription("");
    setAbTestEnabled(false);
    setAbTestBestAdSet(null);
    setSelectedPreset("");
    try {
      localStorage.removeItem("campaign-builder.draft");
    } catch {}
  };

  // ==================== Not connected ====================
  if (!metaStatus?.connected) {
    const wrongAppMsg = metaStatus?.wrongApp
      ? t("notConnected.reconnectDesc")
      : t("notConnected.connectDesc");
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto">
          <Rocket className="w-12 h-12 text-white/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            {metaStatus?.wrongApp ? t("notConnected.reconnect") : t("notConnected.connect")}
          </h3>
          <p className="text-white/60 text-sm mb-5">{wrongAppMsg}</p>
          <Button
            onClick={() => {
              window.location.href = "/integracje?open=meta";
            }}
            className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white"
          >
            {metaStatus?.wrongApp ? t("notConnected.reconnect") : t("notConnected.connect")}
          </Button>
          <p className="text-white/40 text-xs mt-4">
            {t("notConnected.noBusinessQuestion")}{" "}
            <a
              href="https://business.facebook.com/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/60 underline underline-offset-2 hover:text-white/80"
            >
              {t("notConnected.createBusiness")} ↗
            </a>
          </p>
        </div>
      </div>
    );
  }

  // ==================== Render ====================
  if (fastTrackMode) {
    return (
      <FastTrackFlow
        clientId={selectedClientId || null}
        onCancel={() => setFastTrackMode(false)}
        onComplete={({ draftId }) => {
          setFastTrackMode(false);
          setPendingDraftId(draftId);
        }}
      />
    );
  }
  return (
    <div className="campaign-builder-shell flex flex-col h-full min-h-0 relative bg-[#06070a]">
      <div className="campaign-builder-backdrop absolute inset-0 pointer-events-none bg-[radial-gradient(1200px_600px_at_15%_-10%,rgba(239,62,41,0.10),transparent_65%),radial-gradient(900px_500px_at_85%_10%,rgba(239,62,41,0.06),transparent_70%),linear-gradient(180deg,#07080c_0%,#05060a_100%)]" />
      <div className="flex-1 min-h-0 overflow-y-auto relative">
        <div className="px-6 pt-8 pb-10 max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-2">
            <div
              className="w-10 h-10 bg-gradient-to-br from-red-600 to-orange-500 rounded-xl flex items-center justify-center"
              title="Kreator Kampanii Meta Ads — uruchom kampanię w 5 minut"
            >
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{t("header.title")}</h2>
              <p className="text-xs text-white/50">{t("header.subtitle")}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFastTrackMode(true)}
              title="Szybki Tor — wizard krok-po-kroku zamiast pełnego formularza. Pyta tylko o najważniejsze rzeczy (cel, budżet, kreatywy) i resztę dobiera automatycznie. Polecane jeśli pierwszy raz tworzysz kampanię."
              className="border-orange-500/40 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              {t("fastTrack.entry", { defaultValue: "Szybki tor 🚀" })}
            </Button>
            {progressFilled.total > 0 && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.03]"
                title={`Wypełniono ${progressFilled.done} z ${progressFilled.total} wymaganych pól. ${progressFilled.done === progressFilled.total ? "Wszystko gotowe — możesz uruchomić kampanię!" : `Zostało ${progressFilled.total - progressFilled.done} pól. Kliknij "Następny krok" niżej by przewinąć do pierwszego brakującego.`}`}
              >
                <div className="flex items-center gap-1.5">
                  <div className="relative w-16 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className={`absolute left-0 top-0 h-full transition-all ${
                        progressFilled.done === progressFilled.total
                          ? "bg-emerald-400"
                          : progressFilled.done >= progressFilled.total / 2
                            ? "bg-amber-400"
                            : "bg-red-400"
                      }`}
                      style={{ width: `${(progressFilled.done / progressFilled.total) * 100}%` }}
                    />
                  </div>
                  <span
                    className={`text-[11px] font-medium tabular-nums ${
                      progressFilled.done === progressFilled.total
                        ? "text-emerald-400"
                        : "text-white/70"
                    }`}
                  >
                    {progressFilled.done}/{progressFilled.total}
                  </span>
                </div>
                {progressFilled.done === progressFilled.total && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                )}
              </div>
            )}
            {!showHowItWorks && (
              <button
                onClick={() => setShowHowItWorks(true)}
                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition px-2.5 py-1.5 rounded-lg border border-white/[0.08] hover:border-white/[0.2]"
                title="Pokaż przewodnik (skrót: ?)"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Jak to działa
                <kbd className="ml-1 px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/50 font-mono text-[9px]">
                  ?
                </kbd>
              </button>
            )}
          </div>

          {/* Legend: required-field asterisk meaning */}
          <p className="text-[10px] text-white/30 -mt-3 flex items-center gap-2 flex-wrap">
            <span>
              <span className="text-red-400">*</span> wymagane
            </span>
            <span className="text-white/20">·</span>
            <span title="Najedź na ikonkę by zobaczyć tooltip z wyjaśnieniem co to za pole i jak je wypełnić.">
              <HelpCircle className="w-2.5 h-2.5 inline text-white/30 mr-1" />
              ikonki przy etykietach mają wyjaśnienia
            </span>
            <span className="text-white/20">·</span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/50 font-mono text-[9px]">
                ⌘ Enter
              </kbd>{" "}
              uruchamia kampanię
            </span>
            <span className="text-white/20">·</span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/50 font-mono text-[9px]">
                ?
              </kbd>{" "}
              pokazuje przewodnik
            </span>
          </p>

          {/* TOC nav: 9 step circles, click to scroll to section, color reflects status */}
          {(() => {
            const TOC_ITEMS = [
              { n: 1, label: "Kampania", advanced: false },
              { n: 2, label: "Optymalizacja", advanced: false },
              { n: 3, label: "Audiencja", advanced: false },
              { n: 4, label: "Placement", advanced: false },
              { n: 5, label: "Reklama", advanced: false },
              { n: 6, label: "Pliki", advanced: false },
              { n: 7, label: "Tracking", advanced: false },
              { n: 8, label: "A/B Test", advanced: true },
              { n: 9, label: "Dolewanie", advanced: true },
            ];
            const status = (n: number): "done" | "todo" | "optional" => {
              if (n === 8 || n === 9) return "optional";
              const key = `s${n}` as keyof typeof sectionDone;
              return sectionDone[key] ? "done" : "todo";
            };
            const scrollToSection = (n: number) => {
              const el = document.getElementById(`section-${n}`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                el.classList.add("ring-2", "ring-red-500/40", "rounded-xl", "transition-all");
                setTimeout(
                  () => el.classList.remove("ring-2", "ring-red-500/40", "rounded-xl"),
                  1200
                );
              }
            };
            return (
              <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1">
                {TOC_ITEMS.map((item, i) => {
                  const s = status(item.n);
                  return (
                    <div key={item.n} className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => scrollToSection(item.n)}
                        title={`${item.label} — ${s === "done" ? "✓ wypełnione" : s === "optional" ? "opcjonalna sekcja zaawansowana" : "wymaga uwagi"}. Kliknij by przejść do tej sekcji.`}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition border ${
                          s === "done"
                            ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/15"
                            : s === "optional"
                              ? "bg-purple-500/[0.06] border-purple-500/20 text-purple-300/80 hover:bg-purple-500/10"
                              : "bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06] hover:text-white/70"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                            s === "done"
                              ? "bg-emerald-500/20"
                              : s === "optional"
                                ? "bg-purple-500/15"
                                : "bg-white/[0.08]"
                          }`}
                        >
                          {s === "done" ? <Check className="w-2.5 h-2.5" /> : item.n}
                        </span>
                        <span className="font-medium">{item.label}</span>
                      </button>
                      {i < TOC_ITEMS.length - 1 && <div className="w-2 h-px bg-white/[0.08]" />}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Next-step CTA: jump to first missing required field */}
          {missingFields.length > 0 && missingFields[0] && FIELD_ANCHORS[missingFields[0]] && (
            <button
              type="button"
              onClick={() => scrollToField(missingFields[0])}
              title={`Kliknij by przewinąć do pierwszego brakującego pola: ${missingFields[0]}. Potem wypełnij i wróć tu — przycisk pokaże następne brakujące pole.`}
              className="self-start flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-500/15 hover:border-red-500/50 transition group animate-[pulse_3s_ease-in-out_infinite]"
            >
              <ChevronDown className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition" />
              Następny krok: <span className="font-semibold">{missingFields[0]}</span>
              <span className="text-[10px] text-red-600 dark:text-red-300/60 ml-1">
                ({missingFields.length}{" "}
                {missingFields.length === 1 ? "pole" : missingFields.length < 5 ? "pola" : "pól"} do
                wypełnienia)
              </span>
              <ArrowRight className="w-3 h-3 ml-0.5 group-hover:translate-x-0.5 transition" />
            </button>
          )}

          {/* ================ HOW IT WORKS INFOGRAPHIC ================ */}
          {showHowItWorks && (
            <div className="relative rounded-2xl bg-gradient-to-br from-red-500/[0.06] via-purple-500/[0.04] to-transparent border border-white/[0.08] p-5">
              <button
                onClick={dismissHowItWorks}
                className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.08] transition"
                title="Ukryj na zawsze (kliknij '?' lub button 'Jak to działa' w nagłówku, by wrócić)"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-red-400" />
                <h3 className="text-sm font-bold text-white tracking-wide uppercase">
                  Jak to działa — 3 kroki
                </h3>
              </div>
              <p className="text-[11px] text-white/40 mb-5">
                Stwórz kampanię na Meta Ads w 3 prostych krokach. Cały proces zajmie ~5 minut.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Step 1 */}
                <div className="rounded-xl bg-black/30 border border-white/[0.06] p-4 hover:border-white/[0.12] transition">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-bold">
                      1
                    </div>
                    <Bookmark className="w-3.5 h-3.5 text-white/60" />
                    <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">
                      Schemat
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-white mb-1.5">
                    Wybierz typ kampanii
                  </div>
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    <span className="text-white/70">VSL Funnel</span>,{" "}
                    <span className="text-white/70">Autowebinar</span>,{" "}
                    <span className="text-white/70">Lead Gen</span> lub{" "}
                    <span className="text-white/70">Traffic</span> — wybór schematu auto-wypełnia
                    ustawienia.
                  </p>
                </div>
                {/* Step 2 */}
                <div className="rounded-xl bg-black/30 border border-white/[0.06] p-4 hover:border-white/[0.12] transition">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs font-bold">
                      2
                    </div>
                    <Settings2 className="w-3.5 h-3.5 text-white/60" />
                    <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">
                      Konfiguracja
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-white mb-1.5">
                    Klient, budżet, audiencja
                  </div>
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    Wybierz konto reklamowe, ustaw budżet (
                    <span className="text-white/70">ABO</span> = na ad set /{" "}
                    <span className="text-white/70">CBO</span> = na kampanię), audiencję i wgraj
                    kreatywy (grafika lub wideo).
                  </p>
                </div>
                {/* Step 3 */}
                <div className="rounded-xl bg-black/30 border border-white/[0.06] p-4 hover:border-white/[0.12] transition">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <Rocket className="w-3.5 h-3.5 text-white/60" />
                    <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">
                      Uruchom
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-white mb-1.5">
                    Klik "Uruchom kampanię"
                  </div>
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    Kampania ląduje w <span className="text-white/70">Meta Ads</span> jako{" "}
                    <span className="text-yellow-400 font-medium">PAUSED</span> — sprawdź ją w Ads
                    Managerze i włącz ręcznie. Bezpieczna domyślnie.
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/[0.04] flex items-start gap-2">
                <HelpCircle className="w-3.5 h-3.5 text-white/40 shrink-0 mt-0.5" />
                <p className="text-[11px] text-white/40 leading-relaxed">
                  <span className="text-white/60">Wskazówka:</span> jeśli to twoja pierwsza
                  kampania, zacznij od schematu{" "}
                  <span className="text-white/70">Lead Generation</span> + budżet ABO
                  50–100zł/dziennie. Wyłącz A/B Test (krok 8) na początek.
                </p>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-white/30">
                <span className="font-medium text-white/40">Skróty:</span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/60 font-mono">
                    ⌘ Enter
                  </kbd>
                  <span>uruchom</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/60 font-mono">
                    ⌘ S
                  </kbd>
                  <span>zapisz roboczą wersję</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/60 font-mono">
                    ?
                  </kbd>
                  <span>pokaż/ukryj ten przewodnik</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-white/40">·</span>
                  <span>Auto-save co 0.4s</span>
                </span>
              </div>
            </div>
          )}

          {/* ================ PRESETS ================ */}
          <div className="flex flex-wrap items-center gap-2">
            <Bookmark className="w-4 h-4 text-muted-foreground" />
            <span
              className="text-xs text-muted-foreground mr-1"
              title="Schematy auto-uzupełniają cel kampanii, typ budżetu, lokalizację konwersji i nazwę. Wybierz schemat zamiast ręcznie ustawiać każde pole."
            >
              {t("presets.label")}
            </span>
            {CAMPAIGN_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset.id)}
                title={preset.description}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  selectedPreset === preset.id
                    ? "bg-red-500/20 text-foreground border-red-500/40"
                    : "bg-muted text-foreground/70 border-border hover:text-foreground hover:border-border/80"
                }`}
              >
                {preset.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1.5">
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder={t("presets.brandPlaceholder")}
                title="Nazwa twojej firmy używana w auto-szablonach nazw kampanii i ad setów. Zapisuje się lokalnie na zawsze."
                className="h-7 w-40 text-xs bg-background border-border text-foreground placeholder:text-muted-foreground"
              />
              <span title="Nazwa twojej firmy / brandu — używana w szablonach. Zapamiętujemy na lokalnie.">
                <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
              </span>
            </div>
          </div>

          {/* Active preset description */}
          {selectedPreset &&
            (() => {
              const desc = CAMPAIGN_PRESETS.find((p) => p.id === selectedPreset)?.description;
              return desc ? (
                <p className="text-[11px] text-red-300/70 -mt-2 flex items-start gap-1.5">
                  <Bookmark className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>Schemat: {desc}</span>
                </p>
              ) : null;
            })()}

          {/* ================ SECTION 1: KAMPANIA ================ */}
          <div id="section-1" className={SECTION_CLS}>
            <div className="flex items-center gap-2.5">
              <span
                className={`w-6 h-6 rounded-full border text-[11px] font-bold flex items-center justify-center flex-shrink-0 ${
                  sectionDone.s1
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-red-600/15 border-red-500/25 text-red-400"
                }`}
              >
                {sectionDone.s1 ? <Check className="w-3 h-3" /> : "1"}
              </span>
              <Rocket className="w-3.5 h-3.5 text-white/40" />
              <h3 className="text-sm font-semibold text-white tracking-wide flex items-center gap-1.5">
                {t("section1.title")}
                <span title="Podstawowe ustawienia kampanii: konto reklamowe, cel, nazwa, typ budżetu. To korzeń całej kampanii.">
                  <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                </span>
              </h3>
            </div>

            {/* Client picker */}
            {agencyClients.length > 0 && (
              <Field>
                <FieldLabel
                  className="flex items-center gap-1"
                  title="Wybór klienta auto-uzupełnia konto reklamowe, FB Page, IG Account i Pixel z konfiguracji klienta. Skrót: nie musisz ich szukać po Meta API."
                >
                  <Users className="w-3.5 h-3.5 text-cyan-400/70" />
                  {t("section1.clientLabel")}
                  <HelpCircle className="w-3 h-3 text-white/30 ml-1" />
                </FieldLabel>
                <Select value={selectedClientId} onValueChange={handleClientSelect}>
                  <SelectTrigger
                    className={SELECT_TRIGGER_CLS}
                    title="Wybierz klienta z listy. Skróci to konfigurację: konto reklamowe, FB Page, IG Account i Pixel zostaną auto-wypełnione na podstawie ustawień klienta."
                  >
                    <SelectValue placeholder={t("section1.clientPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT_CLS}>
                    {agencyClients.map((c) => (
                      <SelectItem key={c.id} value={c.id} className={SELECT_ITEM_CLS}>
                        {c.name}
                        {c.offerSheetId && (
                          <span
                            className="text-cyan-400/60 ml-2 text-[10px]"
                            title="Klient ma podpięty Google Sheet z ofertami/produktami — Twórca Ofert i AI Copy mogą z niego korzystać."
                          >
                            {t("section1.clientSheetBadge")}
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClient && getClientMetaAccounts(selectedClient).length === 0 && (
                  <p className="text-[10px] text-amber-400/60 mt-1">{t("section1.clientNoMeta")}</p>
                )}
                {!selectedClientId && (
                  <p className="text-[10px] text-cyan-300/70 mt-1 flex items-start gap-1">
                    <HelpCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                      Wybierz klienta, aby filtrować konta reklamowe i auto-uzupełnić
                      Page/Pixel/Instagram dla tego klienta.
                    </span>
                  </p>
                )}
              </Field>
            )}

            {/* Offer sheet selector for non-agency users */}
            {!isAgencyUser && (
              <Field>
                <FieldLabel
                  className="flex items-center gap-1"
                  title="Offer sheet to dokument z pełnym opisem oferty (cena, benefity, FAQ). AI użyje go jako kontekstu przy pisaniu copy reklamy."
                >
                  <FileText className="w-3.5 h-3.5 text-emerald-400/70" />
                  {t("section1.sheetLabel")}
                  <HelpCircle className="w-3 h-3 text-white/30" />
                </FieldLabel>
                <Select value={selectedOfferSheetId} onValueChange={setSelectedOfferSheetId}>
                  <SelectTrigger
                    className={SELECT_TRIGGER_CLS}
                    title="Wybierz offer sheet — AI przeczyta go i dopasuje copy reklamy do Twojej oferty"
                  >
                    <SelectValue placeholder={t("section1.sheetPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT_CLS}>
                    <SelectItem value="none" className={SELECT_ITEM_CLS}>
                      {t("section1.sheetDefaultItem")}
                    </SelectItem>
                    {offerSheetsList.map((s) => (
                      <SelectItem key={s.id} value={s.id} className={SELECT_ITEM_CLS}>
                        {s.title}
                        {s.isActive && (
                          <span className="text-green-400/60 ml-2 text-[10px]">
                            {t("section1.sheetActiveBadge")}
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {offerSheetsList.length === 0 && (
                  <p className="text-[10px] text-amber-400/60 mt-1">{t("section1.sheetEmpty")}</p>
                )}
              </Field>
            )}

            {/* Ad account with search */}
            <Field className="relative" id="field-ad-account">
              <FieldLabel>
                <span
                  className="inline-flex items-center gap-1.5"
                  title="Konto reklamowe Meta (act_xxx) na które idzie wydatek. Lista pochodzi z Business Managera. Wybór konta auto-ładuje powiązane Piksele, audiencje, formularze i ad sety."
                >
                  {t("section1.adAccountLabel")}
                  <span className="text-red-400 ml-0.5" title="Pole wymagane">
                    *
                  </span>
                  <HelpCircle className="w-3 h-3 text-white/30" />
                </span>
              </FieldLabel>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-white/30 pointer-events-none" />
                <Input
                  value={(() => {
                    if (adAccountDropdownOpen) return adAccountSearch;
                    if (!adAccountId) return "";
                    const acc = allAdAccounts.find((a) => a.id === adAccountId);
                    // withActPrefix — id z Meta ma juz `act_`, z recznego wpisu moze nie
                    // miec; bezwarunkowe doklejanie dawalo `act_act_...` (VP-233).
                    if (!acc) return withActPrefix(adAccountId);
                    return acc.name
                      ? `${acc.name} · ${withActPrefix(acc.id)}`
                      : withActPrefix(acc.id);
                  })()}
                  onChange={(e) => {
                    setAdAccountSearch(e.target.value);
                    setAdAccountDropdownOpen(true);
                  }}
                  onFocus={() => setAdAccountDropdownOpen(true)}
                  placeholder={t("section1.adAccountSearch")}
                  title="Wpisz nazwę lub ID konta reklamowego z Meta Ads. Lista zawiera wszystkie konta do których masz dostęp przez Business Managera."
                  className={`${INPUT_CLS} pl-8`}
                />
              </div>
              {adAccountDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setAdAccountDropdownOpen(false)}
                  />
                  <div className="absolute z-20 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#0a0c10] shadow-xl">
                    {filteredAdAccounts.length === 0 && (
                      <div className="p-3 space-y-1">
                        <p className="text-xs text-white/30">{t("section1.adAccountEmpty")}</p>
                        <p className="text-[10px] text-white/40">
                          Sprawdź czy w Business Managerze masz dostęp do co najmniej jednego konta
                          reklamowego.
                        </p>
                      </div>
                    )}
                    {filteredAdAccounts.map((acc) => (
                      <button
                        key={acc.id}
                        type="button"
                        onClick={() => {
                          setAdAccountId(acc.id);
                          setAdAccountSearch("");
                          setAdAccountDropdownOpen(false);
                          // Load per-ad-account Meta defaults from selected client
                          if (selectedClientId) {
                            const client = agencyClients.find((c) => c.id === selectedClientId);
                            const entry = (client?.adAccounts || []).find((a) => a.id === acc.id);
                            if (entry?.facebookPageId) setPageId(entry.facebookPageId);
                            if (entry?.instagramActorId)
                              setInstagramActorId(entry.instagramActorId);
                            if (entry?.pixelId) setSelectedPixelId(entry.pixelId);
                          }
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
                          acc.id === adAccountId
                            ? "bg-red-500/20 text-white border-l-2 border-red-500"
                            : "text-white/70 hover:bg-white/[0.06]"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm">
                            {acc.name || `Konto ${withActPrefix(acc.id)}`}
                          </div>
                          <div className="text-[10px] text-white/30 truncate font-mono">
                            {withActPrefix(acc.id)}
                          </div>
                        </div>
                        {acc.id === adAccountId && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-red-400 flex-shrink-0 ml-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {!adAccountId && (
                <p className="text-[10px] text-amber-300/70 mt-1 flex items-start gap-1">
                  <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>
                    Wybierz konto, aby załadować{" "}
                    <span className="text-white/70">Piksele, audiencje, formularze i ad sety</span>{" "}
                    z tego konta.
                  </span>
                </p>
              )}
              {adAccountId && adAccountFunding?.status === "missing" && (
                <div className="mt-2 rounded-lg border border-red-500/40 bg-red-500/[0.08] p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
                  <div className="flex-1 space-y-1">
                    <div className="text-xs font-semibold text-red-200">
                      Brak metody płatności na tym koncie reklamowym
                    </div>
                    <p className="text-[11px] text-white/70 leading-snug">
                      Meta nie ma podpiętej karty/PayPal/przelewu — kampania zostanie odrzucona albo
                      natychmiast wstrzymana po próbie odpalenia. Dodaj metodę płatności w Ads
                      Managerze zanim klikniesz „Uruchom kampanię".
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <a
                        href={`https://www.facebook.com/ads/manager/account_settings/account_billing/?act=${bareAdAccountId(adAccountId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block text-[11px] font-medium text-red-300 hover:text-red-200 underline underline-offset-2"
                      >
                        Otwórz ustawienia płatności w Meta ↗
                      </a>
                      <button
                        type="button"
                        disabled={isFetchingFunding}
                        onClick={async () => {
                          try {
                            await apiRequest(
                              "GET",
                              `/api/campaign-creator/ad-account-funding?adAccountId=${encodeURIComponent(adAccountId)}&refresh=1`
                            );
                          } catch {}
                          refetchAdAccountFunding();
                        }}
                        className="inline-block text-[11px] font-medium text-white/70 hover:text-white underline underline-offset-2 disabled:opacity-50"
                      >
                        {isFetchingFunding ? "Sprawdzam…" : "Sprawdź ponownie"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Cel kampanii dyktuje pod co Meta optymalizuje wyświetlanie reklamy. LEADS = pozyskiwanie kontaktów, SALES = zakupy, TRAFFIC = ruch na www, AWARENESS = świadomość marki. Wybór celu wpływa na późniejsze pola optymalizacji."
                  >
                    {t("section1.objectiveLabel")}
                    <span className="text-red-400 ml-0.5" title="Pole wymagane">
                      *
                    </span>
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <Select
                  value={objective}
                  onValueChange={(v) => {
                    setObjective(v);
                    if (v === "OUTCOME_LEADS") setGenerateCount(1);
                    const goals = OPTIMIZATION_GOALS[v];
                    if (goals?.length) setOptimizationGoal(goals[0].value);
                    // Reset conversion location if the new objective has locations.
                    // For Lead Generation default to Instant Forms (ON_AD), not the website.
                    const locs = CONVERSION_LOCATIONS[v];
                    if (locs?.length) {
                      const preferred =
                        v === "OUTCOME_LEADS"
                          ? (locs.find((l) => l.value === "ON_AD") ?? locs[0])
                          : locs[0];
                      setConversionLocation(preferred.value);
                    } else setConversionLocation("");
                  }}
                >
                  <SelectTrigger
                    className={SELECT_TRIGGER_CLS}
                    title="Wybierz cel kampanii. Pozycje oznaczone 'polecane' to najczęściej używane (najwięcej danych do nauki algorytmu Meta). Zmiana celu auto-resetuje pola optymalizacji niżej."
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT_CLS}>
                    {OBJECTIVES.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        className={`${SELECT_ITEM_CLS} py-2`}
                      >
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">{t(`objectives.${o.value}`)}</span>
                            {o.recommended && (
                              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-medium">
                                polecane
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-white/40 leading-tight">
                            {o.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Inline hint of currently selected objective's purpose */}
                {(() => {
                  const cur = OBJECTIVES.find((o) => o.value === objective);
                  return cur ? (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                      {cur.description}
                    </p>
                  ) : null;
                })()}
              </Field>

              <Field id="field-campaign-name">
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Nazwa kampanii widoczna w Ads Managerze (do 400 znaków). Konwencja: '{Brand} | {Cel} | {MM/RRRR}' — łatwiej filtrować i porównywać. Kliknij Auto by wstawić auto-uzupełniony szablon."
                  >
                    {t("section1.campaignNameLabel")}
                    <span className="text-red-400 ml-0.5" title="Pole wymagane">
                      *
                    </span>
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <div className="relative">
                  <Input
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder={t("section1.campaignNamePlaceholder")}
                    className={`${INPUT_CLS} pr-20`}
                  />
                  {!campaignName.trim() &&
                    (() => {
                      const objLabel = (OBJECTIVES.find((o) => o.value === objective)?.label || "")
                        .replace(/\s*\([^)]*\)\s*/g, "")
                        .trim();
                      const date = new Date()
                        .toLocaleDateString("pl-PL", { month: "2-digit", year: "numeric" })
                        .replace(".", "/");
                      const suggested = [brandName || "Brand", objLabel || "Kampania", date]
                        .filter(Boolean)
                        .join(" | ");
                      return (
                        <button
                          type="button"
                          onClick={() => setCampaignName(suggested)}
                          title={`Auto-uzupełnij: ${suggested}`}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-medium bg-red-500/15 text-red-300 hover:bg-red-500/25 hover:text-red-200 transition border border-red-500/20"
                        >
                          <Sparkles className="w-2.5 h-2.5 inline mr-1" />
                          Auto
                        </button>
                      );
                    })()}
                </div>
                {!campaignName.trim() && (
                  <p className="text-[10px] text-white/30 mt-1">
                    Tip: kliknij <span className="text-red-300/70">Auto</span> lub wybierz schemat u
                    góry, aby auto-wypełnić nazwę.
                  </p>
                )}
                {campaignName.length > 400 && (
                  <p className="text-[10px] text-red-400/90 mt-1 flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                      Za długa nazwa ({campaignName.length} znaków, max 400). Meta odrzuci kampanię.
                    </span>
                  </p>
                )}
                {campaignName.length > 100 && campaignName.length <= 400 && (
                  <p className="text-[10px] text-amber-300/70 mt-1">
                    {campaignName.length} znaków — długa nazwa, w Ads Managerze może być obcięta w
                    widoku listy.
                  </p>
                )}
              </Field>
            </div>

            {/* ABO / CBO toggle + CBO budget */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="ABO = ustawiasz budżet na każdy ad set osobno (więcej kontroli). CBO = Meta sama dzieli budżet kampanii (wymaga 3+ ad setów). Polecam ABO dla początkujących."
                  >
                    {t("section1.budgetTypeLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <div className="flex rounded-lg border border-white/[0.1] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setBudgetType("ABO")}
                    title="ABO (Ad Set Budget Optimization) — ustawiasz budżet osobno na każdy ad set. Lepsze gdy chcesz ręcznie kontrolować ile dziennie idzie na każdą audiencję. Dobry domyślny wybór."
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                      budgetType === "ABO"
                        ? "bg-red-500/20 text-white border-r border-white/[0.1]"
                        : "bg-[#030407] text-white/50 hover:text-white/70 border-r border-white/[0.1]"
                    }`}
                  >
                    ABO
                    <span className="block text-[10px] text-white/40 mt-0.5">
                      {t("section1.aboLabel")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBudgetType("CBO")}
                    title="CBO (Campaign Budget Optimization) — Meta sama rozdziela budżet między ad sety, dając więcej najlepszemu. Lepsze przy 3+ ad setach gdy ufasz algorytmowi."
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                      budgetType === "CBO"
                        ? "bg-red-500/20 text-white"
                        : "bg-[#030407] text-white/50 hover:text-white/70"
                    }`}
                  >
                    CBO
                    <span className="block text-[10px] text-white/40 mt-0.5">
                      {t("section1.cboLabel")}
                    </span>
                  </button>
                </div>
              </Field>

              {budgetType === "CBO" && (
                <Field>
                  <FieldLabel>
                    <span
                      className="inline-flex items-center gap-1.5"
                      title="Dzienny budżet dla całej kampanii. Meta sama rozdziela go między ad sety dając więcej najlepszemu. Faktyczny wydatek może być +/- 25% w skali tygodnia."
                    >
                      {t("section1.cboBudgetLabel", { currency: selectedAdAccountCurrency })}
                      <HelpCircle className="w-3 h-3 text-white/30" />
                    </span>
                  </FieldLabel>
                  <Input
                    type="number"
                    value={dailyBudget}
                    onChange={(e) => setDailyBudget(e.target.value)}
                    placeholder="50"
                    min="1"
                    className={INPUT_CLS}
                    title="Wpisz dzienny budżet kampanii (rozdzielany między ad sety przez algorytm Meta). Dla CBO: minimum ~50 PLN/dzień + dodatkowo ~15-20 PLN za każdy dodatkowy ad set."
                  />
                  <div className="flex items-center gap-1 mt-1.5">
                    <span className="text-[10px] text-white/30 mr-1">Szybko:</span>
                    {[50, 100, 200, 500].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setDailyBudget(String(v))}
                        title={`Ustaw ${v} ${selectedAdAccountCurrency}/dzień dla całej kampanii`}
                        className={`text-[10px] px-2 py-0.5 rounded border transition ${
                          dailyBudget === String(v)
                            ? "bg-red-500/20 border-red-500/40 text-red-200"
                            : "bg-white/[0.03] border-white/[0.08] text-white/40 hover:text-white/70 hover:border-white/20"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  {budgetWarning && (
                    <p
                      className={`text-[10px] mt-1 flex items-start gap-1 ${budgetWarning.kind === "error" ? "text-red-400/90" : "text-amber-300/80"}`}
                    >
                      <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                      <span>{budgetWarning.msg}</span>
                    </p>
                  )}
                </Field>
              )}
            </div>

            {/* CBO sanity: only meaningful with multiple ad sets */}
            {budgetType === "CBO" &&
              !abTestEnabled &&
              (!targetingVariants || targetingVariants.length <= 1) && (
                <p className="text-[10px] text-amber-300/80 flex items-start gap-1.5 -mt-1">
                  <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>
                    <span className="font-medium">CBO ma sens przy 3+ ad setach.</span> Tworzysz 1
                    ad set — Meta i tak wyda cały budżet na niego, więc ABO da ci więcej kontroli.
                    Rozważ zmianę na ABO lub włącz A/B Test (krok 8) / warianty audiencji.
                  </span>
                </p>
              )}

            {/* Special Ad Categories — optional, collapsible */}
            <div
              className={`border rounded-lg transition-colors ${specialAdCategories.length > 0 ? "border-amber-500/30 bg-amber-500/[0.04]" : "border-white/[0.08]"}`}
            >
              <button
                type="button"
                onClick={() => setSpecialAdCategoriesOpen(!specialAdCategoriesOpen)}
                title="Kategorie specjalne to wymóg Meta dla reklam dotyczących: kredytów, zatrudnienia, mieszkań, polityki, zdrowia/farmacji. Zostaw puste jeśli twoja reklama nie należy do żadnej z tych kategorii."
                className="w-full flex items-center justify-between px-4 py-2.5 text-left"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle
                    className={`w-3.5 h-3.5 ${specialAdCategories.length > 0 ? "text-amber-400" : "text-white/30"}`}
                  />
                  <span
                    className={`text-xs font-medium ${specialAdCategories.length > 0 ? "text-amber-300" : "text-white/40"}`}
                  >
                    {t("section1.specialCategoriesLabel")}
                    {specialAdCategories.length > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px]">
                        {specialAdCategories.length}
                      </span>
                    )}
                  </span>
                  <HelpCircle className="w-3 h-3 text-white/30" />
                  {specialAdCategories.length === 0 && (
                    <span className="text-[10px] text-white/30 ml-1">
                      — zostaw puste jeśli nie wiesz
                    </span>
                  )}
                </div>
                {specialAdCategoriesOpen ? (
                  <ChevronUp className="w-3.5 h-3.5 text-white/30" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-white/30" />
                )}
              </button>

              {specialAdCategoriesOpen && (
                <div className="px-4 pb-4 space-y-3 border-t border-white/[0.06]">
                  <p className="text-[11px] text-white/40 pt-3">
                    {t("section1.specialCategoriesInfo")}
                  </p>
                  <div className="space-y-2">
                    {SPECIAL_AD_CATEGORIES.map((cat) => {
                      const isSelected = specialAdCategories.includes(cat.value);
                      return (
                        <label
                          key={cat.value}
                          className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? "border-amber-500/30 bg-amber-500/[0.06]"
                              : "border-white/[0.06] hover:border-white/[0.12]"
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              const isChecked = checked === true;
                              setSpecialAdCategories((prev) =>
                                isChecked
                                  ? [...prev, cat.value]
                                  : prev.filter((c) => c !== cat.value)
                              );
                            }}
                            className="mt-0.5"
                          />
                          <div>
                            <span
                              className={`text-xs font-medium ${isSelected ? "text-amber-300" : "text-white/70"}`}
                            >
                              {t(`specialAdCategories.${cat.value}.label`)}
                            </span>
                            <p className="text-[10px] text-white/30 mt-0.5">
                              {t(`specialAdCategories.${cat.value}.description`)}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {specialAdCategories.length > 0 && (
                    <Field className="pt-2 border-t border-white/[0.06]">
                      <FieldLabel>
                        <span
                          className="inline-flex items-center gap-1.5"
                          title="Kraj/kraje w których obowiązują regulacje dla wybranych kategorii (np. dla 'Zatrudnienia' w UE wymagana jest deklaracja kraju). Domyślnie: Polska."
                        >
                          {t("section1.specialCategoriesCountryLabel")}
                          <HelpCircle className="w-3 h-3 text-white/30" />
                        </span>
                      </FieldLabel>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {SPECIAL_AD_CATEGORY_COUNTRIES.map((c) => {
                          const isSelected = specialAdCategoryCountry.includes(c.value);
                          return (
                            <button
                              key={c.value}
                              type="button"
                              onClick={() =>
                                setSpecialAdCategoryCountry((prev) =>
                                  isSelected
                                    ? prev.filter((v) => v !== c.value)
                                    : [...prev, c.value]
                                )
                              }
                              className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                                isSelected
                                  ? "border-amber-500/40 bg-amber-500/15 text-amber-300"
                                  : "border-white/[0.08] text-white/40 hover:border-white/[0.15] hover:text-white/60"
                              }`}
                            >
                              {t(`countries.${c.value}`)}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}

                  {hasTargetingRestrictions && (
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/20">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-[10px] text-amber-300/80">
                        {t("section1.specialCategoriesWarning")}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ================ SECTION 2: OPTYMALIZACJA & PIKSEL ================ */}
          <div id="section-2" className={SECTION_CLS}>
            <div className="flex items-center gap-2.5">
              <span
                className={`w-6 h-6 rounded-full border text-[11px] font-bold flex items-center justify-center flex-shrink-0 ${
                  sectionDone.s2
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-red-600/15 border-red-500/25 text-red-400"
                }`}
              >
                {sectionDone.s2 ? <Check className="w-3 h-3" /> : "2"}
              </span>
              <Target className="w-3.5 h-3.5 text-white/40" />
              <h3 className="text-sm font-semibold text-white tracking-wide flex items-center gap-1.5">
                {t("section2.title")}
                <span title="Cel optymalizacji ad seta: gdzie konwersja, jakie zdarzenie liczy się jako sukces, opcjonalnie Instant Form.">
                  <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                </span>
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
              {/* Conversion Location — for objectives that have it (OUTCOME_LEADS, OUTCOME_SALES) */}
              {CONVERSION_LOCATIONS[objective] ? (
                <Field>
                  <FieldLabel>
                    <span
                      className="inline-flex items-center gap-1.5"
                      title="Gdzie ma się dokonać konwersja: 'Strona' = na twojej stronie www (np. zapis przez formularz), 'W reklamie' = formularz Instant Form pojawia się bezpośrednio na Facebook/Instagram (szybsze, taniej za leada)."
                    >
                      {t("section2.conversionLocationLabel")}
                      <HelpCircle className="w-3 h-3 text-white/30" />
                    </span>
                  </FieldLabel>
                  <Select
                    value={conversionLocation}
                    onValueChange={(v) => {
                      setConversionLocation(v);
                      const loc = (CONVERSION_LOCATIONS[objective] || []).find(
                        (cl) => cl.value === v
                      );
                      if (loc) setOptimizationGoal(loc.optimizationGoal);
                    }}
                  >
                    <SelectTrigger className={SELECT_TRIGGER_CLS}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_CLS}>
                      {(CONVERSION_LOCATIONS[objective] || []).map((cl) => (
                        <SelectItem key={cl.value} value={cl.value} className={SELECT_ITEM_CLS}>
                          {t(`conversionLocations.${cl.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ) : (
                <Field>
                  <FieldLabel>
                    <span
                      className="inline-flex items-center gap-1.5"
                      title="Pod jaki cel Meta optymalizuje wyświetlanie reklamy. Np. 'Konwersje' = pokazuje ludziom najbardziej skłonnym do zakupu, 'Kliknięcia w link' = ludziom klikującym w linki. Dla większości celów Meta sama wybiera optymalny — nie zmieniaj jeśli nie wiesz."
                    >
                      {t("section2.optimizationGoalLabel")}
                      <HelpCircle className="w-3 h-3 text-white/30" />
                    </span>
                  </FieldLabel>
                  <Select value={optimizationGoal} onValueChange={setOptimizationGoal}>
                    <SelectTrigger className={SELECT_TRIGGER_CLS}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_CLS}>
                      {(OPTIMIZATION_GOALS[objective] || []).map((g) => (
                        <SelectItem key={g.value} value={g.value} className={SELECT_ITEM_CLS}>
                          {t(`optimizationGoals.${g.value}`, g.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Nazwa ad seta widoczna w Ads Managerze. Pomaga rozróżniać warianty audiencji w jednej kampanii. Możesz zostawić puste — auto: '<nazwa kampanii> - Ad Set'."
                  >
                    {t("section2.adSetNameLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <div className="relative">
                  <Input
                    value={adSetName}
                    onChange={(e) => setAdSetName(e.target.value)}
                    placeholder={`${campaignName || "Kampania"} - Ad Set`}
                    className={`${INPUT_CLS} ${campaignName && !adSetName.trim() ? "pr-20" : ""}`}
                  />
                  {!adSetName.trim() && campaignName && (
                    <button
                      type="button"
                      onClick={() => setAdSetName(`${campaignName} - Ad Set`)}
                      title={`Auto-uzupełnij: ${campaignName} - Ad Set`}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-medium bg-red-500/15 text-red-300 hover:bg-red-500/25 hover:text-red-200 transition border border-red-500/20"
                    >
                      <Sparkles className="w-2.5 h-2.5 inline mr-1" />
                      Auto
                    </button>
                  )}
                </div>
                {!adSetName.trim() && (
                  <p className="text-[10px] text-white/30 mt-0.5">
                    Puste? OK — użyjemy{" "}
                    <span className="text-white/50">
                      "{campaignName || "nazwa kampanii"} - Ad Set"
                    </span>
                    .
                  </p>
                )}
              </Field>
            </div>

            {/* Instant Form picker — when conversion location is ON_AD */}
            {isInstantForm && pageId && (
              <div className="border-t border-white/[0.06] pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Bookmark className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs font-semibold text-white/70 uppercase">
                      {t("section2.instantFormHeader")}
                    </span>
                    <span title="Instant Form to formularz wyświetlany bezpośrednio w Facebook/Instagram (bez wyjścia ze strony Meta). Wybierz istniejący formularz lub stwórz nowy. Zazwyczaj daje najtańsze leady — user nie musi gdzieś klikać.">
                      <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInstantFormCreatorOpen(true)}
                    title="Stwórz nowy Instant Form (formularz na Facebooku) bez opuszczania kreatora"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium bg-gradient-to-r from-cyan-600/20 to-blue-600/20 text-cyan-400 border border-cyan-500/30 hover:from-cyan-600/30 hover:to-blue-600/30 transition-all"
                  >
                    <Plus className="w-3 h-3" />
                    {t("section2.instantFormCreateBtn")}
                  </button>
                </div>
                <Select value={selectedLeadgenFormId} onValueChange={setSelectedLeadgenFormId}>
                  <SelectTrigger
                    className={SELECT_TRIGGER_CLS}
                    title="Wybierz Instant Form (formularz pojawiający się bezpośrednio w reklamie). Lista pochodzi ze Strony FB. Jeśli pusto — użyj 'Nowy formularz' obok."
                  >
                    <SelectValue
                      placeholder={
                        leadgenForms.length === 0
                          ? t("section2.instantFormLoading")
                          : t("section2.instantFormPlaceholder")
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT_CLS}>
                    {leadgenForms.map((f) => (
                      <SelectItem key={f.id} value={f.id} className={SELECT_ITEM_CLS}>
                        {f.name}
                        <span className="text-white/30 ml-2 text-[10px]">
                          {f.status === "ACTIVE" ? t("section2.instantFormActiveBadge") : f.status}
                          {f.leadCount !== undefined
                            ? ` · ${f.leadCount} ${t("section2.instantFormLeads", { count: f.leadCount }).split(" ").slice(1).join(" ")}`
                            : ""}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {leadgenForms.length === 0 && pageId && (
                  <p className="text-[11px] text-amber-300/70 mt-2 flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                      {t("section2.instantFormEmpty")} Kliknij{" "}
                      <span className="text-cyan-300">"Nowy formularz"</span> wyżej by go stworzyć w
                      30 sekund.
                    </span>
                  </p>
                )}
                <InstantFormCreator
                  open={instantFormCreatorOpen}
                  onOpenChange={setInstantFormCreatorOpen}
                  pageId={pageId}
                  pageName={pages.find((p) => p.id === pageId)?.name}
                  onFormCreated={(form) => setSelectedLeadgenFormId(form.id)}
                  clientId={selectedClientId || undefined}
                  trackingParameters={
                    utmEnabled
                      ? Object.fromEntries(
                          Object.entries({
                            utm_source: utmSource,
                            utm_medium: utmMedium,
                            utm_campaign: utmCampaign,
                            utm_term: utmTerm,
                            utm_content: utmContent,
                          }).filter(([, v]) => v)
                        )
                      : undefined
                  }
                />
              </div>
            )}

            {/* Instant Form selected but no FB page yet — point the user to where it lives */}
            {isInstantForm && !pageId && (
              <div className="border-t border-white/[0.06] pt-4 mt-2">
                <div className="flex items-center space-x-2 mb-2">
                  <Bookmark className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-semibold text-white/70 uppercase">
                    {t("section2.instantFormHeader")}
                  </span>
                </div>
                <p className="text-[11px] text-amber-300/80 flex items-start gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Najpierw wybierz <span className="text-white/80">Stronę Facebook</span> w sekcji
                    „Kreacje reklamowe" niżej — wtedy pojawi się tu wybór / tworzenie formularza
                    błyskawicznego.
                  </span>
                </p>
              </div>
            )}

            {/* ABO: budget at ad set level */}
            {budgetType === "ABO" && (
              <Field className="max-w-xs">
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Dzienny budżet dla tego ad seta. Meta wyda max tę kwotę na dzień. Faktyczny wydatek może być +/- 25% w skali tygodnia."
                  >
                    {t("section2.aboBudgetLabel", { currency: selectedAdAccountCurrency })}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <Input
                  type="number"
                  value={dailyBudget}
                  onChange={(e) => setDailyBudget(e.target.value)}
                  placeholder="50"
                  min="1"
                  className={INPUT_CLS}
                  title="Wpisz kwotę dziennego budżetu w walucie konta. Minimum dla optymalizacji konwersji: ~30-50 PLN/dzień (Meta potrzebuje 50 konwersji tygodniowo na ad set by się nauczyć)."
                />
                <div className="flex items-center gap-1 mt-1.5">
                  <span className="text-[10px] text-white/30 mr-1">Szybko:</span>
                  {[20, 50, 100, 200].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDailyBudget(String(v))}
                      title={`Ustaw ${v} ${selectedAdAccountCurrency}/dzień`}
                      className={`text-[10px] px-2 py-0.5 rounded border transition ${
                        dailyBudget === String(v)
                          ? "bg-red-500/20 border-red-500/40 text-red-200"
                          : "bg-white/[0.03] border-white/[0.08] text-white/40 hover:text-white/70 hover:border-white/20"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                {budgetWarning && (
                  <p
                    className={`text-[10px] mt-1 flex items-start gap-1 ${budgetWarning.kind === "error" ? "text-red-400/90" : "text-amber-300/80"}`}
                  >
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>{budgetWarning.msg}</span>
                  </p>
                )}
              </Field>
            )}

            {/* Pixel & Conversion event — only for leads/sales with conversion optimization */}
            {needsPixelConfig && (
              <div className="border-t border-white/[0.06] pt-4 mt-2">
                <div className="flex items-center space-x-2 mb-3">
                  <Crosshair className="w-4 h-4 text-red-400" />
                  <span className="text-xs font-semibold text-white/70 uppercase">
                    {t("section2.pixelHeader")}
                  </span>
                  <span title="Pixel + Zdarzenie konwersji są wymagane gdy chcesz, by Meta optymalizowała pod konwersje na stronie. Pixel zbiera dane o tym kto konwertuje, dzięki czemu Meta szuka podobnych ludzi.">
                    <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
                  <Field id="field-pixel">
                    <FieldLabel>
                      <span
                        className="inline-flex items-center gap-1.5"
                        title="Meta Pixel = kawałek kodu na Twojej stronie www, który raportuje 'kto kupił/zostawił leada'. Bez piksela Meta nie wie kogo szukać i optymalizacja konwersji nie działa."
                      >
                        {t("section2.pixelLabel")}
                        <span className="text-red-400 ml-0.5" title="Pole wymagane dla Lead/Sales">
                          *
                        </span>
                        <HelpCircle className="w-3 h-3 text-white/30" />
                      </span>
                    </FieldLabel>
                    <Select value={selectedPixelId} onValueChange={setSelectedPixelId}>
                      <SelectTrigger
                        className={SELECT_TRIGGER_CLS}
                        title="Wybierz Pixel z konta. Każde konto może mieć kilka pikseli (np. osobne dla różnych domen). Jeśli nie wiesz który — pytaj osoby która konfigurowała tracking."
                      >
                        <SelectValue
                          placeholder={
                            pixels.length === 0
                              ? t("section2.pixelLoading")
                              : t("section2.pixelPlaceholder")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_CLS}>
                        {pixels.map((p) => (
                          <SelectItem key={p.id} value={p.id} className={SELECT_ITEM_CLS}>
                            {p.name} <span className="text-white/30 ml-1">({p.id})</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!adAccountId && (
                      <p className="text-[10px] text-white/40 mt-1 flex items-start gap-1">
                        <HelpCircle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>
                          Najpierw wybierz konto reklamowe — piksele załadują się automatycznie.
                        </span>
                      </p>
                    )}
                    {adAccountId &&
                      !pixelsLoading &&
                      pixels.length === 0 &&
                      !pixelInstallAcknowledged && (
                        <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
                            <div className="flex-1 space-y-1">
                              <div className="text-xs font-semibold text-amber-200">
                                Brak Pixela na koncie reklamowym
                              </div>
                              <p className="text-[11px] text-white/70 leading-snug">
                                Twoja kampania{" "}
                                <span className="text-white/90">e-commerce / sales</span> wymaga
                                Meta Pixela na stronie — bez niego Meta nie wie kto kupił i nie
                                potrafi optymalizować pod sprzedaż. Odpalenie kampanii bez Pixela =
                                palenie budżetu bez pomiaru.
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pl-6">
                            <button
                              type="button"
                              onClick={() => setPixelGuideOpen(true)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/40"
                            >
                              Jak zainstalować Pixel →
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                toast({
                                  title: "OK — wróć tu jak Pixel zacznie strzelać",
                                  description:
                                    "Świeżo zainstalowany Pixel pojawia się w Meta po max 24h od pierwszego eventu. Możesz w międzyczasie ustawić budżet i kreatywy — odpal kampanię gdy Pixel będzie aktywny.",
                                });
                                setPixelInstallAcknowledged(true);
                              }}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/[0.12]"
                            >
                              Mam Pixel ale jeszcze nieaktywny
                            </button>
                            <a
                              href="https://business.facebook.com/events_manager"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/[0.04] hover:bg-white/[0.08] text-white/60 border border-white/[0.08]"
                            >
                              Events Manager ↗
                            </a>
                          </div>
                        </div>
                      )}
                    {adAccountId && pixelsLoading && (
                      <p className="text-[10px] text-white/40 mt-1 flex items-start gap-1">
                        <Loader2 className="w-3 h-3 shrink-0 mt-0.5 animate-spin" />
                        <span>Ładuję piksele...</span>
                      </p>
                    )}
                  </Field>

                  <Field>
                    <FieldLabel>
                      <span
                        className="inline-flex items-center gap-1.5"
                        title="Co Meta ma uznać za 'konwersję' (lead, zakup, rejestracja). Najczęściej: 'Lead' dla lejków leadowych, 'Purchase' dla e-commerce. 'Inne' = wybierz Custom Conversion z Events Managera."
                      >
                        {t("section2.conversionEventLabel")}
                        <HelpCircle className="w-3 h-3 text-white/30" />
                      </span>
                    </FieldLabel>
                    <Select
                      value={selectedConversionEvent}
                      onValueChange={setSelectedConversionEvent}
                    >
                      <SelectTrigger
                        className={SELECT_TRIGGER_CLS}
                        title="Wybierz event który reklama ma optymalizować. Dla lead-genów zazwyczaj 'Lead'. Dla e-commerce 'Purchase'. 'Inne' = wybierz Custom Conversion."
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_CLS}>
                        {STANDARD_EVENTS.map((e) => (
                          <SelectItem key={e.value} value={e.value} className={SELECT_ITEM_CLS}>
                            {e.value === "OTHER" ? t("standardEvents.OTHER") : e.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                {/* Tracking sanity check — red warning when chosen event has 0 fires in last 30d */}
                {selectedPixelId &&
                  selectedConversionEvent &&
                  selectedConversionEvent !== "OTHER" &&
                  pixelEventStats?.count === 0 && (
                    <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/[0.08] p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
                        <div className="flex-1 space-y-1">
                          <div className="text-xs font-semibold text-red-200">
                            Ten Pixel nie zaraportował ani jednego zdarzenia „
                            {selectedConversionEvent}" w ciągu ostatnich 30 dni.
                          </div>
                          <p className="text-[11px] text-white/70 leading-snug">
                            Meta nie ma żadnych danych do optymalizacji pod ten event — odpalenie
                            kampanii to{" "}
                            <span className="text-red-300">palenie budżetu bez sygnału</span>.
                            Sprawdź konfigurację Pixela w Events Managerze albo wybierz event który
                            faktycznie strzela.
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pl-6">
                        <button
                          type="button"
                          onClick={() => setPixelSnippetsOpen((v) => !v)}
                          className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-red-500/15 hover:bg-red-500/25 text-red-700 dark:text-red-100 border border-red-500/30 inline-flex items-center gap-1.5"
                        >
                          <Code className="w-3 h-3" />
                          {pixelSnippetsOpen
                            ? "Ukryj kod do wklejenia"
                            : "Pokaż kod do wklejenia na stronie"}
                        </button>
                        <a
                          href="https://business.facebook.com/events_manager"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/[0.04] hover:bg-white/[0.08] text-white/70 border border-white/[0.12]"
                        >
                          Events Manager ↗
                        </a>
                      </div>
                      {pixelSnippetsOpen &&
                        (() => {
                          const baseSnippet = `<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(arguments)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',\n'https://connect.facebook.net/en_US/fbevents.js');\nfbq('init', '${selectedPixelId}');\nfbq('track', 'PageView');\n</script>\n<noscript><img height="1" width="1" style="display:none"\nsrc="https://www.facebook.com/tr?id=${selectedPixelId}&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->`;
                          const pixelEventName =
                            STANDARD_EVENTS.find((e) => e.value === selectedConversionEvent)
                              ?.label ?? selectedConversionEvent;
                          const isStandardPixelEvent =
                            pixelEventName !== selectedConversionEvent &&
                            selectedConversionEvent !== "OTHER";
                          const trackVerb = isStandardPixelEvent ? "track" : "trackCustom";
                          const eventSnippet = `<!-- Wywołaj w momencie konwersji (np. po wysłaniu formularza, na thank-you page) -->\n<script>\n  fbq('${trackVerb}', '${pixelEventName}');\n</script>`;
                          const copy = async (which: "base" | "event", text: string) => {
                            try {
                              await navigator.clipboard.writeText(text);
                              setCopiedSnippet(which);
                              setTimeout(() => setCopiedSnippet(null), 2000);
                            } catch {
                              toast({
                                title: "Nie udało się skopiować",
                                description: "Skopiuj ręcznie z pola.",
                                variant: "destructive",
                              });
                            }
                          };
                          return (
                            <div className="ml-6 space-y-3 pt-1">
                              <div>
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="text-[11px] font-semibold text-white/80">
                                    1. Base Pixel + PageView{" "}
                                    <span className="text-white/40 font-normal">
                                      — wklej w &lt;head&gt; każdej podstrony lejka
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => copy("base", baseSnippet)}
                                    className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/[0.12] inline-flex items-center gap-1"
                                  >
                                    <Copy className="w-2.5 h-2.5" />
                                    {copiedSnippet === "base" ? "Skopiowano" : "Kopiuj"}
                                  </button>
                                </div>
                                <pre className="text-[10px] leading-relaxed bg-black/40 border border-white/[0.06] rounded p-2 overflow-x-auto text-white/80 font-mono whitespace-pre">
                                  {baseSnippet}
                                </pre>
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="text-[11px] font-semibold text-white/80">
                                    2. Event „{pixelEventName}"{" "}
                                    <span className="text-white/40 font-normal">
                                      — wywołaj w momencie konwersji (thank-you page / button click)
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => copy("event", eventSnippet)}
                                    className="px-2 py-0.5 rounded text-[10px] font-medium bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/[0.12] inline-flex items-center gap-1"
                                  >
                                    <Copy className="w-2.5 h-2.5" />
                                    {copiedSnippet === "event" ? "Skopiowano" : "Kopiuj"}
                                  </button>
                                </div>
                                <pre className="text-[10px] leading-relaxed bg-black/40 border border-white/[0.06] rounded p-2 overflow-x-auto text-white/80 font-mono whitespace-pre">
                                  {eventSnippet}
                                </pre>
                              </div>
                              <p className="text-[10px] text-white/40 italic">
                                Po wklejeniu i odpaleniu strony pierwsze eventy pojawią się w Events
                                Managerze w ciągu 5–20 minut. Cache na tej walidacji to 5 min —
                                odśwież stronę za chwilę żeby warning zniknął.
                              </p>
                            </div>
                          );
                        })()}
                    </div>
                  )}

                {/* Custom conversion picker */}
                {selectedConversionEvent === "OTHER" && (
                  <Field id="field-conversion-event" className="mt-3">
                    <FieldLabel>
                      <span
                        className="inline-flex items-center gap-1.5"
                        title="Custom Conversion = własna definicja konwersji w Events Managerze (np. tylko zakupy > 100 zł, tylko leady z konkretnego URL). Używaj gdy standardowe Lead/Purchase są za szerokie."
                      >
                        {t("section2.customConversionLabel")}
                        <span className="text-red-400 ml-0.5" title="Wymagane gdy wybrano 'Inne'">
                          *
                        </span>
                        <HelpCircle className="w-3 h-3 text-white/30" />
                      </span>
                    </FieldLabel>
                    <Select
                      value={selectedCustomConversionId}
                      onValueChange={setSelectedCustomConversionId}
                    >
                      <SelectTrigger className={SELECT_TRIGGER_CLS}>
                        <SelectValue
                          placeholder={
                            customConversions.length === 0
                              ? t("section2.customConversionEmpty")
                              : t("section2.customConversionPlaceholder")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className={SELECT_CONTENT_CLS}>
                        {customConversions.map((cc) => (
                          <SelectItem key={cc.id} value={cc.id} className={SELECT_ITEM_CLS}>
                            {cc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {customConversions.length === 0 && adAccountId && (
                      <p className="text-[10px] text-amber-300/80 mt-1 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>
                          To konto nie ma żadnych Custom Conversions. Stwórz je w{" "}
                          <a
                            href="https://business.facebook.com/events_manager"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-amber-200"
                          >
                            Events Managerze
                          </a>{" "}
                          albo wybierz standardowe zdarzenie wyżej.
                        </span>
                      </p>
                    )}
                  </Field>
                )}
              </div>
            )}
          </div>

          {/* ================ SECTION 3: TARGETING ================ */}
          <div id="section-3" className={SECTION_CLS}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-red-600/15 border border-red-500/25 text-red-400 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  3
                </span>
                <Users className="w-3.5 h-3.5 text-white/40" />
                <h3 className="text-sm font-semibold text-white tracking-wide flex items-center gap-1.5">
                  {t("section3.title")}
                  <span title="Komu pokazujemy reklamę: lokalizacje, wiek, płeć, języki, zainteresowania, custom audiences.">
                    <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                  </span>
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {/* Variant count selector: 1 / 3 / 5 */}
                <div
                  className="flex items-center gap-0.5 rounded-lg bg-white/[0.04] border border-white/[0.08] p-0.5"
                  title="Ile wariantów audiencji AI ma zaproponować. 1 = jeden klikalny zestaw, 3-5 = porównaj kilka audiencji w kartach poniżej (możesz wybrać jeden lub odpalić wszystkie naraz checkboxem 'launch all')."
                >
                  {([1, 3, 5] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setVariantCount(n)}
                      title={
                        n === 1
                          ? "1 audiencja — jedna propozycja AI"
                          : `${n} audiencje — kilka wariantów do porównania, możesz launchować jeden lub wszystkie`
                      }
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                        variantCount === n
                          ? "bg-red-600/30 text-red-200 border border-red-500/40"
                          : "text-white/50 hover:text-white/80"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={suggestTargetingWithAI}
                  disabled={aiTargetingLoading}
                  title="AI przeanalizuje twoją stronę docelową i zaproponuje audiencję — zainteresowania, demografię, lookalike. Najlepiej działa gdy URL jest już wpisany."
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-red-600/30 to-orange-600/30 border border-red-500/30 text-red-300 hover:from-red-600/40 hover:to-orange-600/40 hover:border-red-500/50 transition-all disabled:opacity-50"
                >
                  {aiTargetingLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  {aiTargetingLoading
                    ? t("section3.aiSuggestLoading")
                    : variantCount > 1
                      ? t("section3.aiSuggestWithCount", { count: variantCount })
                      : t("section3.aiSuggestBtn")}
                </button>
              </div>
            </div>

            {/* AI targeting reasoning (single-variant mode or applied variant) */}
            {aiTargetingReasoning && !targetingVariants && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowTargetingReasoning(!showTargetingReasoning)}
                  title="Zobacz uzasadnienie AI — dlaczego zaproponowała tę audiencję na podstawie analizy strony lub najlepszych reklam."
                  className="flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/60 transition-colors"
                >
                  <Brain className="w-3 h-3" />
                  <span>{t("section3.aiRationaleBtn")}</span>
                  {showTargetingReasoning ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
                {showTargetingReasoning && (
                  <p className="mt-1.5 text-[11px] text-white/40 leading-relaxed bg-white/[0.02] rounded-lg px-3 py-2 border border-white/[0.04]">
                    {aiTargetingReasoning}
                  </p>
                )}
              </div>
            )}

            {/* Multi-variant cards */}
            {targetingVariants && targetingVariants.length > 1 && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">
                    {t("section3.variantsCount", { count: targetingVariants.length })}
                  </span>
                  <span className="text-[10px] text-white/40">{t("section3.variantsHint")}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {targetingVariants.map((v, idx) => {
                    const isSelected = idx === selectedVariantIdx;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setSelectedVariantIdx(idx);
                          applyVariantToFields(v);
                        }}
                        title={
                          isSelected
                            ? "Aktywny wariant — pola targetingu są ustawione na tę audiencję"
                            : "Kliknij by zastosować ten wariant audiencji do pól targetingu"
                        }
                        className={`text-left rounded-lg p-2.5 border transition-all ${
                          isSelected
                            ? "bg-red-600/15 border-red-500/50 ring-1 ring-red-500/30"
                            : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.12]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-[12px] font-semibold text-white truncate">
                            {v.label}
                          </span>
                          {isSelected && (
                            <Check className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                          )}
                        </div>
                        <div className="mt-1 text-[10px] text-white/50 space-y-0.5">
                          <div>
                            {v.countries.join(", ")} · {v.ageMin}-{v.ageMax} ·{" "}
                            {v.genders === "1" ? "M" : v.genders === "2" ? "K" : "M+K"}
                          </div>
                          {v.interests.length > 0 && (
                            <div className="truncate">
                              {v.interests
                                .slice(0, 3)
                                .map((i) => i.name)
                                .join(", ")}
                              {v.interests.length > 3 ? ` +${v.interests.length - 3}` : ""}
                            </div>
                          )}
                        </div>
                        {v.reasoning && (
                          <p
                            className="mt-1.5 text-[10px] text-white/40 line-clamp-2 leading-relaxed"
                            title={`Pełne uzasadnienie AI: ${v.reasoning}`}
                          >
                            {v.reasoning}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Launch-all toggle */}
                <label
                  className="flex items-start gap-2 mt-2 p-2.5 rounded-lg bg-purple-600/10 border border-purple-500/20 cursor-pointer hover:bg-purple-600/15 transition-colors"
                  title="Zamiast 1 wariantu audiencji, stwórz N osobnych ad setów (każdy z innym wariantem). Każdy ad set z własnym budżetem. Klasyczny A/B test audiencji — drożej, ale szybciej znajdziesz najlepszą."
                >
                  <input
                    type="checkbox"
                    checked={launchAllVariants}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setLaunchAllVariants(next);
                      if (next) setSplitAdsEnabled(false);
                    }}
                    className="mt-0.5 accent-purple-500"
                  />
                  <div className="flex-1">
                    <div className="text-[12px] font-medium text-white">
                      {t("section3.allVariantsLabel", { count: targetingVariants.length })}
                    </div>
                    <div className="text-[10px] text-white/50 mt-0.5">
                      {t("section3.allVariantsDesc", {
                        budgetSuffix: dailyBudget
                          ? ` (łącznie ${Number(dailyBudget) * targetingVariants.length} / dzień)`
                          : "",
                      })}
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="space-y-3">
              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Kraje, regiony, miasta gdzie pokażesz reklamy. Im węższa lokalizacja, tym wyższy CPM. Domyślnie: Polska."
                  >
                    {t("section3.locationsLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <GeoTargetingWidget
                  value={geoTargeting}
                  onChange={setGeoTargeting}
                  adAccountId={adAccountId}
                />
              </Field>

              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Język interfejsu odbiorcy (np. wybierz Polski jeśli reklama jest po polsku — Meta pokaże ją tylko ludziom z polskim FB/IG). Zostaw puste = wszystkie języki."
                  >
                    {t("section3.languageLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <div className="relative" ref={localeDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setLocaleDropdownOpen(!localeDropdownOpen);
                      setLocaleSearch("");
                    }}
                    title={
                      selectedLocales.length === 0
                        ? "Kliknij aby wybrać języki targetowania (puste = wszystkie)"
                        : `${selectedLocales.length} wybranych — kliknij aby zmienić`
                    }
                    className={`${INPUT_CLS} w-full flex items-center justify-between px-3 rounded-md border cursor-pointer min-h-[36px]`}
                  >
                    <span className="truncate text-left flex-1">
                      {selectedLocales.length === 0 ? (
                        <span className="text-white/30">{t("section3.languagePlaceholder")}</span>
                      ) : (
                        LOCALE_OPTIONS.filter((l) => selectedLocales.includes(l.key))
                          .map((l) => l.label)
                          .join(", ")
                      )}
                    </span>
                    <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0 ml-2" />
                  </button>

                  {localeDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/[0.08] bg-[#0a0c10] shadow-xl max-h-64 flex flex-col">
                      <div className="p-2 border-b border-white/[0.06]">
                        <Input
                          value={localeSearch}
                          onChange={(e) => setLocaleSearch(e.target.value)}
                          placeholder={t("section3.languageSearch")}
                          title="Wpisz nazwę języka (np. 'polski', 'angielski')"
                          className={`${INPUT_CLS} h-8`}
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {LOCALE_OPTIONS.filter(
                          (l) =>
                            !localeSearch ||
                            l.label.toLowerCase().includes(localeSearch.toLowerCase())
                        ).map((l) => {
                          const isSelected = selectedLocales.includes(l.key);
                          return (
                            <button
                              key={l.key}
                              type="button"
                              onClick={() =>
                                setSelectedLocales((prev) =>
                                  isSelected ? prev.filter((x) => x !== l.key) : [...prev, l.key]
                                )
                              }
                              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-white/[0.06] ${isSelected ? "text-blue-400" : "text-white/70"}`}
                            >
                              <span>{l.label}</span>
                              {isSelected && <Check className="w-4 h-4" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {selectedLocales.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedLocales.map((key) => {
                      const locale = LOCALE_OPTIONS.find((l) => l.key === key);
                      return (
                        <span
                          key={key}
                          className="inline-flex items-center bg-blue-500/15 border border-blue-500/30 rounded-full px-2.5 py-0.5 text-[11px] text-blue-300"
                        >
                          {locale?.label ?? key}
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedLocales((prev) => prev.filter((l) => l !== key))
                            }
                            title="Usuń ten język z targetowania"
                            className="ml-1 hover:text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </Field>
            </div>

            {hasTargetingRestrictions && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/20">
                <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-amber-300/80">{t("section3.specialCategoryLock")}</p>
              </div>
            )}

            <div
              className={`grid grid-cols-1 md:grid-cols-3 gap-balanced ${hasTargetingRestrictions ? "opacity-40 pointer-events-none" : ""}`}
            >
              <Field>
                <FieldLabel title="Minimalny wiek odbiorcy. Meta nie pozwala targetować poniżej 13.">
                  {t("section3.ageFromLabel")}
                </FieldLabel>
                <Input
                  type="number"
                  value={hasTargetingRestrictions ? "13" : ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                  min="13"
                  max="65"
                  className={INPUT_CLS}
                />
              </Field>
              <Field>
                <FieldLabel title="Maksymalny wiek. 65+ oznacza 65 i więcej. Im węższy zakres, tym wyższy CPM.">
                  {t("section3.ageToLabel")}
                </FieldLabel>
                <Input
                  type="number"
                  value={hasTargetingRestrictions ? "65" : ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                  min="13"
                  max="65"
                  className={INPUT_CLS}
                />
              </Field>
              <Field>
                <FieldLabel title="Płeć odbiorców. 'Wszyscy' = najtańsze CPM. Wybieraj konkretną płeć tylko gdy oferta jest jednoznacznie męska/damska — zawężenie podnosi koszty.">
                  {t("section3.genderLabel")}
                </FieldLabel>
                <Select value={hasTargetingRestrictions ? "0" : genders} onValueChange={setGenders}>
                  <SelectTrigger className={SELECT_TRIGGER_CLS}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT_CLS}>
                    <SelectItem value="0" className={SELECT_ITEM_CLS}>
                      {t("section3.genderAll")}
                    </SelectItem>
                    <SelectItem value="1" className={SELECT_ITEM_CLS}>
                      {t("section3.genderMale")}
                    </SelectItem>
                    <SelectItem value="2" className={SELECT_ITEM_CLS}>
                      {t("section3.genderFemale")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* Detailed Targeting – Interests */}
            <div
              className={`border-t border-white/[0.06] pt-4 ${hasTargetingRestrictions ? "opacity-40 pointer-events-none" : ""}`}
            >
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-orange-400" />
                <span className="text-xs font-semibold text-white/70 uppercase">
                  {t("section3.interestsHeader")}
                </span>
                <span title="Zainteresowania to behawioralne profile użytkowników Meta (kupowanie online, fitness, motoryzacja...). Dodaj 3-7 powiązanych z twoją ofertą. Używanie zbyt wielu rozmywa targetowanie.">
                  <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                </span>
                {hasTargetingRestrictions && (
                  <span className="text-[10px] text-amber-400 ml-2">
                    {t("section3.interestsLocked")}
                  </span>
                )}
              </div>

              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-white/30 pointer-events-none" />
                <Input
                  value={interestSearch}
                  onChange={(e) => setInterestSearch(e.target.value)}
                  placeholder={t("section3.interestsSearch")}
                  title="Wpisz zainteresowanie (np. 'fitness', 'kawa', 'gotowanie'). Meta podpowie pasujące — wybierz 3-7. Dla najszerszego zasięgu możesz nie dodawać żadnego (broad audience)."
                  className={`${INPUT_CLS} pl-8`}
                />
                {searchingInterests && (
                  <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 text-white/30 animate-spin" />
                )}
              </div>

              {/* Interest search results */}
              {interestResults.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#0a0c10]">
                  {interestResults.map((interest) => (
                    <button
                      key={interest.id}
                      type="button"
                      onClick={() => addInterest(interest)}
                      className="w-full text-left px-3 py-2 text-sm text-white/70 hover:bg-white/[0.06] flex items-center justify-between"
                    >
                      <span>{interest.name}</span>
                      <span className="text-[10px] text-white/30">
                        {interest.audienceSize
                          ? `~${(interest.audienceSize / 1_000_000).toFixed(1)}M`
                          : ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected interests */}
              {selectedInterests.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedInterests.map((interest) => (
                    <span
                      key={interest.id}
                      className="inline-flex items-center bg-orange-500/15 border border-orange-500/30 rounded-full px-3 py-1 text-xs text-orange-300"
                    >
                      {interest.name}
                      <button
                        type="button"
                        onClick={() => removeInterest(interest.id)}
                        title="Usuń to zainteresowanie z targetowania"
                        className="ml-1.5 hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Audiences */}
            <div className="border-t border-white/[0.06] pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-semibold text-white/70 uppercase">
                  {t("section3.customAudiencesHeader")}
                </span>
                <span title="Niestandardowe audiencje (custom audiences) z konta reklamowego — np. lookalike z bazy klientów, retargeting odwiedzających, listy email. Dodaj do 'Włącz' aby targetować, do 'Wyklucz' aby pominąć (np. dotychczasowych klientów).">
                  <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
                {/* Include */}
                <Field>
                  <FieldLabel
                    className="flex items-center gap-1"
                    title="Audiencje, które MAJĄ zobaczyć reklamę (np. lookalike z klientów, retargeting odwiedzających www, lista email VIP)."
                  >
                    <Plus className="w-3 h-3 text-green-400" />
                    {t("section3.includeAudiencesLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </FieldLabel>
                  <div className="relative mt-1">
                    <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
                    <Input
                      value={audienceIncludeSearch}
                      onChange={(e) => setAudienceIncludeSearch(e.target.value)}
                      placeholder={t("section3.includeAudiencesSearch")}
                      title="Wpisz nazwę audiencji by ją odszukać. Lista pochodzi z konta reklamowego (custom audiences + lookalike z Audience Managera)."
                      className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 pl-7 mb-1"
                    />
                  </div>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#030407]">
                    {availableAudiencesForInclude.length === 0 && (
                      <p className="text-[10px] text-white/30 p-2">
                        {allCustomAudiences.length === 0
                          ? t("section3.noCustomAudiences")
                          : t("section3.noResults")}
                      </p>
                    )}
                    {availableAudiencesForInclude.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleAudience(a, "include")}
                        className="w-full text-left px-2 py-1.5 text-xs text-white/60 hover:bg-white/[0.06] flex items-center justify-between"
                      >
                        <span className="truncate">{a.name}</span>
                        {a.approximateCount && (
                          <span className="text-[9px] text-white/30 ml-1 flex-shrink-0">
                            {formatNumber(a.approximateCount)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  {includedAudiences.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {includedAudiences.map((a) => (
                        <span
                          key={a.id}
                          className="inline-flex items-center bg-green-500/15 border border-green-500/30 rounded-full px-2 py-0.5 text-[10px] text-green-300"
                        >
                          {a.name}
                          <button
                            type="button"
                            onClick={() => toggleAudience(a, "include")}
                            title="Usuń tę audiencję z 'Włącz'"
                            className="ml-1"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </Field>

                {/* Exclude */}
                <Field>
                  <FieldLabel
                    className="flex items-center gap-1"
                    title="Audiencje, które NIE MAJĄ zobaczyć reklamy (np. dotychczasowi klienci, osoby już skonwertowane, pracownicy). Oszczędza budżet na osobach które już kupiły."
                  >
                    <Minus className="w-3 h-3 text-red-400" />
                    {t("section3.excludeAudiencesLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </FieldLabel>
                  <div className="relative mt-1">
                    <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
                    <Input
                      value={audienceExcludeSearch}
                      onChange={(e) => setAudienceExcludeSearch(e.target.value)}
                      placeholder={t("section3.excludeAudiencesSearch")}
                      title="Wpisz nazwę audiencji by ją odszukać i wykluczyć z targetowania. Najczęściej: dotychczasowi klienci, lista email kupujących, pracownicy."
                      className="h-8 text-xs bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 pl-7 mb-1"
                    />
                  </div>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#030407]">
                    {availableAudiencesForExclude.length === 0 && (
                      <p className="text-[10px] text-white/30 p-2">
                        {allCustomAudiences.length === 0
                          ? t("section3.noCustomAudiencesExclude")
                          : t("section3.noResults")}
                      </p>
                    )}
                    {availableAudiencesForExclude.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleAudience(a, "exclude")}
                        className="w-full text-left px-2 py-1.5 text-xs text-white/60 hover:bg-white/[0.06] flex items-center justify-between"
                      >
                        <span className="truncate">{a.name}</span>
                        {a.approximateCount && (
                          <span className="text-[9px] text-white/30 ml-1 flex-shrink-0">
                            {formatNumber(a.approximateCount)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  {excludedAudiences.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {excludedAudiences.map((a) => (
                        <span
                          key={a.id}
                          className="inline-flex items-center bg-red-500/15 border border-red-500/30 rounded-full px-2 py-0.5 text-[10px] text-red-300"
                        >
                          {a.name}
                          <button
                            type="button"
                            onClick={() => toggleAudience(a, "exclude")}
                            title="Usuń tę audiencję z 'Wyklucz'"
                            className="ml-1"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </Field>
              </div>
            </div>
          </div>

          {/* ================ SECTION 4: UMIEJSCOWIENIA ================ */}
          <div id="section-4" className={SECTION_CLS}>
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-red-600/15 border border-red-500/25 text-red-400 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                4
              </span>
              <Maximize2 className="w-3.5 h-3.5 text-white/40" />
              <h3 className="text-sm font-semibold text-white tracking-wide flex items-center gap-1.5">
                {t("section4.title")}
                <span title="Gdzie pokażemy reklamę: Facebook Feed/Stories/Reels, Instagram Feed/Stories/Reels, Audience Network, Messenger. Domyślnie: wszędzie (Auto).">
                  <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                </span>
              </h3>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setManualPlacements(false)}
                  title="Meta sama wybiera gdzie pokazać reklamę (Feed, Stories, Reels, Audience Network). Najlepsze dla początkujących."
                  className={`px-4 py-2 text-sm font-medium transition-colors ${!manualPlacements ? "bg-red-500/20 text-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                >
                  {t("section4.autoBtn")}
                </button>
                <button
                  type="button"
                  onClick={() => setManualPlacements(true)}
                  title="Sam wybierasz konkretne umiejscowienia (np. tylko Reels + Stories). Ogranicza zasięg ale daje kontrolę. Tylko dla zaawansowanych."
                  className={`px-4 py-2 text-sm font-medium transition-colors ${manualPlacements ? "bg-red-500/20 text-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                >
                  {t("section4.manualBtn")}
                </button>
              </div>
              {!manualPlacements && (
                <span className="text-[10px] text-white/30">— polecane dla początkujących</span>
              )}
            </div>

            {manualPlacements && (
              <div className="space-y-4 mt-2">
                {/* Publisher Platforms */}
                <Field>
                  <FieldLabel>
                    <span
                      className="inline-flex items-center gap-1.5"
                      title="Wybierz na których platformach Meta pokaże twoje reklamy. Dla większości kampanii zostaw wszystkie zaznaczone — Meta sama wybierze najtańsze."
                    >
                      {t("section4.platformsLabel")}
                      <HelpCircle className="w-3 h-3 text-white/30" />
                    </span>
                  </FieldLabel>
                  <div className="flex flex-wrap gap-dense">
                    {PUBLISHER_PLATFORMS.map((p) => (
                      <label key={p.value} className="flex items-center space-x-2 cursor-pointer">
                        <Checkbox
                          checked={publisherPlatforms.includes(p.value)}
                          onCheckedChange={() =>
                            setPublisherPlatforms(toggleArrayItem(publisherPlatforms, p.value))
                          }
                        />
                        <span className="text-sm text-white/70">
                          {t(`publisherPlatforms.${p.value}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </Field>

                {/* Facebook Positions */}
                {publisherPlatforms.includes("facebook") && (
                  <Field>
                    <FieldLabel title="Konkretne miejsca na Facebooku gdzie pokażesz reklamę: Feed (główny), Stories, Reels, Marketplace, Search, Video Feed. Najczęściej używane: Feed + Stories + Reels.">
                      {t("section4.fbPositionsLabel")}
                    </FieldLabel>
                    <div className="flex flex-wrap gap-dense">
                      {FACEBOOK_POSITIONS.map((p) => (
                        <label key={p.value} className="flex items-center space-x-2 cursor-pointer">
                          <Checkbox
                            checked={facebookPositions.includes(p.value)}
                            onCheckedChange={() =>
                              setFacebookPositions(toggleArrayItem(facebookPositions, p.value))
                            }
                          />
                          <span className="text-xs text-white/60">
                            {t(`facebookPositions.${p.value}`)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </Field>
                )}

                {/* Instagram Positions */}
                {publisherPlatforms.includes("instagram") && (
                  <Field>
                    <FieldLabel title="Konkretne miejsca na Instagramie: Feed, Stories, Reels, Explore, Shop. Reels mają najwyższy zasięg dla nowych odbiorców.">
                      {t("section4.igPositionsLabel")}
                    </FieldLabel>
                    <div className="flex flex-wrap gap-dense">
                      {INSTAGRAM_POSITIONS.map((p) => (
                        <label key={p.value} className="flex items-center space-x-2 cursor-pointer">
                          <Checkbox
                            checked={instagramPositions.includes(p.value)}
                            onCheckedChange={() =>
                              setInstagramPositions(toggleArrayItem(instagramPositions, p.value))
                            }
                          />
                          <span className="text-xs text-white/60">
                            {t(`instagramPositions.${p.value}`)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </Field>
                )}
              </div>
            )}
          </div>

          {/* ================ SECTION 5: KREACJE REKLAMOWE ================ */}
          <div id="section-5" className={SECTION_CLS}>
            <div className="flex items-center gap-2.5">
              <span
                className={`w-6 h-6 rounded-full border text-[11px] font-bold flex items-center justify-center flex-shrink-0 ${
                  sectionDone.s5
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-red-600/15 border-red-500/25 text-red-400"
                }`}
              >
                {sectionDone.s5 ? <Check className="w-3 h-3" /> : "5"}
              </span>
              <FileText className="w-3.5 h-3.5 text-white/40" />
              <h3 className="text-sm font-semibold text-white tracking-wide">
                {t("section5.title")}
              </h3>
              <span title="Tu konfigurujesz wygląd reklamy: stronę FB skąd jest publikowana, IG, URL strony docelowej i teksty (primary/headline/CTA). To, co user faktycznie zobaczy.">
                <HelpCircle className="w-3.5 h-3.5 text-white/30 hover:text-white/60 cursor-help" />
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
              <Field className="relative" id="field-fb-page">
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Strona FB jako 'twarz' reklamy. Każda reklama musi być uruchomiona z konkretnej Strony FB (nie konta osobistego). Nazwa Strony pojawi się obok reklamy."
                  >
                    {t("section5.fbPageLabel")}
                    <span className="text-red-400 ml-0.5" title="Pole wymagane">
                      *
                    </span>
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <button
                  type="button"
                  onClick={() => setPageDropdownOpen(!pageDropdownOpen)}
                  title="Wybierz Stronę FB skąd reklama będzie publikowana. Lista pochodzi z Business Managera."
                  className="flex items-center justify-between w-full h-9 px-3 rounded-md bg-[#030407] border border-white/[0.1] text-sm text-white text-left"
                >
                  <span className={pageId ? "text-white" : "text-white/30"}>
                    {pageId
                      ? pages.find((p) => p.id === pageId)?.name || pageId
                      : pagesLoading
                        ? t("section5.fbPageLoading")
                        : pagesError
                          ? t("section5.fbPageError")
                          : pages.length === 0
                            ? t("section5.fbPageEmpty")
                            : t("section5.fbPagePlaceholder")}
                  </span>
                  <Search className="w-3.5 h-3.5 text-white/30" />
                </button>
                {pageDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => {
                        setPageDropdownOpen(false);
                        setPageSearch("");
                      }}
                    />
                    <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-[#0a0c10] border border-white/[0.1] rounded-md shadow-xl max-h-64 overflow-hidden">
                      <div className="p-2 border-b border-white/[0.08]">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                          <Input
                            value={pageSearch}
                            onChange={(e) => setPageSearch(e.target.value)}
                            placeholder={t("section5.fbPageSearch")}
                            title="Wpisz nazwę Strony FB by ją odszukać"
                            className="h-8 pl-8 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {pages
                          .filter(
                            (p) =>
                              !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase())
                          )
                          .map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                setPageId(p.id);
                                if (p.instagramBusinessAccount?.id) {
                                  setInstagramActorId(p.instagramBusinessAccount.id);
                                } else {
                                  setInstagramActorId("");
                                }
                                setPageDropdownOpen(false);
                                setPageSearch("");
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/[0.08] flex items-center gap-2 ${p.id === pageId ? "text-[#ef3e29]" : "text-white"}`}
                            >
                              <span className="truncate">{p.name}</span>
                              {p.instagramBusinessAccount?.username && (
                                <span className="text-xs text-purple-400 ml-auto flex-shrink-0">
                                  @{p.instagramBusinessAccount.username}
                                </span>
                              )}
                            </button>
                          ))}
                        {pages.filter(
                          (p) =>
                            !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase())
                        ).length === 0 &&
                          pageSearch.trim() && (
                            <button
                              type="button"
                              onClick={() => {
                                // Manual page-ID entry — use whatever the user typed as the FB Page ID
                                // (covers pages not returned by the API, e.g. freshly created / BM-scoped).
                                setPageId(pageSearch.trim());
                                setPageDropdownOpen(false);
                                setPageSearch("");
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-white/[0.08] text-[#ef3e29]"
                            >
                              Użyj ID strony: {pageSearch.trim()}
                            </button>
                          )}
                        {pages.filter(
                          (p) =>
                            !pageSearch || p.name.toLowerCase().includes(pageSearch.toLowerCase())
                        ).length === 0 &&
                          !pageSearch.trim() && (
                            <div className="px-3 py-2 text-sm text-white/30">
                              Wpisz ID strony FB, jeśli nie ma jej na liście
                            </div>
                          )}
                      </div>
                    </div>
                  </>
                )}
              </Field>

              <Field className="relative">
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Konto Instagram dla reklam na IG (Reels, Stories, Feed). Opcjonalne — bez tego reklama nie pokaże się na IG."
                  >
                    {t("section5.igAccountLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                {(() => {
                  const selectedPage = pages.find((p) => p.id === pageId);
                  const igAcc = selectedPage?.instagramBusinessAccount;
                  const selectedIg = igAccounts.find((a) => a.id === instagramActorId);
                  const isManualId =
                    instagramActorId && !selectedIg && !(igAcc && instagramActorId === igAcc.id);
                  const displayLabel =
                    igAcc && instagramActorId === igAcc.id
                      ? igAcc.username
                        ? `@${igAcc.username}`
                        : igAcc.name || igAcc.id
                      : selectedIg
                        ? selectedIg.username
                          ? `@${selectedIg.username}`
                          : selectedIg.name || selectedIg.id
                        : isManualId
                          ? `ID: ${instagramActorId}`
                          : "";
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => setIgDropdownOpen(!igDropdownOpen)}
                        title="Wybierz konto Instagram (IG Business / Creator) skąd reklama będzie publikowana na IG (Reels, Stories, Feed). Bez tego reklama nie pojawi się na IG."
                        className="flex items-center justify-between w-full h-9 px-3 rounded-md bg-[#030407] border border-white/[0.1] text-sm text-white text-left"
                      >
                        <span className={displayLabel ? "text-white" : "text-white/30"}>
                          {displayLabel ||
                            (igLoading
                              ? t("section5.igLoading")
                              : igError
                                ? t("section5.igError")
                                : igAccounts.length === 0
                                  ? t("section5.igEmpty")
                                  : t("section5.igPlaceholder"))}
                        </span>
                        <Search className="w-3.5 h-3.5 text-white/30" />
                      </button>
                      {igDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => {
                              setIgDropdownOpen(false);
                              setIgSearch("");
                            }}
                          />
                          <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-[#0a0c10] border border-white/[0.1] rounded-md shadow-xl max-h-64 overflow-hidden">
                            <div className="p-2 border-b border-white/[0.08]">
                              <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                                <Input
                                  value={igSearch}
                                  onChange={(e) => setIgSearch(e.target.value)}
                                  placeholder={t("section5.igSearch")}
                                  className="h-8 pl-8 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="overflow-y-auto max-h-48">
                              {igAccounts
                                .filter(
                                  (a) =>
                                    !igSearch ||
                                    (a.username || a.name || a.id || "")
                                      .toLowerCase()
                                      .includes(igSearch.toLowerCase())
                                )
                                .map((a) => (
                                  <button
                                    key={a.id}
                                    type="button"
                                    onClick={() => {
                                      setInstagramActorId(a.id);
                                      setIgDropdownOpen(false);
                                      setIgSearch("");
                                    }}
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-white/[0.08] flex items-center gap-2 ${a.id === instagramActorId ? "text-purple-400" : "text-white"}`}
                                  >
                                    <span className="truncate">
                                      {a.username ? `@${a.username}` : a.name || a.id}
                                    </span>
                                  </button>
                                ))}
                              {igAccounts.filter(
                                (a) =>
                                  !igSearch ||
                                  (a.username || a.name || a.id || "")
                                    .toLowerCase()
                                    .includes(igSearch.toLowerCase())
                              ).length === 0 &&
                                igSearch.trim() && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setInstagramActorId(igSearch.trim());
                                      setIgDropdownOpen(false);
                                      setIgSearch("");
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-white/[0.08] text-purple-400"
                                  >
                                    {t("section5.igUseId", { id: igSearch.trim() })}
                                  </button>
                                )}
                              {igAccounts.filter(
                                (a) =>
                                  !igSearch ||
                                  (a.username || a.name || a.id || "")
                                    .toLowerCase()
                                    .includes(igSearch.toLowerCase())
                              ).length === 0 &&
                                !igSearch.trim() && (
                                  <div className="px-3 py-2 text-sm text-white/30">
                                    {t("section3.noResults")}
                                  </div>
                                )}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </Field>
            </div>

            {/* Partnership Ad */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Label className={LABEL_CLS + " mb-0"}>Partnership Ad</Label>
                  <span className="text-[10px] text-white/30 bg-white/[0.06] px-1.5 py-0.5 rounded">
                    Branded Content
                  </span>
                </div>
                <label
                  className="flex items-center gap-2 cursor-pointer"
                  title="Branded Content / Partnership Ads — reklama uruchamiana z konta influencera/partnera (wymaga jego zgody w Brand Collabs Manager). Daje 'organiczny' look — ludzie widzą reklamę z konta osoby a nie firmy."
                >
                  <span className="text-xs text-white/50">
                    {partnershipEnabled
                      ? t("section5.partnershipEnabled")
                      : t("section5.partnershipDisabled")}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPartnershipEnabled(!partnershipEnabled);
                      if (partnershipEnabled) {
                        setPartnerPageId("");
                        setPartnerIgUserId("");
                        setPartnerIgUserIdInput("");
                      }
                    }}
                    title={
                      partnershipEnabled
                        ? "Wyłącz Branded Content / Partnership Ads"
                        : "Włącz Branded Content — reklama z konta partnera/influencera. Wymaga jego zgody w Brand Collabs Manager."
                    }
                    className={`relative w-9 h-5 rounded-full transition-colors ${partnershipEnabled ? "bg-purple-600" : "bg-white/10"}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${partnershipEnabled ? "left-[18px]" : "left-0.5"}`}
                    />
                  </button>
                </label>
              </div>
              {partnershipEnabled && (
                <div className="space-y-2">
                  {/* Partner list from API */}
                  {partnersLoading && (
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <div className="w-3 h-3 border border-white/30 border-t-transparent rounded-full animate-spin" />
                      {t("section5.partnerLoading")}
                    </div>
                  )}
                  {partnerPages.length > 0 && (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {partnerPages.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setPartnerPageId(p.id);
                            setPartnerPageIdInput(p.id);
                          }}
                          className={`w-full px-3 py-2 rounded-md text-left text-sm flex items-center gap-2 transition-colors ${p.id === partnerPageId ? "bg-purple-500/20 border border-purple-500/30 text-purple-300" : "bg-[#030407] border border-white/[0.06] text-white hover:bg-white/[0.06]"}`}
                        >
                          {p.picture && (
                            <img src={p.picture} alt="" className="w-5 h-5 rounded-full" />
                          )}
                          <span className="truncate">{p.name}</span>
                          {p.creatorUsername && (
                            <span className="text-[11px] text-white/40">@{p.creatorUsername}</span>
                          )}
                          <span className="text-[10px] text-white/20 ml-auto">{p.id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Manual Page ID input (always visible as fallback) */}
                  <div className="relative">
                    <Input
                      value={partnerPageIdInput}
                      onChange={(e) => {
                        setPartnerPageIdInput(e.target.value);
                        setPartnerPageId(e.target.value.trim());
                      }}
                      placeholder={
                        partnerPages.length > 0
                          ? t("section5.partnerIdPlaceholderWithList")
                          : t("section5.partnerIdPlaceholderEmpty")
                      }
                      className="h-9 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30"
                    />
                  </div>
                  {/* Verification feedback for manual ID */}
                  {partnerVerifying && (
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <div className="w-3 h-3 border border-white/30 border-t-transparent rounded-full animate-spin" />
                      {t("section5.partnerVerifying")}
                    </div>
                  )}
                  {verifiedPage && isManualId && debouncedPartnerId === partnerPageId && (
                    <div className="flex items-center gap-2 p-2 rounded-md bg-purple-500/10 border border-purple-500/20">
                      {verifiedPage.picture && (
                        <img src={verifiedPage.picture} alt="" className="w-6 h-6 rounded-full" />
                      )}
                      <span className="text-sm text-purple-300">{verifiedPage.name}</span>
                      <span className="text-[10px] text-white/30">ID: {verifiedPage.id}</span>
                    </div>
                  )}
                  {!partnerVerifying && isManualId && !verifiedPage && (
                    <div className="text-xs text-red-400/70">{t("section5.partnerNotFound")}</div>
                  )}
                  <p className="text-[10px] text-white/30">
                    {partnerPages.length === 0 && !partnersLoading
                      ? t("section5.partnerPermissionsNote")
                      : t("section5.partnerIdHint")}
                  </p>
                  {/* Instagram User ID for Partnership Ad */}
                  <div className="mt-3 pt-3 border-t border-white/[0.06]">
                    <Label className={LABEL_CLS + " text-[11px] mb-1.5"}>
                      {t("section5.partnerIgLabel")}
                    </Label>
                    <Input
                      value={partnerIgUserIdInput}
                      onChange={(e) => {
                        setPartnerIgUserIdInput(e.target.value);
                        setPartnerIgUserId(e.target.value.trim());
                      }}
                      placeholder={t("section5.partnerIgPlaceholder")}
                      className="h-9 text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30"
                    />
                    <p className="text-[10px] text-white/30 mt-1">{t("section5.partnerIgHint")}</p>
                  </div>
                </div>
              )}
            </div>

            {isOnAdOnly && selectedLeadgenFormId && (
              <Field>
                <FieldLabel id="field-link-url">
                  <span
                    className="inline-flex items-center gap-1.5"
                    title={t("section5.leadgenLinkTooltip")}
                  >
                    {t("section5.leadgenLinkLabel")}
                    {needsManualLeadgenLink && (
                      <span className="text-red-400 ml-0.5" title="Pole wymagane">
                        *
                      </span>
                    )}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                {!creativeLinkKnown && !creativeLinkFailed ? (
                  <p className="text-[11px] text-white/40">{t("section5.leadgenLinkChecking")}</p>
                ) : linkUrl.trim() ? (
                  <p className="text-[11px] text-white/50 flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5 text-emerald-400" />
                    <span>{t("section5.leadgenLinkUsingOverride")}</span>
                  </p>
                ) : resolvedCreativeLink ? (
                  <p className="text-[11px] text-white/50 flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5 text-emerald-400" />
                    <span>
                      {t("section5.leadgenLinkResolved")}{" "}
                      <span className="font-mono text-white/70 break-all">
                        {resolvedCreativeLink}
                      </span>{" "}
                      {t("section5.leadgenLinkOverrideHint")}
                    </span>
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-300/80 flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                      {creativeLinkFailed
                        ? t("section5.leadgenLinkCheckFailed")
                        : t("section5.leadgenLinkMissing")}
                    </span>
                  </p>
                )}
                <Input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder={t("section5.linkPlaceholder")}
                  className={`${INPUT_CLS} mt-2`}
                />
              </Field>
            )}

            {!isOnAdOnly && (
              <Field>
                <FieldLabel id="field-link-url">
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Adres URL strony, na który klient trafi po kliknięciu w reklamę (landing page, formularz, sklep). MUSI być pełen URL z https:// na początku. Bez tego reklama nie zostanie zatwierdzona."
                  >
                    {t("section5.linkLabel")}
                    <span className="text-red-400 ml-0.5" title="Pole wymagane">
                      *
                    </span>
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                {(() => {
                  const trimmed = linkUrl.trim();
                  const looksValid = !!trimmed && /^https?:\/\/.+\..+/i.test(trimmed);
                  const looksInvalid = !!trimmed && !looksValid;
                  const missingProtocol =
                    !!trimmed && !/^https?:\/\//i.test(trimmed) && /\..+/.test(trimmed);
                  return (
                    <>
                      <div className="relative">
                        <Input
                          type="url"
                          value={linkUrl}
                          onChange={(e) => setLinkUrl(e.target.value)}
                          placeholder={t("section5.linkPlaceholder")}
                          className={`${INPUT_CLS} ${looksInvalid ? "border-red-500/40" : looksValid ? "border-emerald-500/30" : ""}`}
                        />
                        {looksValid && (
                          <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400 pointer-events-none" />
                        )}
                      </div>
                      {missingProtocol && (
                        <button
                          type="button"
                          onClick={() => setLinkUrl(`https://${trimmed}`)}
                          className="text-[10px] text-amber-300/80 hover:text-amber-200 mt-1 underline decoration-dotted"
                          title="Dodaj https:// na początku"
                        >
                          Dodaj <span className="font-mono">https://</span> automatycznie
                        </button>
                      )}
                      {!missingProtocol && looksInvalid && (
                        <p className="text-[10px] text-red-400/80 mt-1 flex items-start gap-1">
                          <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                          <span>
                            To nie wygląda na poprawny URL. Przykład:{" "}
                            <span className="font-mono text-white/60">
                              https://moja-strona.pl/oferta
                            </span>
                          </span>
                        </p>
                      )}
                    </>
                  );
                })()}
              </Field>
            )}

            {/* UTM Parameters */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Label className={LABEL_CLS + " mb-0"}>Parametry UTM (URL Parameters)</Label>
                  <span className="text-[10px] text-white/30 bg-white/[0.06] px-1.5 py-0.5 rounded">
                    Tracking
                  </span>
                  <span title="UTM parametry pozwalają śledzić ruch w Google Analytics. Domyślne wartości (facebook/paid/{nazwa kampanii}) są OK. Wyłącz toggle po prawej jeśli twoja strona używa innego systemu trackingu i UTM by przeszkadzał.">
                    <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                  </span>
                </div>
                <label
                  className="flex items-center gap-2 cursor-pointer"
                  title="Włącz/wyłącz dodawanie UTM parametrów do URL reklamy. Włączone domyślnie — pomaga w trackingu w GA. Wyłącz tylko jeśli twoja strona ma własny system trackingu kolidujący z UTM."
                >
                  <span className="text-xs text-white/50">
                    {utmEnabled ? t("section5.utmEnabled") : t("section5.utmDisabled")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setUtmEnabled(!utmEnabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${utmEnabled ? "bg-purple-600" : "bg-white/10"}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${utmEnabled ? "left-[18px]" : "left-0.5"}`}
                    />
                  </button>
                </label>
              </div>
              {utmEnabled && (
                <>
                  <p className="text-[11px] text-white/40 mb-3">{t("section5.utmNote")}</p>
                  <div className="grid grid-cols-2 gap-dense sm:grid-cols-3">
                    <div>
                      <Label
                        className="text-[11px] text-white/50 mb-1 block"
                        title="Skąd pochodzi ruch (np. 'facebook', 'instagram'). Domyślnie: facebook."
                      >
                        utm_source
                      </Label>
                      <Input
                        value={utmSource}
                        onChange={(e) => setUtmSource(e.target.value)}
                        placeholder="facebook"
                        className={INPUT_CLS + " text-xs"}
                      />
                    </div>
                    <div>
                      <Label
                        className="text-[11px] text-white/50 mb-1 block"
                        title="Typ kanału (np. 'paid' = płatne, 'organic' = organiczne, 'cpc' = pay per click). Domyślnie: paid."
                      >
                        utm_medium
                      </Label>
                      <Input
                        value={utmMedium}
                        onChange={(e) => setUtmMedium(e.target.value)}
                        placeholder="paid"
                        className={INPUT_CLS + " text-xs"}
                      />
                    </div>
                    <div>
                      <Label
                        className="text-[11px] text-white/50 mb-1 block"
                        title="ID lub nazwa kampanii. Wartość {{campaign.id}} jest auto-zastępowana przez Meta przy publikacji reklamy."
                      >
                        utm_campaign
                      </Label>
                      <Input
                        value={utmCampaign}
                        onChange={(e) => setUtmCampaign(e.target.value)}
                        placeholder="{{campaign.id}}"
                        className={INPUT_CLS + " text-xs"}
                      />
                    </div>
                    <div>
                      <Label
                        className="text-[11px] text-white/50 mb-1 block"
                        title="Identyfikuje konkretny ad set (audiencję). Wartość {{adset.id}} = Meta wstawia ID ad seta automatycznie. Pomocne w analizie skuteczności audiencji."
                      >
                        utm_term
                      </Label>
                      <Input
                        value={utmTerm}
                        onChange={(e) => setUtmTerm(e.target.value)}
                        placeholder="{{adset.id}}"
                        className={INPUT_CLS + " text-xs"}
                      />
                    </div>
                    <div>
                      <Label
                        className="text-[11px] text-white/50 mb-1 block"
                        title="Identyfikuje konkretną reklamę. Wartość {{ad.id}} = Meta wstawia ID reklamy. Pomocne by śledzić która kreatywa generuje konwersje."
                      >
                        utm_content
                      </Label>
                      <Input
                        value={utmContent}
                        onChange={(e) => setUtmContent(e.target.value)}
                        placeholder="{{ad.id}}"
                        className={INPUT_CLS + " text-xs"}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          setUtmSource("facebook");
                          setUtmMedium("paid");
                          setUtmCampaign("{{campaign.id}}");
                          setUtmTerm("{{adset.id}}");
                          setUtmContent("{{ad.id}}");
                        }}
                        title="Resetuje wszystkie UTM-y do domyślnych: facebook / paid / {{campaign.id}} / {{adset.id}} / {{ad.id}}"
                        className="text-[11px] text-purple-400 hover:text-purple-300 transition-colors px-2 py-1.5 rounded border border-purple-500/20 hover:border-purple-500/40 w-full text-center"
                      >
                        {t("section5.utmReset")}
                      </button>
                    </div>
                  </div>
                  <div
                    className="mt-3 p-2 rounded bg-white/[0.03] border border-white/[0.04]"
                    title="Podgląd jak wygląda URL z dokladonymi UTM-ami. Skopiuj jeśli potrzebujesz."
                  >
                    <p className="text-[10px] text-white/30 font-mono break-all">
                      ?utm_source={utmSource}&utm_medium={utmMedium}&utm_campaign={utmCampaign}
                      &utm_term={utmTerm}&utm_content={utmContent}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* 5 Primary Texts */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <Label className={LABEL_CLS} id="field-primary-text">
                    {t("section5.primaryTextLabel")}
                    <span className="text-red-400 ml-0.5" title="Co najmniej jeden tekst wymagany">
                      *
                    </span>
                    <span
                      className="ml-1.5"
                      title="Główny tekst pod nagłówkiem reklamy. Pierwsze 125 znaków widać bez kliknięcia 'Zobacz więcej'. Najważniejsze info dawaj na początek."
                    >
                      <HelpCircle className="w-3 h-3 text-white/30 inline" />
                    </span>
                  </Label>
                  <p className="text-[10px] text-white/30 mt-0.5">
                    {t("section5.primaryTextNote")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="flex items-center gap-1.5"
                    title="Ile wariantów teksów reklamy chcesz przygotować. Meta auto-testuje wszystkie warianty i wybiera najlepszy. Lead Gen używa 1 (formularz Instant Form)"
                  >
                    <span className="text-[10px] text-white/40">
                      {t("section5.primaryTextCount")}
                    </span>
                    <select
                      value={generateCount}
                      onChange={(e) => setGenerateCount(Number(e.target.value))}
                      title="Liczba wariantów copy (primary text + headline). 1 = jeden wariant, 5 = pięć osobnych do testowania. Każdy wariant tworzy osobną reklamę. Polecane: 3-5 dla A/B testów copy."
                      className="bg-[#030407] border border-white/[0.1] text-white text-xs rounded px-1.5 py-1 focus:outline-none focus:border-purple-500/50"
                    >
                      {(objective === "OUTCOME_LEADS" ? [1] : [1, 2, 3, 4, 5]).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setAiCopyMenuOpen(!aiCopyMenuOpen)}
                      disabled={aiCopyLoading}
                      title="AI wygeneruje teksty reklam (primary text + nagłówki) na podstawie wybranego źródła: landing page, najlepszych reklam z konta lub wgranego wideo. Auto-wypełni pola poniżej."
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-600/30 to-blue-600/30 border border-purple-500/30 text-purple-300 hover:from-purple-600/40 hover:to-blue-600/40 hover:border-purple-500/50 transition-all disabled:opacity-50"
                    >
                      {aiCopyLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5" />
                      )}
                      {aiCopyLoading ? t("section5.aiCopyLoading") : t("section5.aiCopyBtn")}
                    </button>
                    {aiCopyMenuOpen && !aiCopyLoading && (
                      <div className="absolute right-0 top-full mt-1 z-30 w-72 rounded-lg border border-white/[0.08] bg-[#0a0c10] shadow-xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_lp")}
                          disabled={!linkUrl}
                          title={
                            !linkUrl
                              ? "Najpierw wpisz URL landing page w sekcji 5 (Reklama)"
                              : "AI przeczyta twoją stronę docelową i wygeneruje teksty pasujące do oferty"
                          }
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <Globe className="w-3.5 h-3.5 text-cyan-400" />
                            Na podstawie landing page
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuLpDesc")}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_best_ads")}
                          disabled={!adAccountId}
                          title={
                            !adAccountId
                              ? "Najpierw wybierz konto reklamowe w sekcji 1 (Cel)"
                              : "AI przeanalizuje najlepsze reklamy z twojego konta (CTR/ROAS) i napisze teksty w podobnym stylu"
                          }
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <Brain className="w-3.5 h-3.5 text-amber-400" />Z najlepszych reklam na
                            koncie
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuBestAdsDesc")}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_both")}
                          disabled={!linkUrl || !adAccountId}
                          title={
                            !linkUrl && !adAccountId
                              ? "Brakuje URL landing page i konta reklamowego"
                              : !linkUrl
                                ? "Najpierw wpisz URL landing page w sekcji 5"
                                : !adAccountId
                                  ? "Najpierw wybierz konto reklamowe w sekcji 1"
                                  : "Najlepsza jakość: AI łączy informacje z landing page + style najlepszych reklam"
                          }
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                            Landing page + najlepsze reklamy
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuLpAndBestDesc")}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_video")}
                          disabled={!creatives.some((c) => c.type === "video")}
                          title={
                            !creatives.some((c) => c.type === "video")
                              ? "Najpierw wgraj wideo w sekcji 6 (Kreatywy)"
                              : "AI przetranskrybuje twoje wideo (mowę / VSL) i napisze teksty na bazie tego co mówisz w nagraniu"
                          }
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <Video className="w-3.5 h-3.5 text-emerald-400" />Z transkrypcji wideo
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuTranscriptDesc")}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_video_lp")}
                          disabled={!creatives.some((c) => c.type === "video") || !linkUrl}
                          title={
                            !creatives.some((c) => c.type === "video") && !linkUrl
                              ? "Brakuje wideo w sekcji 6 i URL landing page w sekcji 5"
                              : !creatives.some((c) => c.type === "video")
                                ? "Najpierw wgraj wideo w sekcji 6"
                                : !linkUrl
                                  ? "Najpierw wpisz URL landing page w sekcji 5"
                                  : "Premium: AI łączy transkrypcję wideo + treść landing page = najbardziej kontekstowy copy"
                          }
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <Video className="w-3.5 h-3.5 text-teal-400" />
                            <span>{t("section5.aiMenuVideoLp")}</span>
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuVideoLpDesc")}
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_video_best_ads")}
                          disabled={!creatives.some((c) => c.type === "video") || !adAccountId}
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <Video className="w-3.5 h-3.5 text-rose-400" />
                            <span>{t("section5.aiMenuVideoBest")}</span>
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuVideoBestDesc")}
                          </p>
                        </button>

                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_offer_sheet")}
                          disabled={!hasOfferSheets && !activeOfferSheet}
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors border-b border-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <FileText className="w-3.5 h-3.5 text-emerald-400" />Z arkusza
                            ofertowego
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            Wygeneruj copy na podstawie Twojego arkusza oferty
                          </p>
                        </button>

                        <button
                          type="button"
                          onClick={() => generateAiCopy("from_graphics")}
                          disabled={
                            !creatives.some(
                              (c) =>
                                !!c.graphicPrompt ||
                                (c.type === "image" && (!!c.uploadedUrl || !!c.file))
                            )
                          }
                          className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
                            <ImageIcon className="w-3.5 h-3.5 text-pink-400" />
                            <span>Z grafik z kreatora reklam</span>
                          </div>
                          <p className="text-[10px] text-white/40 mt-0.5 pl-5.5">
                            {t("section5.aiMenuGraphicsDesc")}
                          </p>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Manual LP text paste area — shown when scraping fails */}
              {showManualLpInput && (
                <div className="mt-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-amber-400">
                      Wklej tekst ze strony docelowej
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setShowManualLpInput(false);
                        setManualLpText("");
                      }}
                      className="text-white/30 hover:text-white/60 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 mb-2">
                    {t("section5.manualLpInstructions")}
                  </p>
                  <Textarea
                    value={manualLpText}
                    onChange={(e) => setManualLpText(e.target.value)}
                    placeholder="Wklej tutaj tekst ze strony..."
                    className="min-h-[100px] text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 resize-none"
                  />
                  <button
                    type="button"
                    onClick={() => generateAiCopy("from_lp")}
                    disabled={manualLpText.trim().length < 20 || aiCopyLoading}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-amber-600/30 to-orange-600/30 border border-amber-500/30 text-amber-300 hover:from-amber-600/40 hover:to-orange-600/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {aiCopyLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    {aiCopyLoading ? t("section5.aiCopyLoading") : t("section5.manualLpBtn")}
                  </button>
                </div>
              )}

              {aiCopyLoading && (
                <AiGeneratingOverlay compact message={t("section5.generatingCopy")} />
              )}

              <div className="space-y-2">
                {primaryTexts.slice(0, generateCount).map((text, idx) => {
                  const isExpanded = expandedPT === idx;
                  const len = text.length;
                  const SWEET = 125;
                  const MAX = 500;
                  const counterColor =
                    len === 0
                      ? "text-white/25"
                      : len > MAX
                        ? "text-red-400"
                        : len > SWEET
                          ? "text-amber-300/80"
                          : "text-emerald-300/70";
                  return (
                    <div key={idx} className="relative group">
                      <Textarea
                        value={text}
                        onChange={(e) => updatePrimaryText(idx, e.target.value)}
                        placeholder={
                          idx === 0
                            ? t("section5.primaryTextMainPlaceholder")
                            : t("section5.primaryTextVariantPlaceholder", { index: idx + 1 })
                        }
                        className={`text-sm bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 resize-none pr-9 transition-all ${idx === 0 ? "border-l-2 border-l-red-500/50" : ""} ${isExpanded ? "min-h-[160px]" : "min-h-[60px]"}`}
                      />
                      {text && (
                        <button
                          type="button"
                          onClick={() => setExpandedPT(isExpanded ? null : idx)}
                          className="absolute top-2 right-2 p-1 rounded text-white/30 hover:text-white/70 transition-colors"
                          title={isExpanded ? t("section5.collapse") : t("section5.expand")}
                        >
                          {isExpanded ? (
                            <Minimize2 className="w-3.5 h-3.5" />
                          ) : (
                            <Maximize2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                      <div
                        className={`text-[10px] mt-1 flex items-center justify-between ${counterColor}`}
                      >
                        <span>
                          {len > MAX
                            ? `Za długi (${len} / max ${MAX} znaków)`
                            : len > SWEET
                              ? `${len} znaków — Meta utnie po ${SWEET} jako "Zobacz więcej"`
                              : len > 0
                                ? `${len} / ${SWEET} znaków (sweet spot)`
                                : `Optymalnie do ${SWEET} znaków, max ${MAX}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 5 Headlines */}
            <div>
              <Label className={LABEL_CLS} id="field-headline">
                {t("section5.headlineLabel")}
                <span className="text-red-400 ml-0.5" title="Co najmniej jeden nagłówek wymagany">
                  *
                </span>
                <span
                  className="ml-1.5"
                  title="Krótki, hookujący nagłówek pod kreatywą. Powinien zawierać benefit dla usera. Sweet spot: do 40 znaków."
                >
                  <HelpCircle className="w-3 h-3 text-white/30 inline" />
                </span>
              </Label>
              <p className="text-[10px] text-white/30 mt-0.5 mb-2">{t("section5.headlineNote")}</p>
              <div className="space-y-2">
                {headlines.slice(0, generateCount).map((text, idx) => {
                  const len = text.length;
                  const SWEET = 40;
                  const MAX = 255;
                  const counterColor =
                    len === 0
                      ? "text-white/25"
                      : len > MAX
                        ? "text-red-400"
                        : len > SWEET
                          ? "text-amber-300/80"
                          : "text-emerald-300/70";
                  return (
                    <div key={idx}>
                      <Input
                        value={text}
                        onChange={(e) => updateHeadline(idx, e.target.value)}
                        placeholder={
                          idx === 0
                            ? t("section5.headlineMainPlaceholder")
                            : t("section5.headlineVariantPlaceholder", { index: idx + 1 })
                        }
                        className={`${INPUT_CLS} ${idx === 0 ? "border-l-2 border-l-red-500/50" : ""}`}
                      />
                      <div className={`text-[10px] mt-0.5 ${counterColor}`}>
                        {len > MAX
                          ? `Za długi nagłówek (${len} / max ${MAX} znaków)`
                          : len > SWEET
                            ? `${len} znaków — Meta może obciąć w niektórych umiejscowieniach (sweet spot: do ${SWEET})`
                            : len > 0
                              ? `${len} / ${SWEET} znaków (sweet spot)`
                              : `Optymalnie do ${SWEET} znaków`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-balanced">
              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Call To Action — przycisk akcji w reklamie (np. 'Dowiedz się więcej', 'Zarejestruj się'). Wpływa na skuteczność reklamy. Najczęściej używane: 'Dowiedz się więcej' (uniwersalny) lub 'Zarejestruj się' / 'Zacznij teraz' przy lead-genie."
                  >
                    {t("section5.ctaLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <Select value={callToAction} onValueChange={setCallToAction}>
                  <SelectTrigger className={SELECT_TRIGGER_CLS}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={SELECT_CONTENT_CLS}>
                    {CTA_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value} className={SELECT_ITEM_CLS}>
                        {t(`cta.${c.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>
                  <span
                    className="inline-flex items-center gap-1.5"
                    title="Krótki opis pod nagłówkiem (np. doprecyzowanie oferty). Opcjonalne — wyświetla się tylko w niektórych umiejscowieniach."
                  >
                    {t("section5.descriptionLabel")}
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </span>
                </FieldLabel>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("section5.descriptionPlaceholder")}
                  className={INPUT_CLS}
                  maxLength={300}
                />
                {description.length > 0 && (
                  <p
                    className={`text-[10px] mt-0.5 ${description.length > 125 ? "text-amber-300/80" : "text-emerald-300/70"}`}
                  >
                    {description.length} / 30 znaków (sweet spot · max 300)
                  </p>
                )}
              </Field>
            </div>
          </div>

          {/* ================ SECTION 6: PLIKI REKLAMOWE ================ */}
          <div id="section-6" className={SECTION_CLS}>
            <div className="flex items-start gap-2.5">
              <span
                className={`w-6 h-6 rounded-full border text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  sectionDone.s6
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-red-600/15 border-red-500/25 text-red-400"
                }`}
              >
                {sectionDone.s6 ? <Check className="w-3 h-3" /> : "6"}
              </span>
              <ImageIcon className="w-3.5 h-3.5 text-white/40 mt-1.5" />
              <div>
                <h3 className="text-sm font-semibold text-white tracking-wide flex items-center gap-1.5">
                  {t("section6.title")}
                  <span title="Wgraj zdjęcia (JPG/PNG/WebP/HEIC) lub wideo (MP4/MOV) — kreatywy reklam. Dla A/B testu kreatywów wgraj kilka i włącz 'Split ads' niżej, by każdy plik dostał osobny ad set.">
                    <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                  </span>
                </h3>
                <p className="text-[10px] text-white/40 mt-0.5">
                  {primaryTexts.filter(Boolean).length > 1 || headlines.filter(Boolean).length > 1
                    ? t("section6.multiVariantInfo", {
                        textCount: Math.max(
                          primaryTexts.filter(Boolean).length,
                          headlines.filter(Boolean).length
                        ),
                      })
                    : t("section6.singleVariantInfo")}
                </p>
              </div>
            </div>

            {(primaryTexts.filter(Boolean).length > 1 || headlines.filter(Boolean).length > 1) && (
              <div className="p-3 rounded-lg border border-blue-500/20 bg-blue-500/5 text-xs text-blue-300/80 leading-relaxed">
                <strong className="text-blue-300">{t("section6.multiVariantBanner")}</strong>{" "}
                {t("section6.multiVariantTexts", {
                  textCount: primaryTexts.filter(Boolean).length,
                  headlineCount: headlines.filter(Boolean).length,
                })}{" "}
                {t("section6.multiVariantPerFile", {
                  count: Math.max(
                    primaryTexts.filter(Boolean).length,
                    headlines.filter(Boolean).length
                  ),
                })}
                {creatives.length > 1 &&
                  ` ${t("section6.multiVariantTotal", { total: creatives.length * Math.max(primaryTexts.filter(Boolean).length, headlines.filter(Boolean).length) })}`}
              </div>
            )}

            {/* Library picker + open ad creator buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setLibraryDialogOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/[0.04] border border-white/[0.1] text-white/80 hover:bg-white/[0.07] hover:border-white/20 transition-colors"
                title="Wybierz wcześniej wygenerowaną grafikę lub wideo z biblioteki tej aplikacji."
              >
                <ImageIcon className="w-3.5 h-3.5" /> Wybierz z biblioteki
              </button>
              <button
                type="button"
                onClick={() => setRouterLocation("/chat/content-creator")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 border border-red-500/30 text-red-200 hover:bg-red-500/20 transition-colors"
                title="Otwórz Twórcę reklam — wygeneruj nową grafikę / wideo, a potem wróć tutaj."
              >
                <Sparkles className="w-3.5 h-3.5" /> Otwórz Twórcę reklam
              </button>
              <span className="text-[10px] text-white/30">albo wgraj plik niżej ⤵</span>
            </div>

            {/* Drop zone */}
            <div
              id="field-creatives"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              title="Wgraj kreatywy reklamy. Każdy plik = osobna reklama (lub wariant w split testach). Wymiary 1:1 (1080x1080) działają na wszystkich placementach, 9:16 (1080x1920) najlepiej na Reels/Stories."
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-red-500/50 hover:bg-white/[0.02] transition-all ${
                creatives.length === 0
                  ? "border-red-500/40 bg-red-500/[0.04] animate-[pulse_3s_ease-in-out_infinite]"
                  : "border-white/[0.15]"
              }`}
            >
              <Upload
                className={`w-8 h-8 mx-auto mb-3 ${creatives.length === 0 ? "text-red-400/70" : "text-white/30"}`}
              />
              <p className="text-sm text-white/70 font-medium">
                {creatives.length === 0 ? (
                  <>
                    Przeciągnij i upuść pliki tutaj <span className="text-white/40">lub</span>{" "}
                    <span className="text-red-400 underline">wybierz z dysku</span>
                  </>
                ) : (
                  <>
                    {t("section6.dropzone")}
                    <span className="text-red-400 underline">{t("section6.dropzoneClick")}</span>
                  </>
                )}
              </p>
              <p className="text-[11px] text-white/40 mt-1.5">
                <span className="text-white/60">JPG · PNG · WebP · HEIC · MP4 · MOV</span>
                <span className="mx-2 text-white/20">·</span>
                <span>max 4 GB / plik</span>
                <span className="mx-2 text-white/20">·</span>
                <span>możesz dodać kilka na raz</span>
              </p>
              {creatives.length === 0 && (
                <p className="text-[10px] text-amber-600 dark:text-amber-300/70 mt-2">
                  Tip: możesz dodać kilka grafik lub wideo i zostaną automatycznie przetestowane
                  jako warianty reklamy.
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,video/mp4,video/quicktime,video/mov"
                onChange={(e) => e.target.files && void handleFiles(e.target.files)}
                className="hidden"
              />
            </div>

            {creatives.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-dense">
                {creatives.map((creative) => (
                  <div
                    key={creative.id}
                    className="relative bg-white/[0.06] border border-white/[0.1] rounded-lg overflow-hidden group"
                  >
                    {creative.type === "image" ? (
                      <img
                        src={creative.preview}
                        alt={creative.name}
                        className="w-full h-24 object-cover"
                      />
                    ) : (
                      <div className="w-full h-24 flex items-center justify-center bg-black/30">
                        <Video className="w-8 h-8 text-white/40" />
                      </div>
                    )}
                    <div className="p-2">
                      <p className="text-[10px] text-white/60 truncate">{creative.name}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        {creative.type === "image" ? (
                          <ImageIcon className="w-3 h-3 text-blue-400" />
                        ) : (
                          <Video className="w-3 h-3 text-purple-400" />
                        )}
                        <span className="text-[9px] text-white/40 uppercase">{creative.type}</span>
                        {creative.uploading && (
                          <span title="Plik jest właśnie wysyłany do Meta...">
                            <Loader2 className="w-3 h-3 text-yellow-400 animate-spin" />
                          </span>
                        )}
                        {creative.uploadedUrl && (
                          <span title="Plik został wgrany do Meta i jest gotowy do użycia w reklamie">
                            <CheckCircle2 className="w-3 h-3 text-green-400" />
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCreative(creative.id);
                      }}
                      title="Usuń tę kreatywkę"
                      className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Library picker dialog */}
            <Dialog open={libraryDialogOpen} onOpenChange={setLibraryDialogOpen}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#0c0e14] border border-white/[0.1]">
                <DialogHeader>
                  <DialogTitle className="text-white">Biblioteka reklam</DialogTitle>
                </DialogHeader>
                {libraryLoading ? (
                  <div className="flex items-center justify-center py-12 text-white/50 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Ładowanie biblioteki...
                  </div>
                ) : libraryItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <ImageIcon className="w-10 h-10 text-white/20 mb-3" />
                    <p className="text-sm text-white/70 font-medium mb-1">
                      Brak wygenerowanych reklam
                    </p>
                    <p className="text-[11px] text-white/40 mb-4 max-w-sm">
                      Wygeneruj pierwsze grafiki / wideo w Twórcy reklam, a potem wróć tu, żeby
                      wybrać je do kampanii.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setLibraryDialogOpen(false);
                        setRouterLocation("/chat/content-creator");
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 transition-colors"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Otwórz Twórcę reklam
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {libraryItems.map((item) => {
                      const isVideo =
                        (item.mediaType ?? "").startsWith("video") ||
                        /\.(mp4|mov|webm)$/i.test(item.filename);
                      const alreadyAdded = creatives.some((c) => c.uploadedUrl === item.signedUrl);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          disabled={!item.signedUrl || alreadyAdded}
                          onClick={() => {
                            handlePickFromLibrary(item);
                          }}
                          className={`group relative rounded-lg overflow-hidden border transition-all text-left ${alreadyAdded ? "border-emerald-500/40 ring-1 ring-emerald-500/20" : "border-white/[0.08] hover:border-red-500/40"} ${!item.signedUrl ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                          <div className="aspect-square bg-black/30 flex items-center justify-center">
                            {isVideo ? (
                              <Video className="w-8 h-8 text-white/40" />
                            ) : item.signedUrl ? (
                              <img
                                src={item.signedUrl}
                                alt={item.filename}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <ImageIcon className="w-8 h-8 text-white/30" />
                            )}
                          </div>
                          <div className="p-1.5 bg-black/40">
                            <p className="text-[10px] text-white/70 truncate">{item.filename}</p>
                            <p className="text-[9px] text-white/35 uppercase">
                              {item.format}
                              {isVideo ? " · video" : ""}
                            </p>
                          </div>
                          {alreadyAdded && (
                            <div className="absolute top-1 right-1 bg-emerald-500/90 rounded-full p-0.5">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <DialogFooter className="border-t border-white/[0.06] pt-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setLibraryDialogOpen(false)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/[0.04] border border-white/[0.1] text-white/70 hover:bg-white/[0.07] transition-colors"
                  >
                    Zamknij
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Split ads across ad sets */}
            {creatives.length > 1 && (
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/80 font-medium flex items-center gap-1.5">
                      {t("section6.splitLabel")}
                      <span title="Zamiast jednego ad seta z N kreatywami, stwórz N osobnych ad setów po 1 kreatywie. Pomaga przy A/B testach kreatywów — każda kreacja dostaje własny budżet.">
                        <HelpCircle className="w-3 h-3 text-white/30 hover:text-white/60 cursor-help" />
                      </span>
                    </p>
                    <p className="text-[11px] text-white/40 mt-0.5">
                      {t("section6.splitDesc", { count: creatives.length })}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={launchAllVariants}
                    title={
                      launchAllVariants
                        ? t("section6.splitConflict")
                        : splitAdsEnabled
                          ? "Wyłącz split — wszystkie kreatywy w jednym ad secie"
                          : "Włącz split — N kreatyw → N osobnych ad setów (każda kreacja dostaje własny budżet i osobne dane skuteczności)."
                    }
                    onClick={() => setSplitAdsEnabled(!splitAdsEnabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 disabled:opacity-40 disabled:cursor-not-allowed ${splitAdsEnabled ? "bg-purple-600" : "bg-white/10"}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${splitAdsEnabled ? "left-[18px]" : "left-0.5"}`}
                    />
                  </button>
                </div>

                {splitAdsEnabled && (
                  <div className="space-y-3">
                    <div className="text-[11px] text-white/50">
                      Ile reklam ma trafić do jednego ad seta:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[1, 3, 5, 10, 26].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => {
                            setAdsPerAdSet(n);
                            setCustomAdsPerAdSetInput("");
                          }}
                          title={
                            n === 1
                              ? "Każda kreacja w osobnym ad secie — najwięcej kontroli"
                              : n >= 10
                                ? "Wszystkie kreatywy w jednym dużym ad secie — testowanie kreatywów wewnętrznie przez Meta"
                                : `${n} reklam na ad set`
                          }
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            adsPerAdSet === n && !customAdsPerAdSetInput
                              ? "bg-purple-600 text-white"
                              : "bg-white/[0.06] text-white/50 hover:text-white/70 hover:bg-white/[0.1]"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                      <Input
                        type="number"
                        min={1}
                        max={creatives.length}
                        value={customAdsPerAdSetInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomAdsPerAdSetInput(val);
                          const num = parseInt(val, 10);
                          if (num > 0 && num <= creatives.length) setAdsPerAdSet(num);
                        }}
                        placeholder="Custom"
                        className="w-20 h-8 text-xs bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30"
                      />
                    </div>

                    <div className="p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/15 text-xs text-purple-300/80">
                      {t("section6.splitInfo", {
                        count: creatives.length,
                        perSet: adsPerAdSet,
                        sets: Math.ceil(creatives.length / adsPerAdSet),
                      })}
                      {creatives.length % adsPerAdSet !== 0 && (
                        <span className="text-white/40">
                          {" "}
                          {t("section6.splitLastSet", {
                            remaining: creatives.length % adsPerAdSet,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ================ SECTION 7: USTAWIENIA REKLAMY ================ */}
          <div id="section-7" className={SECTION_CLS}>
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-red-600/15 border border-red-500/25 text-red-400 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                7
              </span>
              <Crosshair className="w-3.5 h-3.5 text-white/40" />
              <h3 className="text-sm font-semibold text-white tracking-wide">
                {t("section7.title")}
              </h3>
              <span title="UTM parametry to fragmenty URL (utm_source, utm_medium, utm_campaign...) które pomogą ci śledzić skąd przychodzi ruch w Google Analytics. Domyślne wartości są OK — zostaw jeśli nie wiesz co to.">
                <HelpCircle className="w-3.5 h-3.5 text-white/30 hover:text-white/60 cursor-help" />
              </span>
            </div>

            <div className="space-y-4">
              {/* Multi-advertiser ads toggle */}
              <div
                className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] p-4"
                title="Multi-advertiser ads = Twoja reklama może pojawić się w karuzeli obok reklam innych firm w Feed/Stories. Zwiększa zasięg dla niskiego budżetu, ale dzielisz uwagę usera. Zostaw OFF jeśli nie wiesz."
              >
                <div>
                  <p className="text-sm text-white/80 font-medium flex items-center gap-1.5">
                    Multi-advertiser ads
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </p>
                  <p className="text-[11px] text-white/40 mt-0.5">
                    {t("section7.multiAdvertiserDesc")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMultiAdvertiserAds(!multiAdvertiserAds)}
                  title={
                    multiAdvertiserAds
                      ? "Wyłącz multi-advertiser ads"
                      : "Włącz multi-advertiser ads"
                  }
                  className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${multiAdvertiserAds ? "bg-purple-600" : "bg-white/10"}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${multiAdvertiserAds ? "left-[18px]" : "left-0.5"}`}
                  />
                </button>
              </div>

              {/* Advantage+ creative enhancements toggle */}
              <div
                className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] p-4"
                title="Advantage+ creative = Meta sama modyfikuje twoją kreatywę (jasność, kadr, dodaje muzykę, generuje warianty tekstu). Może poprawić wyniki ale traci się kontrolę nad wyglądem. Wyłącz jeśli kreatywa jest brand-sensitive."
              >
                <div>
                  <p className="text-sm text-white/80 font-medium flex items-center gap-1.5">
                    Advantage+ creative enhancements
                    <HelpCircle className="w-3 h-3 text-white/30" />
                  </p>
                  <p className="text-[11px] text-white/40 mt-0.5">
                    {t("section7.advantagePlusDesc")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAdvantagePlusCreative(!advantagePlusCreative)}
                  title={
                    advantagePlusCreative
                      ? "Wyłącz Advantage+ creative enhancements (zachowaj kreatywy bez modyfikacji Meta)"
                      : "Włącz Advantage+ creative enhancements (Meta sama modyfikuje kreatywy)"
                  }
                  className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${advantagePlusCreative ? "bg-purple-600" : "bg-white/10"}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${advantagePlusCreative ? "left-[18px]" : "left-0.5"}`}
                  />
                </button>
              </div>

              <p
                className="text-[10px] text-white/25"
                title="Wartości domyślne (Meta + UTM Tracking) są bezpieczne dla większości kampanii. Zmień tylko jeśli wiesz po co."
              >
                {t("section7.defaultNote")}
              </p>
            </div>
          </div>

          {/* Divider — separates required main flow (1-7) from optional advanced (8-9) */}
          <div className="relative py-2 my-2" aria-hidden="true">
            <div className="absolute inset-0 flex items-center px-2">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-purple-600 dark:text-purple-300/60 font-medium bg-background border border-purple-500/30 rounded-full flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                Tryby zaawansowane (opcjonalne)
              </span>
            </div>
          </div>

          {/* ================ SECTION 8: A/B TEST ================ */}
          <div id="section-8" className={SECTION_CLS}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-red-600/15 border border-red-500/25 text-red-400 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  8
                </span>
                <Sparkles className="w-3.5 h-3.5 text-purple-300/60" />
                <h3 className="text-sm font-semibold text-white tracking-wide">
                  {t("section8.title")}
                </h3>
                <span title="A/B Test duplikuje twoją nową kampanię z audiencją z innego (najlepszego) ad setu. Pozwala porównać czy twoje kreatywy działają lepiej z aktualną audiencją czy z 'sprawdzoną' z poprzednich kampanii. Jeśli to twoja pierwsza kampania — zostaw wyłączone.">
                  <HelpCircle className="w-3.5 h-3.5 text-white/30 hover:text-white/60 cursor-help" />
                </span>
                {!abTestEnabled && (
                  <span className="text-[10px] text-white/30">
                    — polecane wyłączyć dla pierwszej kampanii
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setAbTestEnabled(!abTestEnabled)}
                title={
                  abTestEnabled
                    ? "Wyłącz A/B test (utworzysz tylko nową kampanię)"
                    : "Włącz A/B test (utworzysz nową kampanię + duplikat z audiencją z innego, sprawdzonego ad setu by porównać wyniki)"
                }
                className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${abTestEnabled ? "bg-purple-600" : "bg-white/10"}`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${abTestEnabled ? "left-[18px]" : "left-0.5"}`}
                />
              </button>
            </div>

            {!abTestEnabled && (
              <p className="text-[11px] text-white/40 flex items-start gap-1.5">
                <HelpCircle className="w-3 h-3 mt-0.5 shrink-0 text-white/30" />
                <span>
                  {t("section8.disabledDesc")}{" "}
                  <span className="text-white/60">
                    Krótko: utworzysz tylko 1 kampanię, bez duplikatu testowego.
                  </span>
                </span>
              </p>
            )}

            {abTestEnabled && (
              <div className="space-y-4">
                {/* Mode tabs: Auto vs Manual */}
                <div className="flex gap-1 bg-white/[0.03] rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setAbTestMode("auto")}
                    title="AI sam znajdzie najlepszy ad set z konta (na podstawie CPL/CPA z wybranego okresu)."
                    className={`flex-1 text-xs py-1.5 px-3 rounded-md transition-colors ${
                      abTestMode === "auto"
                        ? "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                        : "text-white/40 hover:text-white/60"
                    }`}
                  >
                    <Target className="w-3 h-3 inline mr-1.5" />
                    {t("section8.autoMode")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAbTestMode("manual")}
                    title="Ręcznie przeglądasz drzewo kampanie → ad sety i wybierasz ten, którego audiencji chcesz użyć."
                    className={`flex-1 text-xs py-1.5 px-3 rounded-md transition-colors ${
                      abTestMode === "manual"
                        ? "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                        : "text-white/40 hover:text-white/60"
                    }`}
                  >
                    <Search className="w-3 h-3 inline mr-1.5" />
                    {t("section8.manualMode")}
                  </button>
                </div>

                {/* Timeframe selector */}
                <div
                  className="flex items-center gap-dense flex-wrap"
                  title="Okres z którego AI analizuje skuteczność ad setów. 7 dni = ostatnie trendy. 30 dni = stabilniejsze dane. 90+ = długoterminowi liderzy. Domyślnie 7 dni."
                >
                  <Select value={abTestDatePreset} onValueChange={setAbTestDatePreset}>
                    <SelectTrigger
                      className="w-[140px] h-9 text-xs bg-[#030407] border-white/[0.1] text-white"
                      title="Z jakiego okresu pobrać dane do oceny ad setów. 7 dni = świeże trendy, 30 dni = stabilne dane, 90+ = długoterminowi liderzy."
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={SELECT_CONTENT_CLS}>
                      <SelectItem value="last_7d" className={SELECT_ITEM_CLS}>
                        {t("section8.last7days")}
                      </SelectItem>
                      <SelectItem value="last_14d" className={SELECT_ITEM_CLS}>
                        {t("section8.last14days")}
                      </SelectItem>
                      <SelectItem value="last_30d" className={SELECT_ITEM_CLS}>
                        {t("section8.last30days")}
                      </SelectItem>
                      <SelectItem value="last_90d" className={SELECT_ITEM_CLS}>
                        {t("section8.last90days")}
                      </SelectItem>
                      <SelectItem value="maximum" className={SELECT_ITEM_CLS}>
                        {t("section8.allTime")}
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {abTestMode === "auto" && (
                    <Button
                      onClick={fetchBestAdSet}
                      disabled={abTestFetching || !adAccountId}
                      variant="outline"
                      title="AI przeanalizuje wszystkie ad sety z konta z wybranego okresu i znajdzie ten z najlepszym CPL/CPA. Audiencja tego ad seta zostanie użyta jako duplikat A/B."
                      className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                    >
                      {abTestFetching ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("section8.fetchBestLoading")}
                        </>
                      ) : (
                        <>
                          <Target className="w-4 h-4 mr-2" />
                          {t("section8.fetchBestBtn")}
                        </>
                      )}
                    </Button>
                  )}

                  {abTestMode === "manual" && !manualBrowserOpen && (
                    <Button
                      onClick={() => setManualBrowserOpen(true)}
                      disabled={!adAccountId}
                      variant="outline"
                      title="Otwórz drzewo wszystkich kampanii i ad setów z konta. Wybierz konkretny ad set, którego audiencji chcesz użyć w duplikacie."
                      className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      {t("section8.browseCampaigns")}
                    </Button>
                  )}

                  {!adAccountId && (
                    <p className="text-[10px] text-red-400/70">{t("section8.selectAdAccount")}</p>
                  )}
                </div>

                {/* Manual browser */}
                {abTestMode === "manual" && manualBrowserOpen && adAccountId && (
                  <AdSetBrowser
                    adAccountId={adAccountId}
                    datePreset={abTestDatePreset}
                    onSelect={(data) => {
                      setManualSelectedAdSet(data);
                      setManualBrowserOpen(false);
                      // Auto-fill page, IG account, and partnership from source adset
                      if (data.sourcePageId) setPageId(data.sourcePageId);
                      if (data.instagramActorId) setInstagramActorId(data.instagramActorId);
                      if (data.brandedContentSponsorPageId) {
                        setPartnershipEnabled(true);
                        setPartnerPageId(data.brandedContentSponsorPageId);
                        setPartnerPageIdInput(data.brandedContentSponsorPageId);
                      }
                    }}
                    onClose={() => setManualBrowserOpen(false)}
                  />
                )}

                {/* Results card — shared between auto and manual modes */}
                {effectiveAdSet && (
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-4 space-y-4">
                    {/* Summary */}
                    <div>
                      <p className="text-xs font-medium text-purple-300 mb-1">
                        {abTestMode === "manual"
                          ? t("section8.selectedAdSetLabel")
                          : t("section8.bestAdSetLabel")}
                      </p>
                      <p className="text-sm text-white/90 font-medium">
                        {effectiveAdSet.adSetName}
                      </p>
                      <div className="flex gap-balanced mt-1.5 text-[11px] text-white/50 flex-wrap">
                        <span title="Łączny wydatek tego ad seta w wybranym okresie">
                          {t("section8.spend")}
                          <span className="text-white/70">
                            {parseFloat(effectiveAdSet.spend).toFixed(2)}{" "}
                            {selectedAdAccountCurrency}
                          </span>
                        </span>
                        <span title="Liczba leadów (zapisów/konwersji) z tego ad seta">
                          {t("section8.leads")}
                          <span
                            className={
                              effectiveAdSet.leads > 0
                                ? "text-green-400 font-medium"
                                : "text-white/40"
                            }
                          >
                            {effectiveAdSet.leads}
                          </span>
                        </span>
                        {effectiveAdSet.leads > 0 && (
                          <span title="Cost Per Lead — średni koszt pozyskania jednego leada (spend / leads). Im niższy, tym lepiej.">
                            {t("section8.cpl")}
                            <span className="text-green-400 font-medium">
                              {effectiveAdSet.costPerLead.toFixed(2)} {selectedAdAccountCurrency}
                            </span>
                          </span>
                        )}
                        <span title="Click-Through Rate — odsetek osób, które kliknęły reklamę spośród tych co ją zobaczyły. Powyżej 1% = OK, powyżej 2% = świetnie.">
                          {t("section8.ctr")}
                          <span className="text-white/70">{effectiveAdSet.ctr}%</span>
                        </span>
                        <span title="Łączna liczba kliknięć w reklamy tego ad seta">
                          {t("section8.clicks")}
                          <span className="text-white/70">{effectiveAdSet.clicks}</span>
                        </span>
                      </div>
                      {abTestMode === "manual" && (
                        <button
                          type="button"
                          onClick={() => {
                            setManualBrowserOpen(true);
                            setManualSelectedAdSet(null);
                          }}
                          title="Otwórz drzewo kampanii by wybrać inny ad set"
                          className="text-[10px] text-purple-300/60 hover:text-purple-300 mt-1.5 underline"
                        >
                          {t("section8.changeSelection")}
                        </button>
                      )}
                    </div>

                    {/* Targeting info */}
                    <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
                      <p className="text-xs text-white/50 mb-1">{t("section8.targetingCopied")}</p>
                      <p className="text-[11px] text-white/70">{t("section8.targetingNote")}</p>
                    </div>

                    {/* Campaign placement: new vs source */}
                    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
                      <p className="text-xs text-white/60 font-medium">
                        {t("section8.dupLocationLabel")}
                      </p>
                      <div className="flex gap-1 bg-white/[0.03] rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => setDupCampaignMode("new")}
                          title="Duplikat ad seta będzie w nowo tworzonej kampanii (twoje sekcje 1-7 wyżej). Ten ad set + nowy z audiencji źródłowej w jednej kampanii."
                          className={`flex-1 text-[11px] py-1.5 px-2 rounded-md transition-colors ${
                            dupCampaignMode === "new"
                              ? "bg-purple-600/20 text-purple-300 border border-purple-500/30"
                              : "text-white/40 hover:text-white/60"
                          }`}
                        >
                          {t("section8.newCampaign")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDupCampaignMode("source")}
                          title="Duplikat ląduje w tej samej kampanii co źródłowy ad set — nie tworzymy nowej kampanii. Tylko dosypujemy nowy ad set obok istniejącego."
                          className={`flex-1 text-[11px] py-1.5 px-2 rounded-md transition-colors ${
                            dupCampaignMode === "source"
                              ? "bg-green-600/20 text-green-300 border border-green-500/30"
                              : "text-white/40 hover:text-white/60"
                          }`}
                        >
                          {t("section8.sourceCampaign")}
                        </button>
                      </div>
                      <p className="text-[10px] text-white/30">
                        {dupCampaignMode === "new"
                          ? t("section8.newCampaignDesc")
                          : t("section8.sourceCampaignDesc")}
                      </p>
                    </div>

                    {/* Clone best ads toggle */}
                    <div
                      className="flex items-center justify-between rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3"
                      title="W trybie 'klonu' duplikat ad seta dostanie dokładnie te same reklamy co źródłowy. Bez nowych kreatywów. Użyteczne by przetestować tylko zmianę audiencji."
                    >
                      <div>
                        <p className="text-xs text-cyan-300 font-medium">
                          {t("section8.cloneAdsLabel")}
                        </p>
                        <p className="text-[10px] text-white/40">{t("section8.cloneAdsDesc")}</p>
                      </div>
                      <button
                        type="button"
                        title={
                          dupCloneBestAds
                            ? "Wyłącz klonowanie reklam — duplikat dostanie kreatywy z sekcji 6"
                            : "Włącz klonowanie — duplikat dostanie te same reklamy co źródłowy ad set (bez nowych kreatywów). Test tylko zmiany audiencji."
                        }
                        onClick={() => {
                          const nextValue = !dupCloneBestAds;
                          setDupCloneBestAds(nextValue);
                          if (nextValue) {
                            setDupBestTextSwapVideo(false);
                            setDupUseCurrentFormAds(false);
                          }
                        }}
                        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${dupCloneBestAds ? "bg-cyan-600" : "bg-white/10"}`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dupCloneBestAds ? "left-[18px]" : "left-0.5"}`}
                        />
                      </button>
                    </div>

                    {/* Warning: no ads in ad set */}
                    {dupCloneBestAds && effectiveAdSet.ads.length === 0 && (
                      <p className="text-[10px] text-amber-400/70 -mt-1">
                        {t("section8.cloneAdsWarning")}
                      </p>
                    )}

                    {/* My creatives + best text toggle — standalone, mutually exclusive with clone */}
                    {effectiveAdSet.ads.length > 0 && (
                      <div
                        className="flex items-center justify-between rounded-lg border border-orange-500/20 bg-orange-500/5 p-3"
                        title="Twoje wgrane wideo + nagłówek/tekst z najlepszej działającej reklamy źródłowego ad seta. Pozwala przetestować nowe wideo z tekstem o sprawdzonej skuteczności."
                      >
                        <div>
                          <p className="text-xs text-orange-300 font-medium">
                            {t("section8.myCreativesBestTextLabel")}
                          </p>
                          <p className="text-[10px] text-white/40">
                            {t("section8.myCreativesBestTextDesc")}
                          </p>
                        </div>
                        <button
                          type="button"
                          title={
                            dupBestTextSwapVideo
                              ? "Wyłącz tryb hybrydowy"
                              : "Włącz hybrydę: twoje wideo (z sekcji 6) + tekst i nagłówek z najlepszej działającej reklamy źródłowego ad seta. Sprawdza nowe wideo z tekstem o znanej skuteczności."
                          }
                          onClick={() => {
                            const nextValue = !dupBestTextSwapVideo;
                            setDupBestTextSwapVideo(nextValue);
                            if (nextValue) {
                              setDupCloneBestAds(false);
                              setDupUseCurrentFormAds(false);
                            }
                          }}
                          className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${dupBestTextSwapVideo ? "bg-orange-600" : "bg-white/10"}`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dupBestTextSwapVideo ? "left-[18px]" : "left-0.5"}`}
                          />
                        </button>
                      </div>
                    )}

                    <div
                      className="flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3"
                      title="W duplikacie użyj swoich kreatywów + swoich tekstów (z formularza wyżej, sekcja 5/6). Pełna kontrola nad treścią — testuj swoje propozycje na sprawdzonej audiencji."
                    >
                      <div>
                        <p className="text-xs text-emerald-300 font-medium">
                          {t("section8.myCreativesMyTextsLabel")}
                        </p>
                        <p className="text-[10px] text-white/40">
                          {t("section8.myCreativesMyTextsDesc")}
                        </p>
                      </div>
                      <button
                        type="button"
                        title={
                          dupUseCurrentFormAds
                            ? "Wyłącz tryb własnych kreatyw + tekstów"
                            : "Włącz: pełna kontrola — twoje kreatywy z sekcji 6 + twoje primary text/headline z sekcji 5. Sprawdzasz całe nowe propozycje na sprawdzonej audiencji."
                        }
                        onClick={() => {
                          const nextValue = !dupUseCurrentFormAds;
                          setDupUseCurrentFormAds(nextValue);
                          if (nextValue) {
                            setDupCloneBestAds(false);
                            setDupBestTextSwapVideo(false);
                          }
                        }}
                        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${dupUseCurrentFormAds ? "bg-emerald-600" : "bg-white/10"}`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dupUseCurrentFormAds ? "left-[18px]" : "left-0.5"}`}
                        />
                      </button>
                    </div>

                    {dupUseCurrentFormAds && (
                      <div className="pl-2 border-l-2 border-emerald-500/20 space-y-1">
                        <p className="text-[10px] text-emerald-300/80">
                          {t("section8.dupOnlyWarning")}
                        </p>
                        <p className="text-[10px] text-white/35">{t("section8.dupRequirements")}</p>
                      </div>
                    )}

                    {/* Best text preview */}
                    {dupBestTextSwapVideo &&
                      effectiveAdSet.ads.length > 0 &&
                      (() => {
                        const bestAd =
                          effectiveAdSet.ads.find((a) => a.primaryText) || effectiveAdSet.ads[0];
                        return (
                          <div className="pl-2 border-l-2 border-orange-500/20 space-y-2">
                            <p className="text-[10px] text-orange-300/70">
                              {t("section8.bestAdTextLabel")}
                            </p>
                            <div className="rounded border border-white/[0.06] bg-white/[0.02] p-2.5">
                              <p className="text-[11px] text-white/70 whitespace-pre-wrap">
                                {bestAd.primaryText?.slice(0, 300) || "—"}
                              </p>
                              {bestAd.headline && (
                                <p className="text-[10px] text-white/40 mt-1">
                                  Headline: {bestAd.headline}
                                </p>
                              )}
                            </div>
                            <p className="text-[10px] text-white/30">
                              {t("section8.creativesFromFilesSection")}
                            </p>
                          </div>
                        );
                      })()}

                    {/* Form selector when cloning best ads — optional */}
                    {(dupCloneBestAds || dupBestTextSwapVideo) && (
                      <Field className="pl-2 border-l-2 border-cyan-500/20">
                        <FieldLabel>
                          <span
                            className="inline-flex items-center gap-1.5"
                            title="Opcjonalny override Instant Form dla duplikatu testowego. Domyślnie duplikat dziedziczy formularz z najlepszej reklamy. Ustaw inny gdy chcesz testować nowy formularz."
                          >
                            {t("section8.dupFormLabel")}
                            <HelpCircle className="w-3 h-3 text-white/30" />
                          </span>
                        </FieldLabel>
                        <p className="text-[10px] text-white/30 mb-1.5">
                          {t("section8.dupFormDesc")}
                        </p>
                        {leadgenForms.length > 0 ? (
                          <div className="space-y-1.5">
                            <Select value={dupLeadgenFormId} onValueChange={setDupLeadgenFormId}>
                              <SelectTrigger className={SELECT_TRIGGER_CLS}>
                                <SelectValue placeholder={t("section8.dupFormPlaceholder")} />
                              </SelectTrigger>
                              <SelectContent className={SELECT_CONTENT_CLS}>
                                {leadgenForms.map((f) => (
                                  <SelectItem key={f.id} value={f.id} className={SELECT_ITEM_CLS}>
                                    {f.name} {f.status !== "ACTIVE" ? `(${f.status})` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {dupLeadgenFormId && (
                              <button
                                type="button"
                                onClick={() => setDupLeadgenFormId("")}
                                className="text-[10px] text-red-400/60 hover:text-red-400 underline"
                              >
                                {t("section8.dupFormClearBtn")}
                              </button>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-white/40">
                            {pageId ? t("section8.dupFormEmpty") : t("section8.dupFormSelectPage")}
                          </p>
                        )}
                      </Field>
                    )}

                    {/* Customize toggle */}
                    <div className="flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                      <div>
                        <p className="text-xs text-white/70">{t("section8.changeSettingsLabel")}</p>
                        <p className="text-[10px] text-white/40">
                          {t("section8.changeSettingsDesc")}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDupCustomize(!dupCustomize)}
                        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${dupCustomize ? "bg-purple-600" : "bg-white/10"}`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dupCustomize ? "left-[18px]" : "left-0.5"}`}
                        />
                      </button>
                    </div>

                    {dupCustomize && (
                      <div className="space-y-3 pl-2 border-l-2 border-purple-500/20">
                        {/* Conversion location override */}
                        {CONVERSION_LOCATIONS[objective] && (
                          <Field>
                            <FieldLabel>{t("section8.dupDestinationLabel")}</FieldLabel>
                            <Select value={dupConvLocation} onValueChange={setDupConvLocation}>
                              <SelectTrigger className={SELECT_TRIGGER_CLS}>
                                <SelectValue
                                  placeholder={t("section8.dupDestinationPlaceholder")}
                                />
                              </SelectTrigger>
                              <SelectContent className={SELECT_CONTENT_CLS}>
                                {(CONVERSION_LOCATIONS[objective] || []).map((cl) => (
                                  <SelectItem
                                    key={cl.value}
                                    value={cl.value}
                                    className={SELECT_ITEM_CLS}
                                  >
                                    {cl.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                        )}

                        {/* Leadgen form for duplicate */}
                        {dupNeedsInstantForm && (
                          <Field>
                            <FieldLabel>{t("section8.dupInstantFormLabel")}</FieldLabel>
                            {leadgenForms.length > 0 ? (
                              <Select value={dupLeadgenFormId} onValueChange={setDupLeadgenFormId}>
                                <SelectTrigger className={SELECT_TRIGGER_CLS}>
                                  <SelectValue
                                    placeholder={t("section8.dupInstantFormPlaceholder")}
                                  />
                                </SelectTrigger>
                                <SelectContent className={SELECT_CONTENT_CLS}>
                                  {leadgenForms.map((f) => (
                                    <SelectItem key={f.id} value={f.id} className={SELECT_ITEM_CLS}>
                                      {f.name} {f.status !== "ACTIVE" ? `(${f.status})` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="text-xs text-white/40">
                                {pageId
                                  ? t("section8.dupInstantFormEmpty")
                                  : t("section8.dupInstantFormSelectPage")}
                              </p>
                            )}
                          </Field>
                        )}

                        {/* Link override */}
                        <Field>
                          <FieldLabel>{t("section8.dupLinkLabel")}</FieldLabel>
                          <Input
                            value={dupLinkUrl}
                            onChange={(e) => setDupLinkUrl(e.target.value)}
                            placeholder={linkUrl || t("section8.dupLinkSame")}
                            className={INPUT_CLS}
                          />
                          <p className="text-[10px] text-white/30 mt-0.5">
                            {t("section8.dupLinkNote")}
                          </p>
                        </Field>

                        {/* Optimization goal override */}
                        {OPTIMIZATION_GOALS[objective] && (
                          <Field>
                            <FieldLabel>{t("section8.dupEventLabel")}</FieldLabel>
                            <Select
                              value={dupOptimizationGoal || "__default__"}
                              onValueChange={(v) =>
                                setDupOptimizationGoal(v === "__default__" ? "" : v)
                              }
                            >
                              <SelectTrigger className={SELECT_TRIGGER_CLS}>
                                <SelectValue placeholder={t("section8.dupEventPlaceholder")} />
                              </SelectTrigger>
                              <SelectContent className={SELECT_CONTENT_CLS}>
                                <SelectItem value="__default__" className={SELECT_ITEM_CLS}>
                                  {t("section8.dupEventSameItem")}
                                </SelectItem>
                                {(OPTIMIZATION_GOALS[objective] || []).map((g) => (
                                  <SelectItem
                                    key={g.value}
                                    value={g.value}
                                    className={SELECT_ITEM_CLS}
                                  >
                                    {t(`optimizationGoals.${g.value}`, g.label)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                        )}

                        {/* Targeting source toggle */}
                        <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
                          <div>
                            <p className="text-xs text-white/70 font-medium">
                              {t("section8.dupTargetingFromFormLabel")}
                            </p>
                            <p className="text-[10px] text-white/40">
                              {dupUseFormTargeting
                                ? t("section8.dupTargetingFromFormDesc")
                                : t("section8.dupTargetingFromAdSetDesc")}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const next = !dupUseFormTargeting;
                              setDupUseFormTargeting(next);
                              if (next) initDupTargetingFromForm();
                            }}
                            className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ml-4 ${dupUseFormTargeting ? "bg-purple-600" : "bg-white/10"}`}
                          >
                            <span
                              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${dupUseFormTargeting ? "left-[18px]" : "left-0.5"}`}
                            />
                          </button>
                        </div>

                        {/* Dup targeting editor */}
                        {dupUseFormTargeting && (
                          <div className="space-y-3 rounded-lg border border-purple-500/20 bg-purple-500/[0.03] p-3">
                            <p className="text-[10px] text-purple-300/70 font-medium uppercase tracking-wide flex items-center gap-1.5">
                              <Target className="w-3 h-3" /> {t("section8.dupTargetingHeader")}
                            </p>

                            {/* Geo */}
                            <Field>
                              <FieldLabel>{t("section8.dupLocationsLabel")}</FieldLabel>
                              <GeoTargetingWidget
                                value={dupGeoTargeting}
                                onChange={setDupGeoTargeting}
                                adAccountId={adAccountId}
                              />
                            </Field>

                            {/* Age + Gender row */}
                            <div
                              className={`grid grid-cols-1 md:grid-cols-3 gap-2 ${hasTargetingRestrictions ? "opacity-40 pointer-events-none" : ""}`}
                            >
                              <Field>
                                <FieldLabel>Wiek: {t("section8.dupAgeFromLabel")}</FieldLabel>
                                <Input
                                  type="number"
                                  value={hasTargetingRestrictions ? "18" : dupAgeMin}
                                  onChange={(e) => setDupAgeMin(e.target.value)}
                                  min="13"
                                  max="65"
                                  className={INPUT_CLS}
                                />
                              </Field>
                              <Field>
                                <FieldLabel>Wiek: {t("section8.dupAgeToLabel")}</FieldLabel>
                                <Input
                                  type="number"
                                  value={hasTargetingRestrictions ? "65" : dupAgeMax}
                                  onChange={(e) => setDupAgeMax(e.target.value)}
                                  min="13"
                                  max="65"
                                  className={INPUT_CLS}
                                />
                              </Field>
                              <Field>
                                <FieldLabel>{t("section8.dupGenderLabel")}</FieldLabel>
                                <Select
                                  value={hasTargetingRestrictions ? "0" : dupGenders}
                                  onValueChange={setDupGenders}
                                >
                                  <SelectTrigger className={SELECT_TRIGGER_CLS}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className={SELECT_CONTENT_CLS}>
                                    <SelectItem value="0" className={SELECT_ITEM_CLS}>
                                      {t("section3.genderAll")}
                                    </SelectItem>
                                    <SelectItem value="1" className={SELECT_ITEM_CLS}>
                                      {t("section3.genderMale")}
                                    </SelectItem>
                                    <SelectItem value="2" className={SELECT_ITEM_CLS}>
                                      {t("section3.genderFemale")}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </Field>
                            </div>

                            {/* Interests */}
                            <div
                              className={
                                hasTargetingRestrictions ? "opacity-40 pointer-events-none" : ""
                              }
                            >
                              <FieldLabel className="flex items-center gap-1 mb-1.5">
                                <Target className="w-3 h-3 text-orange-400" />{" "}
                                {t("section8.dupInterestsLabel")}
                              </FieldLabel>
                              <div className="relative">
                                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
                                <Input
                                  value={dupInterestSearch}
                                  onChange={(e) => setDupInterestSearch(e.target.value)}
                                  placeholder={t("section8.dupInterestsSearch")}
                                  className={`${INPUT_CLS} pl-8 h-8 text-xs`}
                                />
                                {dupSearchingInterests && (
                                  <Loader2 className="absolute right-2.5 top-2 w-3.5 h-3.5 text-white/30 animate-spin" />
                                )}
                              </div>
                              {dupInterestResults.length > 0 && (
                                <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#0a0c10]">
                                  {dupInterestResults.map((interest) => (
                                    <button
                                      key={interest.id}
                                      type="button"
                                      onClick={() => addDupInterest(interest)}
                                      className="w-full text-left px-3 py-1.5 text-xs text-white/70 hover:bg-white/[0.06] flex items-center justify-between"
                                    >
                                      <span>{interest.name}</span>
                                      <span className="text-[9px] text-white/30">
                                        {interest.audienceSize
                                          ? `~${(interest.audienceSize / 1_000_000).toFixed(1)}M`
                                          : ""}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {dupSelectedInterests.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {dupSelectedInterests.map((interest) => (
                                    <span
                                      key={interest.id}
                                      className="inline-flex items-center bg-orange-500/15 border border-orange-500/30 rounded-full px-2 py-0.5 text-[10px] text-orange-300"
                                    >
                                      {interest.name}
                                      <button
                                        type="button"
                                        onClick={() => removeDupInterest(interest.id)}
                                        className="ml-1 hover:text-white"
                                      >
                                        <X className="w-2.5 h-2.5" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              {dupSelectedInterests.length === 0 && (
                                <p className="text-[10px] text-white/30 mt-1">
                                  {t("section8.broadTargeting")}
                                </p>
                              )}
                            </div>

                            {/* Custom Audiences */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {/* Include */}
                              <Field>
                                <FieldLabel className="flex items-center gap-1">
                                  <Plus className="w-3 h-3 text-green-400" />{" "}
                                  {t("section8.dupIncludeAudiencesLabel")}
                                </FieldLabel>
                                <div className="relative mt-1">
                                  <Search className="absolute left-2 top-2 w-3 h-3 text-white/30 pointer-events-none" />
                                  <Input
                                    value={dupAudienceIncludeSearch}
                                    onChange={(e) => setDupAudienceIncludeSearch(e.target.value)}
                                    placeholder={t("section8.dupAudiencesSearch")}
                                    title="Wpisz nazwę custom audience by ją włączyć w duplikacie ad seta"
                                    className="h-7 text-[10px] bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 pl-6 mb-1"
                                  />
                                </div>
                                <div className="max-h-24 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#030407]">
                                  {dupAvailableAudiencesForInclude.length === 0 && (
                                    <p className="text-[9px] text-white/30 p-2">
                                      {allCustomAudiences.length === 0
                                        ? t("section8.noAudiences")
                                        : t("section8.noResults")}
                                    </p>
                                  )}
                                  {dupAvailableAudiencesForInclude.map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onClick={() => toggleDupAudience(a, "include")}
                                      className="w-full text-left px-2 py-1 text-[10px] text-white/60 hover:bg-white/[0.06] flex items-center justify-between"
                                    >
                                      <span className="truncate">{a.name}</span>
                                    </button>
                                  ))}
                                </div>
                                {dupIncludedAudiences.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {dupIncludedAudiences.map((a) => (
                                      <span
                                        key={a.id}
                                        className="inline-flex items-center bg-green-500/15 border border-green-500/30 rounded-full px-2 py-0.5 text-[9px] text-green-300"
                                      >
                                        {a.name}
                                        <button
                                          type="button"
                                          onClick={() => toggleDupAudience(a, "include")}
                                          className="ml-1"
                                        >
                                          <X className="w-2.5 h-2.5" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </Field>

                              {/* Exclude */}
                              <Field>
                                <FieldLabel className="flex items-center gap-1">
                                  <Minus className="w-3 h-3 text-red-400" />{" "}
                                  {t("section8.dupExcludeAudiencesLabel")}
                                </FieldLabel>
                                <div className="relative mt-1">
                                  <Search className="absolute left-2 top-2 w-3 h-3 text-white/30 pointer-events-none" />
                                  <Input
                                    value={dupAudienceExcludeSearch}
                                    onChange={(e) => setDupAudienceExcludeSearch(e.target.value)}
                                    placeholder={t("section8.dupAudiencesSearch")}
                                    title="Wpisz nazwę custom audience by ją WYKLUCZYĆ z duplikatu (np. dotychczasowi klienci)"
                                    className="h-7 text-[10px] bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 pl-6 mb-1"
                                  />
                                </div>
                                <div className="max-h-24 overflow-y-auto rounded-lg border border-white/[0.08] bg-[#030407]">
                                  {dupAvailableAudiencesForExclude.length === 0 && (
                                    <p className="text-[9px] text-white/30 p-2">
                                      {allCustomAudiences.length === 0
                                        ? t("section8.noAudiences")
                                        : t("section8.noResults")}
                                    </p>
                                  )}
                                  {dupAvailableAudiencesForExclude.map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onClick={() => toggleDupAudience(a, "exclude")}
                                      className="w-full text-left px-2 py-1 text-[10px] text-white/60 hover:bg-white/[0.06] flex items-center justify-between"
                                    >
                                      <span className="truncate">{a.name}</span>
                                    </button>
                                  ))}
                                </div>
                                {dupExcludedAudiences.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {dupExcludedAudiences.map((a) => (
                                      <span
                                        key={a.id}
                                        className="inline-flex items-center bg-red-500/15 border border-red-500/30 rounded-full px-2 py-0.5 text-[9px] text-red-300"
                                      >
                                        {a.name}
                                        <button
                                          type="button"
                                          onClick={() => toggleDupAudience(a, "exclude")}
                                          className="ml-1"
                                        >
                                          <X className="w-2.5 h-2.5" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </Field>
                            </div>

                            {/* Summary */}
                            <p className="text-[10px] text-purple-300/60 border-t border-purple-500/10 pt-2">
                              Targeting:{" "}
                              {[...dupGeoTargeting.included, ...dupGeoTargeting.excluded]
                                .map((l) => (l.mode === "exclude" ? `−${l.name}` : l.name))
                                .join(", ") || "PL"}{" "}
                              | {t("section3.ageLabel")}: {dupAgeMin}–{dupAgeMax}
                              {dupSelectedInterests.length > 0
                                ? ` | ${dupSelectedInterests.length} ${t("section3.interestsCount")}`
                                : ` | ${t("section3.broadTargeting")}`}
                              {dupIncludedAudiences.length > 0
                                ? ` | +${dupIncludedAudiences.length} audiences`
                                : ""}
                              {dupExcludedAudiences.length > 0
                                ? ` | −${dupExcludedAudiences.length} ${t("section3.excludedCount")}`
                                : ""}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Ads preview from this adset */}
                    {effectiveAdSet.ads.length > 0 && (
                      <div>
                        <p className="text-xs text-white/50 mb-1.5">
                          {t("section8.adsInAdSet", { count: effectiveAdSet.ads.length })}:
                        </p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {effectiveAdSet.ads.map((ad) => (
                            <div
                              key={ad.adId}
                              className="flex items-start gap-2 rounded border border-white/[0.06] bg-white/[0.02] p-2"
                            >
                              {(ad.imageUrl || ad.thumbnailUrl) && (
                                <img
                                  src={ad.thumbnailUrl || ad.imageUrl!}
                                  alt=""
                                  className="w-10 h-10 rounded object-cover flex-shrink-0"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] text-white/70 font-medium truncate">
                                  {ad.adName}
                                </p>
                                {ad.headline && (
                                  <p className="text-[10px] text-white/40 truncate">
                                    {t("section8.headlineLabel")}: {ad.headline}
                                  </p>
                                )}
                                {ad.primaryText && (
                                  <p className="text-[10px] text-white/40 truncate">
                                    {t("section8.primaryLabel")}: {ad.primaryText.slice(0, 80)}...
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ================ SECTION 9: ADD TO EXISTING AD SET ================ */}
          <div id="section-9" className={SECTION_CLS}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-red-600/15 border border-red-500/25 text-red-400 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                  9
                </span>
                <Plus className="w-3.5 h-3.5 text-purple-300/60" />
                <h3 className="text-sm font-semibold text-white tracking-wide">
                  {t("section9.title")}
                </h3>
                <span title="Tryb 'dolewania' nowych kreatywów do ad seta który już chodzi i ma dane. Nie tworzy nowej kampanii — dodaje grafiki/wideo do działającego ad seta. Używaj gdy znasz ad set który dobrze konwertuje i chcesz tylko dosypać świeżych kreatywów.">
                  <HelpCircle className="w-3.5 h-3.5 text-white/30 hover:text-white/60 cursor-help" />
                </span>
                {!addToAdSetEnabled && (
                  <span className="text-[10px] text-white/30">— tryb dla zaawansowanych</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setAddToAdSetEnabled(!addToAdSetEnabled)}
                title={
                  addToAdSetEnabled
                    ? "Wyłącz tryb dosypywania (utworzysz nową kampanię od zera)"
                    : "Włącz tryb dosypywania (dodasz tylko nowe kreatywy do istniejącego ad seta — nie tworzy nowej kampanii). Tylko dla zaawansowanych."
                }
                className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${addToAdSetEnabled ? "bg-purple-600" : "bg-white/10"}`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${addToAdSetEnabled ? "left-[18px]" : "left-0.5"}`}
                />
              </button>
            </div>

            {!addToAdSetEnabled && (
              <p className="text-[11px] text-white/40 flex items-start gap-1.5">
                <HelpCircle className="w-3 h-3 mt-0.5 shrink-0 text-white/30" />
                <span>
                  {t("section9.disabledDesc")}{" "}
                  <span className="text-white/60">
                    Krótko: zostawiasz wyłączone = tworzysz całą nową kampanię. Włącz tylko jeśli
                    wiesz który ad set ci konwertuje.
                  </span>
                </span>
              </p>
            )}

            {addToAdSetEnabled && (
              <div className="space-y-3">
                {/* Date preset + browse */}
                <div className="flex items-center gap-dense flex-wrap">
                  <Select value={addToAdSetDatePreset} onValueChange={setAddToAdSetDatePreset}>
                    <SelectTrigger
                      className="w-[140px] h-9 text-xs bg-[#030407] border-white/[0.1] text-white"
                      title="Z jakiego okresu wyświetlać statystyki ad setów (spend / leads / CPL) podczas wybierania docelowego ad seta. Pomaga ocenić który ad set warto 'dosypywać' kreatywami."
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#030407] border-white/[0.08] text-white/90 z-[9999]">
                      <SelectItem
                        value="last_7d"
                        className="text-white/80 focus:bg-white/[0.06] focus:text-white"
                      >
                        {t("section8.last7days")}
                      </SelectItem>
                      <SelectItem
                        value="last_14d"
                        className="text-white/80 focus:bg-white/[0.06] focus:text-white"
                      >
                        {t("section8.last14days")}
                      </SelectItem>
                      <SelectItem
                        value="last_30d"
                        className="text-white/80 focus:bg-white/[0.06] focus:text-white"
                      >
                        {t("section8.last30days")}
                      </SelectItem>
                      <SelectItem
                        value="last_90d"
                        className="text-white/80 focus:bg-white/[0.06] focus:text-white"
                      >
                        {t("section8.last90days")}
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {!addToAdSetBrowserOpen && (
                    <Button
                      onClick={() => setAddToAdSetBrowserOpen(true)}
                      disabled={!adAccountId}
                      variant="outline"
                      title={
                        selectedAddToAdSet
                          ? "Zmień docelowy ad set — otwiera drzewo kampanii i ad setów do wyboru"
                          : "Otwórz drzewo wszystkich kampanii i ad setów. Wybierz docelowy ad set, do którego mają zostać dodane nowe kreatywy."
                      }
                      className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      {selectedAddToAdSet
                        ? t("section9.changeAdSet")
                        : t("section9.browseCampaigns")}
                    </Button>
                  )}
                  {!adAccountId && (
                    <p className="text-[10px] text-red-400/70">{t("section9.selectAdAccount")}</p>
                  )}
                  {adAccountId && !selectedAddToAdSet && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-300/70 flex items-start gap-1">
                      <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                      <span>
                        Wybierz docelowy ad set z aktywnych kampanii — kreatywy zostaną do niego
                        dodane bez tworzenia nowej kampanii.
                      </span>
                    </p>
                  )}
                </div>

                {/* Browser */}
                {addToAdSetBrowserOpen && adAccountId && (
                  <AdSetBrowser
                    adAccountId={adAccountId}
                    datePreset={addToAdSetDatePreset}
                    onSelect={(data) => {
                      setSelectedAddToAdSet(data);
                      setAddToAdSetBrowserOpen(false);
                    }}
                    onClose={() => setAddToAdSetBrowserOpen(false)}
                  />
                )}

                {/* Selected ad set info */}
                {selectedAddToAdSet && !addToAdSetBrowserOpen && (
                  <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.04] p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold text-white/80">
                          {selectedAddToAdSet.adSetName}
                        </p>
                        <p className="text-[10px] text-white/40 mt-0.5">
                          ID: {selectedAddToAdSet.adSetId}
                        </p>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    </div>

                    {/* Texts option */}
                    <div className="pt-1 border-t border-white/[0.06]">
                      <label
                        className="flex items-center gap-2 cursor-pointer select-none"
                        title="Zaznaczone (domyślnie): nowe kreatywy dostaną teksty z istniejących reklam w ad secie. Odznaczone: użyjemy tekstów z formularza wyżej (sekcja 5)."
                      >
                        <Checkbox
                          checked={addToAdSetUseSourceTexts}
                          onCheckedChange={(v) => setAddToAdSetUseSourceTexts(!!v)}
                          className="border-white/30 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                        />
                        <span className="text-[11px] text-white/60">
                          {t("section9.copyFromAdSet")}
                        </span>
                        <HelpCircle className="w-3 h-3 text-white/30" />
                      </label>
                      {!addToAdSetUseSourceTexts && (
                        <p className="text-[10px] text-white/35 mt-1 pl-5">
                          {t("section9.useFormTexts")}
                        </p>
                      )}
                    </div>

                    {/* Existing ads preview */}
                    {selectedAddToAdSet.ads.length > 0 && (
                      <div className="space-y-1.5">
                        <p
                          className="text-[10px] text-white/40 uppercase tracking-wider"
                          title="Reklamy które już istnieją w wybranym ad secie. Twoje nowe kreatywy zostaną dodane obok nich."
                        >
                          {t("section9.existingAds")}
                        </p>
                        {selectedAddToAdSet.ads.slice(0, 3).map((ad) => (
                          <div
                            key={ad.adId}
                            className="flex items-center gap-2 rounded border border-white/[0.06] bg-white/[0.02] p-2"
                          >
                            {(ad.imageUrl || ad.thumbnailUrl) && (
                              <img
                                src={ad.thumbnailUrl || ad.imageUrl!}
                                alt=""
                                className="w-8 h-8 rounded object-cover flex-shrink-0"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] text-white/70 font-medium truncate">
                                {ad.adName}
                              </p>
                              {ad.primaryText && (
                                <p className="text-[10px] text-white/35 truncate">
                                  {ad.primaryText.slice(0, 80)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                        {selectedAddToAdSet.ads.length > 3 && (
                          <p
                            className="text-[10px] text-white/30"
                            title={`Łącznie ${selectedAddToAdSet.ads.length} reklam w tym ad secie. Pokazuje 3 pierwsze.`}
                          >
                            {t("section9.moreAds", {
                              count: selectedAddToAdSet.ads.length - 3,
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>{" "}
        {/* end inner content */}
        {/* Launch log — inside scroll area so it doesn't push the sticky footer */}
        {launchProgress.length > 0 && (
          <div ref={launchLogRef} className="max-w-6xl mx-auto px-6 pb-6">
            <CampaignLaunchLog
              progress={launchProgress}
              isLaunching={isLaunching}
              launchResult={launchResult}
              adAccountId={adAccountId}
              onNewCampaign={handleNewCampaign}
            />
          </div>
        )}
      </div>{" "}
      {/* end scroll container */}
      {/* ================ LAUNCH FOOTER — STICKY ================ */}
      <div className="flex-shrink-0 border-t border-white/[0.08] bg-[#06070a]/95 backdrop-blur-md relative z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 space-y-2">
          {/* Advanced settings toggle */}
          <div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              title="Ustawienia, które normalnie zostawia się w domyślnej wartości — np. czy uruchomić od razu zamiast jako PAUSED."
              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 transition-colors"
            >
              {advancedOpen ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
              {t("footer.advancedSettings")}
            </button>
            {advancedOpen && (
              <div className="mt-2 pl-1">
                <label
                  className="flex items-center gap-2.5 cursor-pointer select-none group"
                  title="Domyślnie kampania jest tworzona jako PAUSED — bezpieczna i wymaga ręcznego włączenia w Ads Managerze. Zaznacz tę opcję tylko jeśli wszystko już sprawdziłeś i chcesz żeby ruszyła od razu."
                >
                  <Checkbox
                    checked={activateImmediately}
                    onCheckedChange={(v) => setActivateImmediately(!!v)}
                    className="border-white/30 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                  />
                  <span className="text-xs text-white/60 group-hover:text-white/80 transition-colors">
                    {t("footer.activateNow")}{" "}
                    <span className="text-green-400 font-medium">{t("footer.statusActive")}</span>
                    {t("footer.inMeta")}
                  </span>
                  <HelpCircle className="w-3 h-3 text-white/30 group-hover:text-white/50 transition" />
                </label>
                {activateImmediately && (
                  <p className="text-[10px] text-amber-300/80 mt-1.5 ml-6 flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                      UWAGA: kampania ruszy od razu — pieniądze zaczną się palić w ciągu kilku
                      minut.
                    </span>
                  </p>
                )}

                {/* Budget mode: standard daily vs lifetime (total) budget with end date */}
                <div className="mt-4 border-t border-white/[0.06] pt-3">
                  <p className="text-xs font-medium text-white/70 mb-2">Tryb budżetu</p>
                  <div className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] bg-white/[0.02] p-0.5">
                    <button
                      type="button"
                      onClick={() => setBudgetMode("daily")}
                      className={`px-3 py-1.5 rounded-md text-xs ${budgetMode === "daily" ? "bg-[#ef3e29]/20 text-white" : "text-white/55 hover:text-white"}`}
                    >
                      Dzienny (standard)
                    </button>
                    <button
                      type="button"
                      onClick={() => setBudgetMode("lifetime")}
                      className={`px-3 py-1.5 rounded-md text-xs ${budgetMode === "lifetime" ? "bg-[#ef3e29]/20 text-white" : "text-white/55 hover:text-white"}`}
                    >
                      Całkowity (lifetime)
                    </button>
                  </div>
                  {budgetMode === "lifetime" && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                      <div>
                        <label className="text-[11px] text-white/55 mb-1 block">
                          Budżet całkowity ({selectedAdAccountCurrency})
                        </label>
                        <Input
                          type="number"
                          min="1"
                          value={lifetimeBudget}
                          onChange={(e) => setLifetimeBudget(e.target.value)}
                          placeholder="1000"
                          className={INPUT_CLS}
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-white/55 mb-1 block">
                          Data zakończenia
                        </label>
                        <Input
                          type="datetime-local"
                          value={budgetEndDate}
                          onChange={(e) => setBudgetEndDate(e.target.value)}
                          className={`${INPUT_CLS} [color-scheme:dark]`}
                        />
                      </div>
                      <p className="sm:col-span-2 text-[10px] text-white/40 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>
                          Kampania będzie wydawać aż do wyczerpania całego budżetu lub do daty
                          zakończenia (Meta wymaga daty końca dla budżetu całkowitego). Zastępuje
                          budżet dzienny.
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-balanced">
            <div className="space-y-1.5">
              <div
                className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${
                  activateImmediately
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-amber-500/10 border-amber-500/30"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                    activateImmediately ? "bg-emerald-400 animate-pulse" : "bg-amber-400"
                  }`}
                />
                <div className="text-xs space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-white/70">Status po uruchomieniu:</span>
                    <span
                      className={`font-bold uppercase tracking-wider text-[11px] px-1.5 py-0.5 rounded ${
                        activateImmediately
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-amber-500/20 text-amber-300"
                      }`}
                    >
                      {activateImmediately ? "AKTYWNA" : "PAUSED"}
                    </span>
                  </div>
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    {activateImmediately
                      ? "Kampania ruszy od razu po stworzeniu — pieniądze zaczną się palić."
                      : "Kampania trafi do Meta Ads jako wstrzymana — sprawdź ustawienia w Ads Managerze i włącz ręcznie. Bezpieczna domyślnie."}
                  </p>
                </div>
              </div>
              {launchAllVariants && targetingVariants && targetingVariants.length > 1 && (
                <p className="text-[11px] text-purple-300/80 px-3">
                  {t("footer.variantBudget", {
                    count: targetingVariants.length,
                    budget: Number(dailyBudget) || 0,
                    total: (Number(dailyBudget) || 0) * targetingVariants.length,
                  })}
                </p>
              )}
              {draftSavedAt && (
                <p
                  className="text-[10px] text-white/30 flex items-center gap-1 px-3"
                  title="Wersja robocza zapisana w przeglądarce (localStorage). Bezpiecznie przeładujesz stronę / zamkniesz zakładkę — wartości wrócą. Czyści się dopiero po stworzeniu kampanii lub manualnym 'Reset'."
                >
                  <CheckCircle2 className="w-3 h-3 text-emerald-400/60" />
                  Roboczo zapisane{" "}
                  {new Date(draftSavedAt).toLocaleTimeString("pl-PL", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  <HelpCircle className="w-2.5 h-2.5 ml-0.5 text-white/30" />
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {missingFields.length > 0 && (
                <div className="flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-300/80 max-w-md">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    <span className="font-medium">Wypełnij:</span>{" "}
                    {missingFields.map((field, i) => (
                      <span key={field}>
                        {FIELD_ANCHORS[field] ? (
                          <button
                            type="button"
                            onClick={() => scrollToField(field)}
                            className="underline decoration-dotted underline-offset-2 hover:text-amber-200 hover:decoration-solid transition"
                            title="Przejdź do pola"
                          >
                            {field}
                          </button>
                        ) : (
                          <span>{field}</span>
                        )}
                        {i < missingFields.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {canLaunch && !isLaunching && (
                <div className="text-[10px] text-white/30">
                  Skrót:{" "}
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/60 font-mono text-[9px]">
                    ⌘ Enter
                  </kbd>{" "}
                  /{" "}
                  <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] text-white/60 font-mono text-[9px]">
                    Ctrl Enter
                  </kbd>
                </div>
              )}
              <Button
                onClick={() => canLaunch && setConfirmLaunchOpen(true)}
                disabled={!canLaunch}
                title={
                  missingFields.length > 0
                    ? `Brakuje: ${missingFields.join(", ")}. Kliknij brakujące pole na pasku statusu by przejść.`
                    : "Otwórz okno potwierdzenia. Kampania zostanie utworzona w Meta Ads Manager w trybie WYŁĄCZONYM (musisz ją ręcznie włączyć po review)."
                }
                className={`bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white px-6 py-2.5 flex-shrink-0 font-semibold shadow-lg shadow-red-600/20 disabled:opacity-40 disabled:shadow-none transition-all ${
                  canLaunch && !isLaunching
                    ? "ring-2 ring-red-500/40 ring-offset-2 ring-offset-[#06070a] animate-[pulse_2.5s_ease-in-out_infinite]"
                    : ""
                }`}
              >
                {isLaunching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isAddToAdSet
                      ? t("footer.addingToAdSet")
                      : isDupOnly
                        ? t("footer.creatingDupOnly")
                        : isCloneOnly || isSwapNewCampaign
                          ? t("footer.creatingCampaignDup")
                          : t("footer.creatingCampaign")}
                    ...
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4 mr-2" />
                    {isAddToAdSet
                      ? t("footer.addToAdSetBtn", {
                          adSetName: selectedAddToAdSet?.adSetName || t("footer.adSetNameFallback"),
                        })
                      : isDupOnly
                        ? t("footer.dupOnlyBtn")
                        : isCloneOnly || isSwapNewCampaign
                          ? t("footer.campaignDupBtn")
                          : t("footer.launchCampaignBtn")}
                    {abTestEnabled &&
                    !isAddToAdSet &&
                    !isDupOnly &&
                    !isCloneOnly &&
                    !isSwapNewCampaign
                      ? ` + ${t("footer.launchCampaignWithDupBtn")}`
                      : ""}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <PaywallModal open={paywallOpen} onClose={() => setPaywallOpen(false)} />
      <PixelInstallGuideModal open={pixelGuideOpen} onClose={() => setPixelGuideOpen(false)} />
      {/* Pre-launch confirmation modal — last sanity check */}
      <Dialog open={confirmLaunchOpen} onOpenChange={setConfirmLaunchOpen}>
        <DialogContent className="bg-[#0a0c10] border-white/[0.08] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Rocket className="w-4 h-4 text-red-400" />
              Potwierdź uruchomienie kampanii
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] divide-y divide-white/[0.04]">
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                  Kampania
                </span>
                <span className="text-white/90 break-words flex-1">{campaignName || "—"}</span>
              </div>
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                  Konto
                </span>
                <span className="text-white/90 break-words flex-1">
                  {allAdAccounts.find((a) => a.id === adAccountId)?.name ||
                    withActPrefix(adAccountId)}
                </span>
              </div>
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                  Cel
                </span>
                <span className="text-white/90 flex-1">
                  {OBJECTIVES.find((o) => o.value === objective)?.label || objective}
                </span>
              </div>
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                  Budżet
                </span>
                <span className="text-white/90 flex-1">
                  {budgetMode === "lifetime" ? (
                    <>
                      {lifetimeBudget} {selectedAdAccountCurrency} całkowity{" "}
                      <span className="text-white/40">({budgetType})</span>
                      <span className="block text-[10px] text-white/40 mt-0.5">
                        do wyczerpania lub do{" "}
                        {budgetEndDate
                          ? new Date(budgetEndDate).toLocaleString("pl-PL", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </span>
                    </>
                  ) : (
                    <>
                      {dailyBudget} {selectedAdAccountCurrency}/dzień{" "}
                      <span className="text-white/40">({budgetType})</span>
                      <span className="block text-[10px] text-white/40 mt-0.5">
                        ≈ {(Number(dailyBudget) || 0) * 30} {selectedAdAccountCurrency}/miesiąc
                      </span>
                    </>
                  )}
                </span>
              </div>
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                  Kreatywy
                </span>
                <span className="text-white/90 flex-1">
                  {creatives.length} {creatives.length === 1 ? "plik" : "pliki/ów"}
                </span>
              </div>
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                  Status
                </span>
                <span className="flex-1">
                  <span
                    className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                      activateImmediately
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-amber-500/20 text-amber-300"
                    }`}
                  >
                    {activateImmediately ? "AKTYWNA" : "PAUSED"}
                  </span>
                  <span className="block text-[10px] text-white/40 mt-1">
                    {activateImmediately
                      ? "Wystartuje natychmiast po stworzeniu."
                      : "Wstrzymana — uruchom ręcznie w Ads Managerze."}
                  </span>
                </span>
              </div>
              {abTestEnabled &&
                !isAddToAdSet &&
                !isDupOnly &&
                !isCloneOnly &&
                !isSwapNewCampaign && (
                  <div className="flex items-start gap-3 px-3 py-2.5">
                    <span className="text-[10px] uppercase tracking-wider text-white/40 w-24 shrink-0 mt-0.5">
                      A/B Test
                    </span>
                    <span className="text-purple-300/90 flex-1">
                      Włączony — utworzy się też duplikat z audiencją z{" "}
                      {abTestMode === "manual" ? "wybranego" : "najlepszego"} ad seta.
                    </span>
                  </div>
                )}
              {budgetWarning && (
                <div className="flex items-start gap-3 px-3 py-2.5">
                  <AlertCircle
                    className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${budgetWarning.kind === "error" ? "text-red-400" : "text-amber-400"}`}
                  />
                  <span
                    className={`text-[11px] flex-1 ${budgetWarning.kind === "error" ? "text-red-400/90" : "text-amber-300/90"}`}
                  >
                    {budgetWarning.msg}
                  </span>
                </div>
              )}
            </div>

            <p className="text-[11px] text-white/40 leading-relaxed">
              Sprawdź dane wyżej. Po kliknięciu "Tak, uruchom" kampania zostanie utworzona w Meta
              Ads
              {activateImmediately
                ? " i ruszy natychmiast"
                : " jako PAUSED — pieniądze nie palą się dopóki nie włączysz jej w Ads Managerze"}
              .
            </p>

            {!activateImmediately && (
              <div className="rounded-lg bg-amber-500/[0.06] border border-amber-500/25 p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-[11px] text-amber-200/90 leading-relaxed">
                  <strong className="text-amber-300">Co dalej?</strong> Po stworzeniu kampania
                  będzie WYŁĄCZONA. Aby ją uruchomić: wejdź w Meta Ads Manager → znajdź kampanię →
                  przesuń suwak <span className="font-mono text-white">Off → On</span>. Pierwsze
                  wyniki zobaczysz po 24-48h.
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmLaunchOpen(false)}
              className="border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08]"
            >
              Anuluj
            </Button>
            <Button
              onClick={() => {
                setConfirmLaunchOpen(false);
                launchMutation.mutate();
              }}
              className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white font-semibold shadow-lg shadow-red-600/20"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Tak, uruchom
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
